/*! Niadira Bag 3D Configurator — © 2026 Niadira. All rights reserved. */
const ROOT = document.querySelector('[data-niadira-configurator]');
const $ = (sel) => ROOT.querySelector(sel);
const stage = $('three-d-stage');

/* Everything below waits on the 3D library. If that load is slow or blocked the
   wait never resolves, so the launch button is wired FIRST and works regardless. */
const earlyOpen = ROOT.querySelector('#launch-open');
const earlyCover = ROOT.querySelector('#launcher');
const earlyClose = ROOT.querySelector('#cfg-close');
const cfgHost = ROOT;
function windowOpen(){
  if (earlyCover) earlyCover.hidden = true;
  if (earlyClose) earlyClose.hidden = false;
  if (cfgHost !== document.body && cfgHost.parentNode !== document.body){
    cfgHost.__home = { parent: cfgHost.parentNode, next: cfgHost.nextSibling };
    document.body.appendChild(cfgHost);
  }
  cfgHost.dataset.window = 'open';
  document.documentElement.classList.add('niadira-window-open');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  if (window.__niadiraAfterOpen) window.__niadiraAfterOpen();
  if (typeof measureOffset === 'function') measureOffset();
}
function windowClose(){
  if (window.__niadiraBeforeClose) window.__niadiraBeforeClose();
  document.documentElement.classList.remove('niadira-window-open');
  if (cfgHost.__home){
    cfgHost.__home.parent.insertBefore(cfgHost, cfgHost.__home.next);
    cfgHost.__home = null;
  }
  delete cfgHost.dataset.window;
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  if (earlyClose) earlyClose.hidden = true;
  if (earlyCover) earlyCover.hidden = false;
}
if (earlyOpen) earlyOpen.addEventListener('click', windowOpen);
if (earlyClose){ earlyClose.hidden = true; earlyClose.addEventListener('click', windowClose); }

/* The stage rejects when the device declines a WebGL context — an older tablet,
   hardware acceleration switched off. Left unhandled, that rejection stopped this
   module dead at its second line, so the tab row, the colour pages, the text and
   the cart were all never wired: exactly the opposite of what the fallback message
   promises. The 3D library itself loads perfectly well in that case (geometry is
   arithmetic; only drawing needs the context), so fall back to importing it
   directly. Everything except the picture then works. */
let THREE = null, has3D = true;
try {
  ({ THREE } = await stage.ready);
} catch (err) {
  has3D = false;
  try {
    THREE = await import('https://esm.sh/three@0.184.0');
  } catch (e) {
    THREE = null;                      // no library at all: guarded below
  }
}
try { stage.shadowRoot.querySelector('.toolbar').remove(); } catch (e) {}
if (!THREE){
  // nothing 3D can be built; the launch and close controls are already wired
  throw new Error('Niadira: 3D library unavailable — colour and text UI not built');
}

/* ---------- palettes ---------- */
const LEATHER = [
  // reds
  ['Coral Red','#C5453C'],  ['Cherry','#A82A28'],     ['Crimson','#8E1F26'],
  ['Oxblood','#6A2C2C'],     ['Claret','#4E2027'],     ['Burgundy','#3C171C'],
  // blues
  ['Powder Blue','#A8BCCB'], ['Denim','#5C7A96'],      ['Cobalt','#2F5A8C'],
  ['Petrol','#2A4A55'],      ['Navy','#22314A'],       ['Midnight','#161E2E'],
  // tans
  ['Bone','#E4DDCC'],        ['Oat','#D3C7AE'],        ['Sand','#C1B091'],
  ['Camel','#C08E56'],       ['Cognac','#9C6234'],     ['Chestnut','#7A4526'],
  ['Chocolate','#4A342A'],   ['Espresso','#33251E'],
  // greens
  ['Pale Sage','#C2C6AC'],   ['Sage','#9AA083'],       ['Moss','#767C59'],
  ['Olive','#5E6446'],       ['Deep Olive','#3F4531'], ['Forest','#27362B'],
];
const POCKET = [['Champagne','#C8B89A'], ...LEATHER];
const PARTS = [
  { id:'front',  name:'Front panel',       swatches:LEATHER, def:'#E4DDCC', kind:'grain' },
  { id:'back',   name:'Back panel',        swatches:LEATHER, def:'#E4DDCC', kind:'grain' },
  { id:'pocket', name:'Inner zip pocket',  swatches:POCKET,  def:'#C8B89A', kind:'fine' },
  // fixed in black on every bag
  { id:'base',   name:'Base', label:'Black', def:'#1B1A18', kind:'grain',  fixed:true },
  { id:'trim',   name:'Top edge & piping', label:'Black', def:'#191817', kind:'smooth', fixed:true },
  { id:'strap',  name:'Handles & tabs', label:'Black',    def:'#191817', kind:'smooth', fixed:true },
  { id:'lining', name:'Suede lining',      def:'#171614', kind:'suede',  fixed:true },
  { id:'stitch', name:'Seam stitching',    def:'#141312', kind:'thread', fixed:true },
  { id:'stitchSide', name:'Side seam thread', label:'Cream', def:'#CFC7B4', kind:'thread', fixed:true },
  { id:'zip',    name:'Zip hardware', label:'Silver', def:'#D6D8DA', kind:'metal',  fixed:true },
];
const SECTIONS = PARTS.filter(p => !p.fixed);

/* ---------- procedural leather grain ----------
   Matched to the reference photos: shrunken bull grain — wide flat plateaus
   separated by thin deep creases, with a second finer network inside them,
   and irregular cell sizes. Matte, no sheen. */
function cellField(size, cells, jitter, drop){
  const g = cells, cw = size/g;
  const px = new Float32Array(g*g), py = new Float32Array(g*g), on = new Uint8Array(g*g);
  for (let j=0;j<g;j++) for (let i=0;i<g;i++){
    const k = j*g+i;
    px[k] = (i + 0.5 + (Math.random()-0.5)*jitter)*cw;
    py[k] = (j + 0.5 + (Math.random()-0.5)*jitter)*cw;
    on[k] = Math.random() < drop ? 0 : 1;    // dropped sites merge neighbours
  }
  return (x, y) => {
    const ci = Math.floor(x/cw), cj = Math.floor(y/cw);
    let d1 = 1e9, d2 = 1e9;
    for (let oj=-2;oj<=2;oj++) for (let oi=-2;oi<=2;oi++){
      const i = (ci+oi+g)%g, j = (cj+oj+g)%g, k = j*g+i;
      if (!on[k]) continue;
      let dx = px[k] - x, dy = py[k] - y;
      if (dx >  size/2) dx -= size; if (dx < -size/2) dx += size;
      if (dy >  size/2) dy -= size; if (dy < -size/2) dy += size;
      const d = dx*dx + dy*dy;
      if (d < d1){ d2 = d1; d1 = d; } else if (d < d2) d2 = d;
    }
    return (Math.sqrt(d2) - Math.sqrt(d1)) / cw;   // 0 on a crease, ~1 mid-plateau
  };
}

function leatherMaps(size, big, small, opts){
  const o = Object.assign({ creaseW:0.20, fineW:0.34, fineDepth:0.34, plateau:0.55 }, opts||{});
  const A = cellField(size, big,   1.5, 0.14);
  const B = cellField(size, small, 1.7, 0.10);
  const bump = document.createElement('canvas'); bump.width = bump.height = size;
  const rough = document.createElement('canvas'); rough.width = rough.height = size;
  const bctx = bump.getContext('2d'), rctx = rough.getContext('2d');
  const bi = bctx.createImageData(size,size), ri = rctx.createImageData(size,size);
  for (let y=0;y<size;y++){
    for (let x=0;x<size;x++){
      // primary creases: narrow valley, then a broad flat top
      let a = Math.min(1, A(x,y)/o.creaseW);
      a = a*a*(3-2*a);
      a = o.plateau + (1-o.plateau)*a;
      // secondary network inside the plateaus, shallower
      let b = Math.min(1, B(x,y)/o.fineW);
      b = b*b*(3-2*b);
      const v = a * (1 - o.fineDepth*(1-b));
      const speckle = 0.965 + Math.random()*0.035;
      const k = (y*size+x)*4;
      const c = Math.round(255 * Math.min(1, v*speckle));
      bi.data[k]=bi.data[k+1]=bi.data[k+2]=c; bi.data[k+3]=255;
      // creases sit matte and dusty; plateaus a touch less so
      const r = Math.round(255 * (0.98 - 0.16*v));
      ri.data[k]=ri.data[k+1]=ri.data[k+2]=r; ri.data[k+3]=255;
    }
  }
  bctx.putImageData(bi,0,0); rctx.putImageData(ri,0,0);
  const mk = (cv) => {
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
    return t;
  };
  return { bump: mk(bump), rough: mk(rough) };
}

function noiseMap(size, scale){
  const cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d');
  const img = ctx.createImageData(size,size);
  for (let i=0;i<size*size;i++){
    const c = 150 + Math.random()*scale;
    img.data[i*4]=img.data[i*4+1]=img.data[i*4+2]=c; img.data[i*4+3]=255;
  }
  ctx.putImageData(img,0,0);
  const t = new THREE.CanvasTexture(cv);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

const grain = leatherMaps(1024, 14, 30, { creaseW:0.11, fineW:0.28, fineDepth:0.18, plateau:0.64 });
const fine  = leatherMaps(512,  20, 42, { creaseW:0.15, fineW:0.32, fineDepth:0.18, plateau:0.66 });
const suede = noiseMap(512, 90);

const mats = {};
for (const s of PARTS){
  const m = new THREE.MeshStandardMaterial({
    name: s.id, color: new THREE.Color(s.def), side: THREE.DoubleSide,
  });
  if (s.kind === 'grain'){
    m.roughness = 0.94; m.metalness = 0.0;
    m.bumpMap = grain.bump.clone(); m.bumpMap.needsUpdate = true;
    m.bumpMap.repeat.set(7.6,4.9); m.bumpScale = 1.7;
    m.roughnessMap = grain.rough.clone(); m.roughnessMap.needsUpdate = true;
    m.roughnessMap.repeat.copy(m.bumpMap.repeat);
  } else if (s.kind === 'fine'){
    m.roughness = 0.92; m.metalness = 0.0;
    m.bumpMap = fine.bump.clone(); m.bumpMap.needsUpdate = true;
    m.bumpMap.repeat.set(4.4,2.6); m.bumpScale = 1.2;
    m.roughnessMap = fine.rough.clone(); m.roughnessMap.needsUpdate = true;
    m.roughnessMap.repeat.copy(m.bumpMap.repeat);
  } else if (s.kind === 'suede'){
    m.roughness = 0.98; m.metalness = 0.0;
    m.bumpMap = suede.clone(); m.bumpMap.needsUpdate = true;
    m.bumpMap.repeat.set(14,10); m.bumpScale = 0.6;
  } else if (s.kind === 'metal'){
    m.roughness = 0.34; m.metalness = 0.42;   // brushed silver, lit without an env map
  } else if (s.kind === 'thread'){
    m.roughness = 0.55; m.metalness = 0.0;    // waxed thread
  } else {
    m.roughness = 0.38; m.metalness = 0.06;   // smooth calf trim
  }
  mats[s.id] = m;
}

/* ---------- shape ----------
   Large: 46cm across the rim (43.3cm at the base), 32cm tall, 17cm deep.
   Rounded-rectangle base flaring to a lens-shaped opening; the rim rides
   higher at the two side seams and dips across the front and back.      */
const H = 0.32, TH = 0.0032, SEG = 160, ROWS = 44;
const lerp = (a,b,t)=>a+(b-a)*t;

function prof(t){
  const e = t;                          // straight-line taper, flat side edges
  return {
    w: 0.259,                          // 51.8cm across at base and rim alike
    d: lerp(0.085, 0.076, e),          // 17cm at the base, slimmer at the rim
    n: lerp(4.4, 2.0, e),              // squarish base → lens rim
  };
}
function pt(t, theta, inset){
  const p = prof(t), w = p.w - inset, d = p.d - inset;
  const c = Math.cos(theta), s = Math.sin(theta);
  const x = w * Math.sign(c) * Math.pow(Math.abs(c), 2/p.n);
  const z = d * Math.sign(s) * Math.pow(Math.abs(s), 2/p.n);
  const y = t*H;                        // level, straight top edge
  return new THREE.Vector3(x,y,z);
}
function zOf(x, t, face, inset){
  const p = prof(t), w = p.w - inset, d = p.d - inset;
  const u = Math.min(Math.abs(x)/w, 0.999);
  return face * d * Math.pow(1 - Math.pow(u, p.n), 1/p.n);
}

function shell(inset, flip, a, b, uSpan){
  const pos = [], uv = [], idx = [];
  for (let r=0;r<=ROWS;r++){
    const t = r/ROWS;
    for (let i=0;i<=SEG;i++){
      const f = i/SEG;
      const p = pt(t, lerp(a,b,f), inset);
      pos.push(p.x,p.y,p.z); uv.push(f*uSpan, t);
    }
  }
  const stride = SEG+1;
  for (let r=0;r<ROWS;r++) for (let i=0;i<SEG;i++){
    const A=r*stride+i, B=A+1, C=A+stride, D=C+1;
    if (flip) idx.push(A,B,C, B,D,C); else idx.push(A,C,B, B,C,D);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}

function cap(inset, y, up){
  const pos = [0,y,0], uv = [0.5,0.5], idx = [];
  for (let i=0;i<=SEG;i++){
    const th = (i/SEG)*Math.PI*2;
    const p = pt(0, th, inset);
    pos.push(p.x, y, p.z);
    uv.push(0.5 + p.x*2, 0.5 + p.z*3);
  }
  for (let i=1;i<=SEG;i++) up ? idx.push(0,i,i+1) : idx.push(0,i+1,i);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}

function rimBand(){
  const pos = [], uv = [], idx = [];
  for (let i=0;i<=SEG;i++){
    const th = (i/SEG)*Math.PI*2;
    const o = pt(1, th, -0.0012), n = pt(1, th, TH);
    pos.push(o.x,o.y,o.z, n.x,n.y+0.0004,n.z);
    uv.push(i/SEG,0, i/SEG,1);
  }
  for (let i=0;i<SEG;i++){
    const A=i*2,B=A+1,C=A+2,D=A+3;
    idx.push(A,C,B, B,C,D);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}

function ribbon(points, width, thick, steps){
  const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.35);
  const sh = new THREE.Shape();
  const w = width/2, h = thick/2;
  sh.moveTo(-w,-h); sh.lineTo(w,-h); sh.lineTo(w,h); sh.lineTo(-w,h); sh.closePath();
  return new THREE.ExtrudeGeometry(sh, { extrudePath: curve, steps: steps||220, bevelEnabled:false });
}

/* handles: two straps per face, tabs riveted flat to the outer panel and a
   long shallow arc over the opening */
function handlePath(face, x1){
  const wallL = (t)=> new THREE.Vector3(-x1, t*H, zOf(-x1, t, face, -0.004));
  const wallR = (t)=> new THREE.Vector3( x1, t*H, zOf( x1, t, face, -0.004));
  const zL = zOf(-x1, 1, face, 0), zR = zOf(x1, 1, face, 0);
  const drop = 0.21, shoulder = H + drop - x1;   // straight rise, then a
  const pts = [                                   // true semicircular top
    wallR(0.60), wallR(0.80),
    new THREE.Vector3( x1, H + 0.004, zR*1.02),
    new THREE.Vector3( x1, H + 0.055, zR*0.94),
  ];
  const N = 30;
  for (let i=0;i<=N;i++){
    const a = (i/N)*Math.PI;
    pts.push(new THREE.Vector3(
      x1*Math.cos(a),
      shoulder + x1*Math.sin(a),
      zR*(0.88 - 0.34*Math.sin(a))
    ));
  }
  pts.push(
    new THREE.Vector3(-x1, H + 0.055, zL*0.94),
    new THREE.Vector3(-x1, H + 0.004, zL*1.02),
    wallL(0.80), wallL(0.60)
  );
  return pts;
}
const HANDLE_W = 0.017, HANDLE_T = 0.003;
function handle(face, x1){
  return ribbon(handlePath(face, x1), HANDLE_W, HANDLE_T, 300);
}
/* ---------- assemble ---------- */
const bag = new THREE.Group();
bag.name = 'via_dira_tote';
const add = (name, geo, matId)=>{
  const m = new THREE.Mesh(geo, mats[matId]);
  m.name = name; m.userData.section = matId; bag.add(m); return m;
};

add('front_panel', shell(0, false, 0, Math.PI, 3.4), 'front');
add('back_panel',  shell(0, false, Math.PI, Math.PI*2, 3.4), 'back');
add('base_panel',  cap(0, 0, false), 'base');
add('lining_wall', shell(TH, true, 0, Math.PI*2, 6), 'lining');
add('lining_floor', cap(TH*1.4, TH, true), 'lining');
add('rim_binding', rimBand(), 'trim');
/* the strap ends in a squared tab laid flat on the outer panel */
function strapTab(face, x, yTop, len){
  const w = 0.019, cols = 6, rows = 10, pos = [], uv = [], idx = [];
  for (let r=0;r<=rows;r++){
    const y = yTop - (r/rows)*len;
    for (let i=0;i<=cols;i++){
      const px2 = x - w/2 + (i/cols)*w;
      pos.push(px2, y, zOf(px2, Math.min(Math.max(y,0),H)/H, face, 0) + face*0.0016);
      uv.push(i/cols, r/rows);
    }
  }
  for (let r=0;r<rows;r++) for (let i=0;i<cols;i++){
    const A=r*(cols+1)+i, B=A+1, C=A+cols+1, D=C+1;
    idx.push(A,B,C, B,D,C);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}
for (const face of [1,-1]) for (const sgn of [1,-1]){
  add('strap_tab_' + (face>0?'f':'b') + (sgn>0?'r':'l'),
      strapTab(face, sgn*0.085, H*0.80, 0.058), 'strap');
}

/* ---------- saddle stitching ----------
   As on the bag: a run down each side seam, a run just under the top binding
   on both faces, and a run above the base piping. */
function stitchRun(points, gap, len, thick){
  const box = new THREE.BoxGeometry(1,1,1);
  const bp = box.attributes.position.array, bi = box.index.array;
  const pos = [], idx = [];
  const path = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.3);
  const total = path.getLength();
  const n = Math.max(2, Math.floor(total / gap));
  const up = new THREE.Vector3(0,1,0), tmp = new THREE.Vector3();
  for (let s=0; s<=n; s++){
    const u = s/n;
    const p = path.getPointAt(u), tan = path.getTangentAt(u).normalize();
    const ref = Math.abs(tan.dot(up)) > 0.9 ? new THREE.Vector3(1,0,0) : up;
    const side = tmp.copy(ref).cross(tan).normalize();
    const nrm = new THREE.Vector3().copy(tan).cross(side).normalize();
    const m = new THREE.Matrix4().makeBasis(tan, nrm, side)
      .scale(new THREE.Vector3(len, thick, thick*1.6))
      .setPosition(p);
    const base = pos.length/3;
    for (let k=0;k<bp.length;k+=3){
      const v = new THREE.Vector3(bp[k],bp[k+1],bp[k+2]).applyMatrix4(m);
      pos.push(v.x,v.y,v.z);
    }
    for (let k=0;k<bi.length;k++) idx.push(base + bi[k]);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}
{
  const OUT = 0.0011;
  // side seams, both edges
  for (const sgn of [1,-1]){
    const pts = [];
    for (let i=0;i<=14;i++){
      const t = 0.045 + (i/14)*0.915;
      const p = pt(t, sgn > 0 ? 0 : Math.PI, -OUT);
      pts.push(p);
    }
    add('stitch_side_' + (sgn>0?'r':'l'), stitchRun(pts, 0.0042, 0.0026, 0.0008), 'stitchSide');
    // black saddle stitches flanking the seam, one run each side
    for (const s2 of [1]){
      const row = [];
      for (let i=0;i<=14;i++){
        const t = 0.045 + (i/14)*0.915;
        const p = prof(t);
        const zTarget = 0.0052;                       // 5mm off the seam line
        const th = Math.asin(Math.min(1, Math.pow(zTarget/p.d, p.n/2))) * s2;
        row.push(pt(t, sgn > 0 ? th : Math.PI - th, -OUT));
      }
      add('stitch_side_' + (sgn>0?'r':'l') + (s2>0?'a':'b'),
          stitchRun(row, 0.0058, 0.0034, 0.0010), 'stitch');
    }
  }
  // under the top binding, front and back
  for (const face of [0, 1]){
    const pts = [];
    for (let i=0;i<=48;i++){
      const th = Math.PI*(face + i/48);
      const p = pt(0.962, th, -OUT);
      pts.push(p);
    }
    add('stitch_top_' + face, stitchRun(pts, 0.0060, 0.0036, 0.0011), 'stitch');
  }
  // above the base piping
  {
    const pts = [];
    for (let i=0;i<=96;i++){
      const th = (i/96)*Math.PI*2;
      const p = pt(0.030, th, -OUT);
      pts.push(p);
    }
    add('stitch_base', stitchRun(pts, 0.0060, 0.0036, 0.0011), 'stitch');
  }
}

add('handle_front', handle(1, 0.085), 'strap');
add('handle_back',  handle(-1, 0.085), 'strap');

let POCKET_Y = 0.195;
/* inner zip pocket: curved leather strip following the back lining wall,
   horizontal zip set into its upper third */
{
  const pw = 0.21, ph = 0.15, py = 0.195, OFF = 0.0016;
  POCKET_Y = py;
  const zAt = (x, y) => zOf(x, y/H, -1, TH) + OFF;
  const cols = 40, rows = 8;
  const pos = [], uv = [], idx = [];
  for (let r=0;r<=rows;r++){
    const v = r/rows;                       // 0 at the bottom edge, 1 at the top
    const y = py - ph/2 + v*ph;
    const half = (pw - v * 0.011) / 2;      // marginally wider at the bottom
    for (let i=0;i<=cols;i++){
      const x = -half + (i/cols) * half * 2;
      pos.push(x, y, zAt(x,y)); uv.push(i/cols, v);
    }
  }
  for (let r=0;r<rows;r++) for (let i=0;i<cols;i++){
    const A=r*(cols+1)+i, B=A+1, C=A+cols+1, D=C+1;
    idx.push(A,B,C, B,D,C);
  }
  const pg = new THREE.BufferGeometry();
  pg.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  pg.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
  pg.setIndex(idx); pg.computeVertexNormals();
  const panel = new THREE.Mesh(pg, mats.pocket);
  panel.name = 'pocket_panel'; panel.userData.section = 'pocket';
  bag.add(panel);

  // zip, matched to the bag: dark tape, blackened metal teeth, small slider
  // with a teardrop leather pull, and stitching round the window and panel
  const zipY = py + ph/2 - 0.0092, zw = pw*0.90, zh = 0.0040;
  const zpos = [], zuv = [], zidx = [];
  for (let r=0;r<=1;r++){
    const y = zipY - zh/2 + r*zh;
    for (let i=0;i<=cols;i++){
      const x = -zw/2 + (i/cols)*zw;
      zpos.push(x, y, zAt(x,y) + 0.0009); zuv.push(i/cols, r);
    }
  }
  for (let i=0;i<cols;i++){
    const A=i, B=A+1, C=A+cols+1, D=C+1;
    zidx.push(A,B,C, B,D,C);
  }
  const zg = new THREE.BufferGeometry();
  zg.setAttribute('position', new THREE.Float32BufferAttribute(zpos,3));
  zg.setAttribute('uv', new THREE.Float32BufferAttribute(zuv,2));
  zg.setIndex(zidx); zg.computeVertexNormals();
  const tape = new THREE.Mesh(zg, mats.trim);
  tape.name = 'pocket_zip_tape'; tape.userData.section = 'trim'; bag.add(tape);

  // two rows of teeth meeting on the centre line
  const zipLine = (yOff, z, segs) => {
    const p = [], N = segs || 24;
    for (let i=0;i<=N;i++){
      const x = -zw/2 + (i/N)*zw;
      p.push(new THREE.Vector3(x, zipY + yOff, zAt(x, zipY) + z));
    }
    return p;
  };
  // each tooth is a distinct interlocking element: a fine pitch of short
  // silver blocks, the two rows offset so they read as meshing
  for (const s of [1,-1]){
    const teeth = new THREE.Mesh(
      stitchRun(zipLine(s*0.0011, 0.0022, 62), 0.0017, 0.0024, 0.0010), mats.zip);
    teeth.name = 'pocket_zip_teeth_' + (s>0?'top':'bottom');
    teeth.userData.section = 'trim'; bag.add(teeth);
  }
  {
    // the closed chain reads as one continuous silver line between the teeth
    const spine = new THREE.Mesh(
      stitchRun(zipLine(0, 0.0026), 0.0060, 0.0011, 0.0003), mats.zip);
    spine.name = 'pocket_zip_chain'; spine.userData.section = 'trim'; bag.add(spine);
  }

  {
    const lx = -zw/2 + 0.0016, rx = zw/2 - 0.0012;
    const stopL = new THREE.Mesh(new THREE.BoxGeometry(0.0032, 0.0040, 0.0022), mats.zip);
    stopL.position.set(lx, zipY, zAt(lx, zipY) + 0.0018);
    stopL.name = 'pocket_zip_stop_left'; stopL.userData.section = 'trim'; bag.add(stopL);
    const stopR = new THREE.Mesh(new THREE.BoxGeometry(0.0024, 0.0036, 0.0020), mats.zip);
    stopR.position.set(rx, zipY, zAt(rx, zipY) + 0.0017);
    stopR.name = 'pocket_zip_stop_right'; stopR.userData.section = 'trim'; bag.add(stopR);
  }

  // slider and its teardrop leather pull
  const sx = -zw/2 + 0.0086, sz = zAt(sx, zipY);
  const slider = new THREE.Mesh(new THREE.BoxGeometry(0.0132, 0.0050, 0.0030), mats.zip);
  slider.position.set(sx, zipY, sz + 0.0022);
  slider.name = 'pocket_zip_slider'; slider.userData.section = 'trim'; bag.add(slider);

  {
    // a slim silver hook: it leaves the slider, drops, and curls back on itself
    const pts = [
      new THREE.Vector3( 0.0005,  0.0004, 0),
      new THREE.Vector3( 0.0010, -0.0022, 0),
      new THREE.Vector3( 0.0009, -0.0055, 0),
      new THREE.Vector3( 0.0002, -0.0081, 0),
      new THREE.Vector3(-0.0014, -0.0095, 0),
      new THREE.Vector3(-0.0031, -0.0088, 0),
      new THREE.Vector3(-0.0036, -0.0070, 0),
      new THREE.Vector3(-0.0027, -0.0057, 0),
    ];
    const hook = new THREE.Mesh(
      new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.4),
        48, 0.00058, 8, false), mats.zip);
    hook.position.set(sx, zipY - 0.0026, sz + 0.0024);
    hook.name = 'pocket_zip_pull'; hook.userData.section = 'trim'; bag.add(hook);
    // the small metal tab joining the hook to the slider body
    const tab = new THREE.Mesh(new THREE.BoxGeometry(0.0022, 0.0028, 0.0013), mats.zip);
    tab.position.set(sx + 0.0004, zipY - 0.0020, sz + 0.0024);
    tab.name = 'pocket_zip_tab'; tab.userData.section = 'trim'; bag.add(tab);

    // the leather pull: an elongated tongue, widest at the top and tapering to
    // a rounded point, hanging vertically against the panel
    const tw = 0.0094, tl = 0.0378, tip = 0.0022;
    const hw = tw / 2, yTip = -(tl - tip);
    const tongue = new THREE.Shape();
    // anticlockwise round the outline: top cap, left flank, tip, right flank
    tongue.moveTo(hw, 0);
    tongue.absarc(0, 0, hw, 0, Math.PI, false);          // domed top, widest here
    tongue.bezierCurveTo(-hw, -tl*0.30, -tip*1.25, yTip + tip*0.9, -tip, yTip);
    tongue.absarc(0, yTip, tip, Math.PI, Math.PI*2, false);  // rounded point
    tongue.bezierCurveTo(tip*1.25, yTip + tip*0.9, hw, -tl*0.30, hw, 0);
    const sg = new THREE.ExtrudeGeometry(tongue, {
      depth: 0.0018, bevelEnabled: true, bevelThickness: 0.0005,
      bevelSize: 0.0005, bevelSegments: 2, curveSegments: 20,
    });
    const leather = new THREE.Mesh(sg, mats.trim);
    leather.position.set(sx - 0.0030, zipY - 0.0086, sz + 0.0016);
    leather.name = 'pocket_zip_strap'; leather.userData.section = 'trim';
    bag.add(leather);

    // the seam that runs down the centre of the pull
    const seamPts = [];
    for (let i=0;i<=12;i++){
      seamPts.push(new THREE.Vector3(
        sx - 0.0030, zipY - 0.0098 - (i/12) * (tl - 0.0090), sz + 0.0042));
    }
    const seam = new THREE.Mesh(
      stitchRun(seamPts, 0.0024, 0.0011, 0.0005), mats.stitch);
    seam.name = 'pocket_zip_pull_seam'; seam.userData.section = 'trim';
    bag.add(seam);

    // the silver dome rivet where the pull meets the slider loop
    const rivet = new THREE.Mesh(
      new THREE.SphereGeometry(0.0013, 16, 10, 0, Math.PI*2, 0, Math.PI/2), mats.zip);
    rivet.position.set(sx - 0.0030, zipY - 0.0098, sz + 0.0040);
    rivet.rotation.x = Math.PI / 2;
    rivet.name = 'pocket_zip_rivet'; rivet.userData.section = 'trim';
    bag.add(rivet);
  }

  // stitching: around the zip window, then around the panel edge
  for (const yOff of [-(zh/2 + 0.0022)]){
    const run = new THREE.Mesh(
      stitchRun(zipLine(yOff, 0.0009), 0.0042, 0.0026, 0.0009), mats.stitch);
    run.name = 'pocket_zip_stitch'; run.userData.section = 'pocket'; bag.add(run);
  }
  {
    // the panel is trapezoidal, so the side runs must taper with it
    const edge = [], inset = 0.0035;
    const halfAt = (v) => (pw - v * 0.011) / 2 - inset;
    const yAt = (v) => py - ph/2 + v*ph;
    const v0 = inset / ph, v1 = 1 - inset / ph;
    const y0 = yAt(v0), y1 = yAt(v1);
    const push = (x, y) => edge.push(new THREE.Vector3(x, y, zAt(x,y) + 0.0009));
    const across = (v, dir, n) => {
      const h = halfAt(v), y = yAt(v);
      for (let i=0;i<=n;i++){
        const t = i/n;
        push(dir > 0 ? -h + t*h*2 : h - t*h*2, y);
      }
    };
    const down = (sign, from, to, n) => {
      for (let i=0;i<=n;i++){
        const v = from + (to - from)*i/n;
        push(sign * halfAt(v), yAt(v));
      }
    };
    across(v1, 1, 18); down(1, v1, v0, 12);
    across(v0, -1, 18); down(-1, v0, v1, 12);
    const run = new THREE.Mesh(stitchRun(edge, 0.0042, 0.0026, 0.0009), mats.stitch);
    run.name = 'pocket_edge_stitch'; run.userData.section = 'pocket'; bag.add(run);
  }
}

/* ---------- text on the panels ---------- */
const FONTS = [
  ['Montserrat',   '500 {S}px "Montserrat", system-ui, sans-serif'],
  ['Poppins',      '500 {S}px "Poppins", system-ui, sans-serif'],
  ['Raleway',      '500 {S}px "Raleway", system-ui, sans-serif'],
  ['Josefin Sans', '600 {S}px "Josefin Sans", system-ui, sans-serif'],
  ['Quicksand',    '600 {S}px "Quicksand", system-ui, sans-serif'],
  ['Bebas Neue',   '400 {S}px "Bebas Neue", Impact, sans-serif'],
  ['Anton',        '400 {S}px "Anton", Impact, sans-serif'],
  ['Playfair Display', '700 {S}px "Playfair Display", Georgia, serif'],
  ['DM Serif Display', '400 {S}px "DM Serif Display", Georgia, serif'],
  ['Abril Fatface', '400 {S}px "Abril Fatface", Georgia, serif'],
  ['Libre Baskerville', '400 {S}px "Libre Baskerville", Georgia, serif'],
  ['Lora',         '500 {S}px "Lora", Georgia, serif'],
  ['Cormorant Garamond', '600 {S}px "Cormorant Garamond", Georgia, serif'],
  ['Dancing Script', '600 {S}px "Dancing Script", cursive'],
  ['Great Vibes',  '400 {S}px "Great Vibes", cursive'],
];

const TEXT_TARGETS = [
  { id:'front',  label:'Front panel',  face: 1, mirror:false, off: 0.0022,
    x:[-0.11,0.11], y:[0.045,0.190], cx:0, cy:0.130, maxW:0.26, maxH:0.070, ceil:H*0.80-0.058 },
  { id:'back',   label:'Back panel',   face:-1, mirror:true,  off: 0.0022,
    x:[-0.11,0.11], y:[0.045,0.190], cx:0, cy:0.130, maxW:0.26, maxH:0.070, ceil:H*0.80-0.058 },
  { id:'pocket', label:'Inner pocket', face:-1, mirror:false, off:-(TH + 0.0034),
    x:[-0.06,0.06], y:[POCKET_Y-0.056, POCKET_Y+0.004], cx:0, cy:POCKET_Y-0.042,
    maxW:0.15, maxH:0.034, limitW:0.19 },
];

const textTools = {};
for (const T of TEXT_TARGETS){
  const cv = document.createElement('canvas');
  cv.width = 1400; cv.height = 240;
  const tex = new THREE.CanvasTexture(cv);
  // basic (unlit) so the layer modulates the leather instead of being shaded itself:
  // printed ink draws normally, a blind deboss multiplies the panel's own colour
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, alphaTest: 0.02,
    side: THREE.DoubleSide, depthWrite: false,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  mat.name = 'text_' + T.id;
  const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat);
  mesh.name = 'text_' + T.id;
  mesh.userData.section = T.id;
  bag.add(mesh);

  const cfg = { text:'', msg:'', font:0, size:1, x:T.cx, y:T.cy, finish:'print', ink:null, align:'center' };

  const build = () => {
    // keep the strip inside its panel: clamp height, then width, then centre
    const hRaw = T.maxW * cfg.size * (cv.height/cv.width);
    const hgt = Math.min(hRaw, T.maxH);
    const yMax = T.ceil !== undefined
      ? Math.min(T.y[1], T.ceil - hgt/2 - 0.004) : T.y[1];
    cfg.y = Math.min(Math.max(cfg.y, T.y[0]), Math.max(T.y[0], yMax));
    const panelW = 2 * prof(Math.min(Math.max(cfg.y,0),H)/H).w;
    const room = (T.limitW !== undefined ? T.limitW : panelW) - 0.014;
    const w = Math.min(T.maxW * cfg.size, room);
    const half = Math.max(0, room/2 - w/2);
    cfg.x = Math.min(Math.max(cfg.x, -half), half);
    const cols = 40, pos = [], uv = [], idx = [];
    for (let r=0;r<=1;r++){
      const yy = cfg.y - hgt/2 + r*hgt;
      for (let i=0;i<=cols;i++){
        const x = cfg.x - w/2 + (i/cols)*w;
        pos.push(x, yy, zOf(x, Math.min(Math.max(yy,0),H)/H, T.face, 0) + T.face*T.off);
        uv.push(T.mirror ? 1-(i/cols) : i/cols, r);
      }
    }
    for (let i=0;i<cols;i++){
      const A=i, B=A+1, C=A+cols+1, D=C+1;
      idx.push(A,B,C, B,D,C);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv,2));
    g.setIndex(idx); g.computeVertexNormals();
    mesh.geometry.dispose(); mesh.geometry = g;
    if (typeof drawNow === 'function') drawNow();
  };

  const draw = () => {
    const ctx = cv.getContext('2d');
    ctx.clearRect(0,0,cv.width,cv.height);
    const msg = (cfg.msg || '').trim();

    if (msg){
      const al = cfg.align || 'center';
      ctx.textAlign = al; ctx.textBaseline = 'middle';
      // the customer's ink, or by default near-black on a pale hide and cream on a dark one
      ctx.fillStyle = cfg.ink || ((typeof inkOn === 'function' && chosen[T.id])
        ? inkOn(chosen[T.id][1]) : '#000');
      // the strip is a fixed height, so the type is sized to the number of lines
      // it needs — one line sits large, three sit smaller, and none is clipped
      const wrapAt = (px) => {
        ctx.font = FONTS[cfg.font][1].replace('{S}', px);
        const out = [];
        let line = '';
        for (const w of msg.split(/\s+/)){
          const t = line ? line + ' ' + w : w;
          if (ctx.measureText(t).width > cv.width*0.90 && line){ out.push(line); line = w; }
          else line = t;
        }
        if (line) out.push(line);
        return out;
      };
      const PLAN = [[150, 0], [98, 108], [72, 80]];   // [font px, line step]
      let rows = wrapAt(PLAN[0][0]), tier = 0;
      while (rows.length > tier + 1 && tier < PLAN.length - 1){
        tier++;
        rows = wrapAt(PLAN[tier][0]);
      }
      rows = rows.slice(0, tier + 1);
      const [px, step] = PLAN[tier];
      ctx.font = FONTS[cfg.font][1].replace('{S}', px);
      const top = cv.height/2 - (rows.length - 1) * step / 2;
      const ax = al === 'left' ? cv.width*0.04 : al === 'right' ? cv.width*0.96 : cv.width/2;
      rows.forEach((l, i) => ctx.fillText(l, ax, top + i*step, cv.width*0.92));
    }
    mat.alphaTest = 0.02;
    mat.needsUpdate = true;
    if (typeof drawNow === 'function') drawNow();
    tex.needsUpdate = true;
    if (T.id === 'pocket' && typeof drawPocketFlat === 'function') drawPocketFlat();
  };

  build(); draw();
  textTools[T.id] = { cfg, build, draw, T, canvas: cv };
}

/* text tab UI */
const textHost = $('#tab-text');
textHost.innerHTML = `
  <p class="editnote"><b>An edition of one.</b> Stitch a name, a date or a few words of your own
    into the panels or the lining — the one detail no second bag will carry.</p>`
  + TEXT_TARGETS.map(T => `
  <div class="tgroup" data-tgroup="${T.id}">
    <button class="seclabel" data-tfold="${T.id}" aria-expanded="true">
      <b>${T.label}</b><i class="chev">−</i></button>
    <div class="tbody">
    <label class="row"><span>Text or short message</span>
      <textarea rows="2" maxlength="90" data-t="${T.id}" data-k="msg"
        placeholder="A name, a date, a few words worth keeping"></textarea>
      <em class="cap"><b data-out="${T.id}-msg">0</b> of 90 characters</em></label>
    <div class="row two">
      <label class="row"><span>Font</span>
        <select data-t="${T.id}" data-k="font">
          ${FONTS.map((f,i)=>`<option value="${i}">${f[0]}</option>`).join('')}
        </select></label>
      <label class="row"><span>Font size <b class="val" data-out="${T.id}-size">18 pt</b></span>
        <input type="number" min="10" max="28" step="1" value="18"
          data-t="${T.id}" data-k="pt" inputmode="numeric"></label>
    </div>
    <div class="row two">
      <label class="row"><span>Across <b class="val" data-out="${T.id}-x">0</b></span>
        <input type="range" min="${T.x[0]}" max="${T.x[1]}" step="0.002" value="${T.cx}" data-t="${T.id}" data-k="x"></label>
      <label class="row"><span>Height <b class="val" data-out="${T.id}-y">${Math.round(T.cy*100)}cm</b></span>
        <input type="range" min="${T.y[0]}" max="${T.y[1]}" step="0.002" value="${T.cy}" data-t="${T.id}" data-k="y"></label>
    </div>
    <button class="reset applybag panelapply" data-applybag="${T.id}">Apply and see the bag</button>
    </div>
  </div>`).join('');

// the pocket's controls are moved into the pocket window, so events must be
// delegated from an ancestor of BOTH, not from the text tab alone
const textScope = ROOT;
function tEl(sel){
  return textHost.querySelector(sel) || (pocketEl('#pocket-controls') || document).querySelector(sel);
}
textScope.addEventListener('click', (e) => {
  if (!e.target.closest('#tab-text, #pocket-controls, #textcard, [data-applybag]')) return;
  const ab = e.target.closest('[data-applybag]');
  if (ab){
    // inside the pocket window, this button is also what closes it
    const pm = ROOT.querySelector('#pocket-modal');
    if (pm && !pm.hidden){
      closePocketWindow();
      seeTheBag('pocket');
      return;
    }
    const open = [...host.querySelectorAll('.tgroup')]
      .find(g => g.dataset.folded !== '1');
    const which = ab.dataset.applybag === 'auto'
      ? (open ? open.dataset.tgroup : 'front') : ab.dataset.applybag;
    // an explicit way back to the bag, so nobody has to wait for a pause
    clearTimeout(textPeekTimer);
    const el = document.activeElement;
    if (el && el.blur) el.blur();
    seeTheBag(which);
    return;
  }
  const f = e.target.closest('[data-tfold]');
  if (!f) return;
  const id = f.dataset.tfold;
  if (id === 'pocket' && pocketEl('#pocket-modal')){ openPocketWindow(); return; }
  const wasOpen = f.closest('.tgroup').dataset.folded !== '1';
  textHost.querySelectorAll('.tgroup').forEach(g => {
    const on = !wasOpen && g.dataset.tgroup === id;
    g.dataset.folded = on ? '0' : '1';
    const btn = g.querySelector('[data-tfold]');
    if (btn){
      btn.setAttribute('aria-expanded', String(on));
      btn.querySelector('.chev').textContent = on ? '\u2212' : '+';
    }
  });
});
textHost.addEventListener('pointerdown', (e) => {
  const g = e.target.closest('[data-tgroup]');
  if (g) lookAtPanel(g.dataset.tgroup);
});
textHost.querySelectorAll('.tgroup').forEach((g, i) => {
  g.dataset.folded = i === 0 ? '0' : '1';
  const b = g.querySelector('[data-tfold]');
  if (b){ b.setAttribute('aria-expanded', String(i === 0)); b.querySelector('.chev').textContent = i === 0 ? '\u2212' : '+'; }
});
const T_LABELS = { front:'Front panel', back:'Back panel', pocket:'Inner pocket' };
let textPeekTimer = null, lastTextKeys = '';
function peekText(id, tool, immediate){
  // inside the pocket window the only way back to the bag is its own button,
  // so nothing here may step out from under the customer
  const pm = document.querySelector('#pocket-modal');
  if (pm && !pm.hidden) return;
  // step out to the bag when they pause, or as soon as a slider is released —
  // never mid-keystroke, which would eject them from the field
  clearTimeout(textPeekTimer);
  if (!(tool.cfg.msg || '').trim()) return;
  const snapshot = tool.cfg.msg;
  lastTextKeys = snapshot;
  if (immediate){ seeTheBag(id); return; }
  textPeekTimer = setTimeout(() => {
    if (lastTextKeys !== snapshot) return;           // still typing
    const el = document.activeElement;
    if (el && el.blur) el.blur();                    // let go of the keyboard
    seeTheBag(id);
  }, 4000);          // a clear pause, so nobody is moved on mid-thought
}
textScope.addEventListener('change', (e) => {
  if (!e.target.closest('#tab-text, #pocket-controls, #textcard')) return;
  const el = e.target, id = el.dataset.t;
  if (!id) return;
  const tool = textTools[id];
  /* changing a setting no longer moves them; the Apply button does that */
});
textScope.addEventListener('pointerup', (e) => {
  if (true) return;                    // sliders no longer step out to the bag
  if (!e.target.closest('#tab-text, #pocket-controls, #textcard')) return;
  const el = e.target.closest('input[type="range"][data-t]');
  if (!el) return;
  const tool = textTools[el.dataset.t];
  if (tool) peekText(el.dataset.t, tool, true);
});
textScope.addEventListener('input', (e) => {
  const row = e.target.closest && e.target.closest('.row');
  if (row){
    row.removeAttribute('data-typing');
    void row.offsetWidth;                  // restart the mark on every keystroke
    row.dataset.typing = '1';
    clearTimeout(row.__t);
    row.__t = setTimeout(() => row.removeAttribute('data-typing'), 420);
  }
  if (!e.target.closest('#tab-text, #pocket-controls, #textcard')) return;
  const el = e.target, id = el.dataset.t, k = el.dataset.k;
  if (!id) return;
  if (stage._controls && stage._controls.autoRotate){
    // remember that it was turning, so Apply can give it back
    if (textCard && !textCard.__spun) textCard.__spun = true;
    stage._controls.autoRotate = false;
  }
  const tool = textTools[id];
  if (k === 'msg'){
    tool.cfg.msg = el.value;
    // the counter appears on the text page and again in the card
    document.querySelectorAll(`[data-out="${id}-msg"]`)
      .forEach(o => { o.textContent = el.value.length; });
    tool.draw(); syncTextConfirm(); validate();
    return;
  }
  if (k === 'font'){ tool.cfg.font = +el.value; tool.draw(); peekText(id, tool); return; }
  if (k === 'ink'){
    tool.cfg.ink = el.value === 'auto' ? null : el.value;
    tool.draw();
    const mk = document.querySelector('.toolmark[data-tool="ink"] .ico-ink');
    if (mk) mk.style.setProperty('--ink', tool.cfg.ink || '#141312');
    return;
  }
  if (k === 'align'){
    tool.cfg.align = el.value;
    tool.draw();
    const fld = document.getElementById('textcard-field');
    if (fld) fld.style.textAlign = el.value;
    const mk = document.querySelector('.toolmark[data-tool="align"] .ico-align');
    if (mk) mk.dataset.al = el.value;
    return;
  }
  if (k === 'pt'){
    // 18pt is our reference size; the model works in a unitless scale
    const pt = Math.min(28, Math.max(10, Math.round(parseFloat(el.value) || 18)));
    tool.cfg.size = pt / 18;
    if (String(pt) !== el.value) el.value = pt;      // never show a value we ignored
    const out = tEl(`[data-out="${id}-size"]`);
    if (out) out.textContent = pt + ' pt';
    tool.build();
    return;
  }
  tool.cfg[k] = parseFloat(el.value);
  if (k === 'size'){
    const el2 = tEl(`[data-out="${id}-size"]`);
    if (el2) el2.textContent = Math.round(tool.cfg.size * 18) + ' pt';
  }
  if (k === 'x') document.querySelector(`[data-out="${id}-x"]`).textContent = (tool.cfg.x*100).toFixed(1) + 'cm';
  if (k === 'y') document.querySelector(`[data-out="${id}-y"]`).textContent = (tool.cfg.y*100).toFixed(1) + 'cm';
  tool.build();
  peekText(id, tool);
  if (id === 'pocket' && typeof drawPocketFlat === 'function') drawPocketFlat();
  syncTextConfirm();
  const yEl = tEl(`[data-t="${id}"][data-k="y"]`);
  if (yEl && Math.abs(parseFloat(yEl.value) - tool.cfg.y) > 1e-4) yEl.value = tool.cfg.y;
  const yOut = tEl(`[data-out="${id}-y"]`);
  if (yOut) yOut.textContent = (tool.cfg.y*100).toFixed(1) + 'cm';
  const xEl = tEl(`[data-t="${id}"][data-k="x"]`);
  if (Math.abs(parseFloat(xEl.value) - tool.cfg.x) > 1e-4) xEl.value = tool.cfg.x;
  tEl(`[data-out="${id}-x"]`).textContent = (tool.cfg.x*100).toFixed(1) + 'cm';
});

/* text confirmation — only required once there is text to confirm */
const anyText = () => TEXT_TARGETS.some(T => (textTools[T.id].cfg.msg || '').trim());
function syncTextConfirm(){
  const box = $('#text-confirm');
  if (!box) return;
  const on = anyText();
  box.hidden = !on;
  if (!on) $('#textok').checked = false;
}

/* ---------- the inner pocket, opened flat ----------
   1642 x 1344 px at 200 DPI = 20.9 x 17.1 cm of printable leather. The stage
   below is that exact rectangle, so what they position here is what is printed. */
let pocketHome = null;
function pocketEl(sel){ return ROOT.querySelector(sel); }

/* the zip, drawn as it appears on the print spec: a plain bar inside a dashed
   keep-out box near the top of the sheet — not a picture of a zipper */
function drawPocketZip(ctx, w, h, zipH){
  const m = Math.round(w * 0.028);            // the spec's own margin
  const x0 = m, x1 = w - m;
  const y0 = Math.round(h * 0.030), y1 = y0 + zipH;
  ctx.save();
  ctx.fillStyle = '#E8E8E8';                  // the zip, flat grey
  ctx.fillRect(x0 + 6, y0 + 6, (x1 - x0) - 12, zipH - 12);
  ctx.strokeStyle = '#6E6E6E';                // its keep-out box
  ctx.lineWidth = Math.max(2, Math.round(w / 620));
  ctx.setLineDash([Math.round(w / 190), Math.round(w / 260)]);
  ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
  ctx.restore();
  return y1;
}
function drawPocketBounds(ctx, w, h){
  const m = Math.round(w * 0.010);
  ctx.save();
  ctx.strokeStyle = '#5A5A5A';
  ctx.lineWidth = Math.max(2, Math.round(w / 620));
  ctx.setLineDash([Math.round(w / 190), Math.round(w / 260)]);
  ctx.strokeRect(m, m, w - m * 2, h - m * 2);
  ctx.restore();
}

function drawPocketFlat(){
  const pocketCanvas = pocketEl('#pocket-canvas');
  if (!pocketCanvas) return;
  let tool, colour = '#333';
  try { tool = textTools.pocket; colour = chosen.pocket ? chosen.pocket[1] : colour; }
  catch (err) { return; }               // called during init: nothing to draw yet
  const ctx = pocketCanvas.getContext('2d');
  const w = 1642, h = 1344;             // the printable area at 200 DPI
  const zipH = Math.round(h * 0.085);   // the zip's keep-out band, inside the sheet
  const mW = w / 200 * 0.0254;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, w, h);
  // a hint of grain, so the colour is not read as flat ink
  ctx.globalAlpha = 0.06;
  for (let i = 0; i < 2600; i++){
    ctx.fillStyle = i % 2 ? '#000' : '#fff';
    ctx.fillRect(Math.random() * w, Math.random() * h, 2, 2);
  }
  ctx.globalAlpha = 1;

  const zipBottom = drawPocketZip(ctx, w, h, zipH);
  drawPocketBounds(ctx, w, h);
  if (!tool || !(tool.cfg.msg || '').trim()) return;
  const T = TEXT_TARGETS.find(x => x.id === 'pocket');
  const src = tool.canvas;
  if (!T || !src) return;
  const pxPerM = w / mW;
  const stripW = Math.min(T.maxW * tool.cfg.size, mW - 0.008) * pxPerM;
  const stripH = stripW * (src.height / src.width);
  const cx = w / 2 + (tool.cfg.x * pxPerM);
  const cy = h / 2 - ((tool.cfg.y - T.cy) * pxPerM);
  ctx.drawImage(src, cx - stripW / 2, cy - stripH / 2, stripW, stripH);
}

/* the same sheet without the guide marks, at the manufacturer's exact pixel
   size, so it can be handed straight to them */
function pocketPrintSheet(){
  const w = 1642, h = 1344;
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  ctx.fillStyle = (chosen.pocket ? chosen.pocket[1] : '#DCD6C0');
  ctx.fillRect(0, 0, w, h);
  const tool = textTools.pocket;
  const T = TEXT_TARGETS.find(x => x.id === 'pocket');
  if (tool && T && tool.canvas && (tool.cfg.msg || '').trim()){
    const mW = w / 200 * 0.0254, pxPerM = w / mW, src = tool.canvas;
    const sw = Math.min(T.maxW * tool.cfg.size, mW - 0.008) * pxPerM;
    const sh = sw * (src.height / src.width);
    const cx = w/2 + tool.cfg.x * pxPerM;
    const cy = h/2 - (tool.cfg.y - T.cy) * pxPerM;
    ctx.drawImage(src, cx - sw/2, cy - sh/2, sw, sh);
  }
  return out;
}
function downloadPocketSheet(){
  const a = document.createElement('a');
  a.download = 'niadira-inner-pocket-1642x1344.png';
  a.href = pocketPrintSheet().toDataURL('image/png');
  a.click();
}

let pocketSay = null;
function liftPocketSay(){
  if (pocketSay) return pocketSay;
  const modal = pocketEl('#pocket-modal');
  if (!modal) return null;
  const head = modal.querySelector('.photohead');
  const note = modal.querySelector('.anynote');
  if (!head || !note) return null;
  pocketSay = document.createElement('div');
  pocketSay.className = 'pocketsay';
  pocketSay.hidden = true;
  pocketSay.appendChild(head);
  pocketSay.appendChild(note);
  ROOT.appendChild(pocketSay);
  return pocketSay;
}
function openPocketWindow(){
  const pocketSlot = pocketEl('#pocket-controls');
  const g = textHost.querySelector('.tgroup[data-tgroup="pocket"]');
  if (g && pocketSlot && g.parentNode !== pocketSlot){
    pocketHome = { parent: g.parentNode, next: g.nextSibling };
    pocketSlot.appendChild(g);
    g.dataset.folded = '0';
  }
  if (typeof closePanelPick === 'function') closePanelPick(false);
  if (typeof hideShadeBack === 'function') hideShadeBack();
  if (famModal && !famModal.hidden){
    famModal.hidden = true;
    famModal.removeAttribute('data-seethrough');
  }
  lookAtPanel('pocket');
  drawPocketFlat();
  pocketEl('#pocket-modal').hidden = false;
  const say = liftPocketSay();
  if (say) say.hidden = false;
  ROOT.dataset.pocket = 'open';
}
function closePocketWindow(){
  if (pocketSay) pocketSay.hidden = true;
  delete ROOT.dataset.pocket;
  const pocketSlot = pocketEl('#pocket-controls');
  const g = pocketSlot && pocketSlot.querySelector('.tgroup[data-tgroup="pocket"]');
  if (g && pocketHome){
    pocketHome.parent.insertBefore(g, pocketHome.next);
    pocketHome = null;
  }
  pocketEl('#pocket-modal').hidden = true;
}
if (ROOT.querySelector('#pocket-back')){
  // leaving without adding anything: the window closes, the text page stays open
  ROOT.querySelector('#pocket-back').addEventListener('click', () => { closePocketWindow(); });
}
if (pocketEl('#pocket-modal')){
  const legacyApply = ROOT.querySelector('#pocket-apply');
  if (legacyApply) legacyApply.addEventListener('click', () => {
    closePocketWindow();
    seeTheBag('pocket');
  });
}

/* tabs */
function showTab(name){
  const pnl = ROOT.querySelector('.panel');
  if (pnl) pnl.dataset.tab = name;
  ROOT.querySelectorAll('.tab').forEach(b =>
    b.setAttribute('aria-selected', String(b.dataset.tab === name)));
  $('#tab-colour').hidden = name !== 'colour';
  $('#tab-text').hidden = name !== 'text';
}
ROOT.querySelectorAll('.tab').forEach(btn => {
  btn.addEventListener('click', () => {
    if (btn.dataset.tab === 'match'){ $('#match-modal').hidden = false; return; }
    showTab(btn.dataset.tab);
  });
});

try { stage.setObject(bag); } catch (e) {}
// the stage's own loop may never tick in an embedded frame, so paint immediately
// and keep a slow timer running from the outset rather than from launch
(function primeDrawing(){
  const paint = () => {
    try {
      // render only: calling controls.update() here re-fires 'change' and loops
      if (stage._renderer && stage._scene && stage._camera)
        stage._renderer.render(stage._scene, stage._camera);
    } catch (err) {}
  };
  [0, 80, 300, 900].forEach(ms => setTimeout(paint, ms));
})();

/* ---------- click to select ---------- */
const canvas = stage._renderer ? stage._renderer.domElement : null;
const ray = new THREE.Raycaster();
const QSEL = (sel) => ROOT.querySelector(sel);
/* ---------------------------------------------------------------------------
   The bag is the menu.

   A brass mark sits over each customisable panel. Tapping the panel — on the
   model or on its mark — haloes it and opens one small chooser: Colour, Text,
   or Match my photo. Each row opens in place, so the chooser stays put while
   the customer looks; only a real selection sends them back to the bag.
   --------------------------------------------------------------------------- */
const PANEL_ANCHORS = {
  front:  [0, 0.27,  0.100],
  back:   [0, 0.27, -0.100],
  pocket: [0, 0.335, -0.055],
};
const dotHost  = QSEL('#paneldots');
const pickWrap = QSEL('#panelpick');
const pickTitle = QSEL('#pick-title');
let pickedPanel = null;

if (dotHost){
  dotHost.innerHTML = PARTS.filter(s => !s.fixed).map(s => `
    <button class="dotpin" data-dot="${s.id}" aria-label="Customise the ${s.name.toLowerCase()}">
      <i></i><em>${s.name}</em></button>`).join('')
    + '<span class="halo" id="panelhalo" data-on="0"></span>';
}

/* the marks follow the bag as it turns, so they always sit on their own panel */
function placeDots(){
  if (!dotHost || !stage._camera) return;
  const cam = stage._camera;
  cam.updateMatrixWorld(true);
  const box = stage.getBoundingClientRect();
  const host = dotHost.getBoundingClientRect();
  if (box.width < 40) return;
  const V = cam.position.constructor;
  const centre = new V(0, 0.27, 0);
  const camDir = cam.position.clone().sub(centre);
  PARTS.filter(s => !s.fixed).forEach(s => {
    const pin = dotHost.querySelector(`[data-dot="${s.id}"]`);
    if (!pin) return;
    const a = PANEL_ANCHORS[s.id];
    if (!a){ pin.style.display = 'none'; return; }
    const p = new V(a[0], a[1], a[2]);
    // a panel facing away from the camera hides its own mark
    const facing = s.id === 'pocket'
      ? camDir.y > 0.05
      : (a[2] >= 0 ? camDir.z > 0 : camDir.z < 0);
    const q = p.clone().project(cam);
    pin.style.display = facing && q.z < 1 ? 'grid' : 'none';
    pin.style.left = ((q.x + 1) / 2 * box.width + box.left - host.left) + 'px';
    pin.style.top  = ((1 - (q.y + 1) / 2) * box.height + box.top - host.top) + 'px';
    pin.dataset.on = pickedPanel === s.id ? '1' : '0';
    if (pickedPanel === s.id){
      const halo = dotHost.querySelector('#panelhalo');
      if (halo){
        halo.dataset.on = '1';
        halo.style.left = pin.style.left;
        halo.style.top = pin.style.top;
      }
    }
  });
  if (!pickedPanel){
    const halo = dotHost.querySelector('#panelhalo');
    if (halo) halo.dataset.on = '0';
  }
}
setInterval(placeDots, 120);
if (stage._controls && stage._controls.addEventListener)
  stage._controls.addEventListener('change', placeDots);
window.addEventListener('resize', placeDots);

/* a real glow on the panel itself, not only a mark over it */
let glowFade = null;
function glowPanel(id){
  clearTimeout(glowFade);
  // everything other than the chosen panel steps back
  Object.keys(mats).forEach(k => {
    const m = mats[k];
    if (!m) return;
    if (m.__base === undefined) m.__base = m.color.clone();
    if (!id){ m.color.copy(m.__base); }
    else if (k === id){ m.color.copy(m.__base); }
    else {
      const c = m.__base.clone();
      m.color.setRGB(
        c.r * 0.55 + 0.20, c.g * 0.55 + 0.19, c.b * 0.55 + 0.17);
    }
    m.needsUpdate = true;
  });
  Object.keys(mats).forEach(k => {
    const m = mats[k];
    if (!m || !m.emissive) return;
    const on = k === id;
    m.emissive.set(on ? '#8A6616' : '#000000');
    m.emissiveIntensity = on ? 0.34 : 0;
    m.needsUpdate = true;
  });
  drawNow();
  // hold it long enough to see which panel changed, then let the bag be itself
  if (id) glowFade = setTimeout(() => {
    Object.keys(mats).forEach(k => {
      const m = mats[k];
      if (!m) return;
      if (m.emissive){ m.emissive.set('#000000'); m.emissiveIntensity = 0; }
      if (m.__base) m.color.copy(m.__base);        // the bag comes back up
      m.needsUpdate = true;
    });
    drawNow();
  }, 2600);
}

/* ---- the chooser ---- */
function fillPickColour(id){
  const box = QSEL('#pickbody-colour');
  if (!box) return;
  box.innerHTML = '<p class="picknote">Choose a family, then a shade.</p>'
    + '<div class="pickfams">'
    + FAMILIES.map(fm =>
        `<button data-pickfam="${fm[0]}" style="--fc:${FAM_INK[fm[0]]}">${fm[0]}</button>`).join('')
    + `<button class="anycol" data-pickany="1">Any colour</button>
       <button class="anycol" data-pickblack="1" style="--fc:#1B1A18">Back to black</button></div>`;
}
function fillPickText(id){
  // whatever they wrote before is still theirs: show it, do not blank it
  const had = (textTools[id] && textTools[id].cfg &&
    (textTools[id].cfg.msg != null ? textTools[id].cfg.msg : textTools[id].cfg.text)) || '';
  const box = QSEL('#pickbody-text');
  if (!box) return;
  const cur = (textTools[id] && textTools[id].cfg.text) || '';
  box.innerHTML = `
    <p class="picknote">A name, a date, a line worth keeping.</p>
    <div class="picktext">
      <textarea id="picktext-field" maxlength="90" placeholder="Type here">${cur}</textarea>
      <button class="pickgo" data-picktextgo="1">Apply and see the bag</button>
    </div>`;
}
function fillPickMatch(id){
  const box = QSEL('#pickbody-match');
  if (!box) return;
  box.innerHTML = `
    <p class="picknote">Photograph something you own, and we will find the leathers
      that hold its colour.</p>
    <button class="pickgo" data-pickmatchgo="1">Open Match My Photo\u2122</button>`;
  const go = box.querySelector('[data-pickmatchgo]');
  if (go) go.addEventListener('click', () => { closePanelPick(false); showMatch(); });
}

function openPanelPick(id){
  /* with the photograph open, a tap on a panel only turns the bag to it —
     the chooser is not raised over the photo page */
  const photoUp = ['#photo-modal', '#match-modal']
    .some(sel => { const el = document.querySelector(sel); return el && !el.hidden; });
  if (photoUp){
    if (typeof glowPanel === 'function') glowPanel(id);
    if (typeof lookAtPanel === 'function') lookAtPanel(id);
    return;
  }
  if (typeof textCard !== 'undefined' && textCard && !textCard.hidden &&
      typeof openTextCard === 'function'){
    openTextCard(id);
    return;
  }
  photoFlow = false;
  if (typeof hideShadeBack === 'function') hideShadeBack();
  if (famModal && !famModal.hidden){
    famModal.hidden = true;
    famModal.removeAttribute('data-seethrough');
  }
  const sec = PARTS.find(s => s.id === id);
  if (!sec || !pickWrap) return;
  pickedPanel = id;
  glowPanel(id);
  lookAtPanel(id);
  placeDots();
  if (pickTitle) pickTitle.textContent = sec.name;
  pickWrap.hidden = false;
  pickWrap.dataset.arming = '1';
  clearTimeout(pickWrap.__arm);
  pickWrap.__arm = setTimeout(() => pickWrap.removeAttribute('data-arming'), 420);
  placeHub(id);
}

/* the hub sits beside the panel that was touched, not in the middle of the page */
/* The three marks stand in a fixed column clear of the bag, on the right, so
   the leather is never obscured by the menu that changes it. Position is CSS;
   nothing is measured or placed here any more. */
function placeHub(){}
function clearPickArm(){ if (pickWrap){ delete pickWrap.dataset.moved; } }
function closePanelPick(clearGlow){
  if (pickWrap) pickWrap.hidden = true;
  if (typeof closeRibbon === 'function') closeRibbon();
  if (typeof closeTextCard === 'function') closeTextCard();
  if (clearGlow !== false){ pickedPanel = null; glowPanel(null); placeDots(); }
}

const finishBtn = QSEL('#finishorder');
if (finishBtn) finishBtn.addEventListener('click', () => {
  const link = QSEL('#orderopen');
  if (link) link.click();
});
const orderBtn = QSEL('#orderopen');
if (orderBtn) orderBtn.addEventListener('click', () => {
  closePanelPick();
  openDrawer('order', PANEL_TITLES.order, 'order');
});

if (dotHost) dotHost.addEventListener('click', e => {
  const b = e.target.closest('[data-dot]');
  if (b) openPanelPick(b.dataset.dot);
});
if (QSEL('#pick-close')) QSEL('#pick-close').addEventListener('click', () => closePanelPick());
if (pickWrap){
  pickWrap.addEventListener('click', e => { if (e.target === pickWrap) closePanelPick(); });
  /* Three free-standing marks beside the panel — no card, no title bar, and no
     rows opening in place. Each mark does one thing and then steps aside. */
  pickWrap.querySelectorAll('.pickmark').forEach(mark => {
    mark.addEventListener('click', () => {
      const id = pickedPanel;
      if (!id) return;
      if (pickWrap.dataset.arming) return;         // not the tap that opened it
      const k = mark.dataset.pick;
      closePanelPick(false);
      if (k === 'colour'){ openRibbon(id); return; }
      if (k === 'text'){
        if (typeof openTextCard === 'function') openTextCard(id);
        return;
      }
      if (typeof soloSection === 'function') soloSection('photo');
      showMatch();
    });
  });
}

/* ---- the colour ribbon ----------------------------------------------------
   One row of shades, swiped with a thumb. Two tools travel with it: a black
   leather circle that resets the panel, and a lens that opens the photo tool.
   Choosing a shade updates the bag and the ribbon fades away, so the screen
   returns to the product. */
const ribbonEl = QSEL('#colourribbon');
const ribTrack = QSEL('#ribbontrack');
let ribbonFor = null;

function openRibbon(id){
  photoFlow = false;
  if (!ribbonEl || !ribTrack) return;
  if (typeof soloSection === 'function') soloSection('colour');
  clearTimeout(ribHideT); ribHideT = null;   // a pending close must not hide this
  ribbonFor = id;
  const curFam = pickedFamily[id] || familyOf((chosen[id] && chosen[id][1]) || '');
  ribTrack.innerHTML =
    `<button class="ribtool black" data-ribblack="1" aria-label="Back to black">\u27F2</button>`
    + FAMILIES.map(([fam, cols]) => {
        // a pill in the family's own ink, showing five of its shades
        const dots = cols.filter((c, i) => i % Math.ceil(cols.length / 5) === 0)
          .slice(0, 5)
          .map(c => `<i style="background:${c[1]}"></i>`).join('');
        return `<button class="ribfampill" data-ribfam="${fam}"
          style="--fc:${FAM_INK[fam] || 'var(--ink)'}"
          aria-pressed="${fam === curFam}">
          <span class="ribfamdots">${dots}</span>
          <span class="ribfamname">${fam}</span></button>`;
      }).join('')
    + `<button class="ribtool lens" data-riblens="1" aria-label="Match my photo">\u25CE</button>`;
  ribbonEl.removeAttribute('data-out');
  ribbonEl.hidden = false;
  fitRibArrows();
  const tag = ROOT.querySelector('#ribfamnow');
  if (tag){
    tag.textContent = 'Choose a range';
    tag.style.setProperty('--fc', 'var(--brass)');
  }
  ribTrack.scrollTop = 0;
  fitRibArrows();
}
/* names whichever family is under the thumb, so the row is never anonymous */
let ribMap = null;
function buildRibMap(){
  ribMap = [...ribTrack.querySelectorAll('[data-ribin]')]
    .map(c => ({ at: c.offsetLeft + c.offsetWidth / 2, fam: c.dataset.ribin }));
}
function trackRibbonFamily(){
  const tag = QSEL('#ribfamnow');
  if (!tag || !ribTrack || ribbonEl.hidden) return;
  if (!ribMap || !ribMap.length) buildRibMap();
  if (!ribMap.length) return;
  const mid = ribTrack.scrollLeft + ribTrack.clientWidth / 2;
  let fam = ribMap[0].fam, bestD = 1e9;
  for (const e of ribMap){
    const d = Math.abs(e.at - mid);
    if (d < bestD){ bestD = d; fam = e.fam; }
  }
  tag.textContent = 'Shades of ' + fam.replace(/s$/, '');
  tag.style.setProperty('--fc', FAM_INK[fam] || 'var(--ink)');
}
if (ribTrack) ribTrack.addEventListener('scroll', () => {
  clearTimeout(ribTrack.__t);
  ribTrack.__t = setTimeout(trackRibbonFamily, 60);
});

/* the strip can be swiped, but arrows make that obvious and give a thumb
   something definite to aim at */
function ribNudge(dir){
  if (!ribTrack) return;
  ribTrack.scrollBy({ top: dir * Math.max(140, ribTrack.clientHeight * 0.62), behavior:'smooth' });
  setTimeout(fitRibArrows, 380);
}
function fitRibArrows(){
  const L = ROOT.querySelector('#ribprev'), R = ROOT.querySelector('#ribnext');
  if (!L || !R || !ribTrack) return;
  const max = ribTrack.scrollHeight - ribTrack.clientHeight - 2;
  L.hidden = ribTrack.scrollTop <= 2;
  R.hidden = ribTrack.scrollTop >= max;
}
if (ribTrack) ribTrack.addEventListener('scroll', () => {
  clearTimeout(ribTrack.__a);
  ribTrack.__a = setTimeout(fitRibArrows, 80);
});
{
  const L = ROOT.querySelector('#ribprev'), R = ROOT.querySelector('#ribnext');
  if (L) L.addEventListener('click', () => ribNudge(-1));
  if (R) R.addEventListener('click', () => ribNudge(1));
}
window.addEventListener('resize', fitRibArrows);

let ribHideT = null;
function closeRibbon(){
  if (!ribbonEl || ribbonEl.hidden) return;
  ribbonEl.dataset.out = '1';
  clearTimeout(ribHideT);
  ribHideT = setTimeout(() => {
    ribHideT = null;
    ribbonEl.hidden = true;
    ribbonEl.removeAttribute('data-out');
  }, 300);
  ribbonFor = null;
}
if (ribTrack) ribTrack.addEventListener('click', e => {
  const id = ribbonFor;
  if (!id) return;
  if (e.target.closest('[data-ribblack]')){
    apply(id, '#1B1A18', 'Signature Black', true);
    closeRibbon(); closePanelPick(); seeTheBag(id);
    return;
  }
  if (e.target.closest('[data-riblens]')){
    closeRibbon(); closePanelPick();
    showMatch();
    return;
  }
  const pill = e.target.closest('[data-ribfam]');
  if (pill){
    closeRibbon();
    openFamily(id, pill.dataset.ribfam);
    const fm = ROOT.querySelector('#fam-modal');
    if (fm) fm.dataset.seethrough = '1';     // the bag stays visible behind
    return;
  }
  const chip = e.target.closest('[data-ribhex]');
  if (chip){
    const hex = chip.dataset.ribhex;
    apply(id, hex, nameFor(hex), true);
    closeRibbon(); closePanelPick(); seeTheBag(id);
  }
});

/* ---- the short text card ---- */
/* the leather manufacturer's printable areas, quoted as they give them */
const PANEL_PRINT = {
  front: '4585 × 2720 px · 200 DPI',
  back:  '4585 × 2720 px · 200 DPI',
  pocket: '1642 × 1344 px · 200 DPI',
};
const PANEL_RATIO = { front: 4585 / 2720, back: 4585 / 2720, pocket: 1642 / 1344 };

const textCard = QSEL('#textcard');
let textCardFor = null;
function soloSection(keep){
  const shut = {
    text:   () => { if (textCard) textCard.hidden = true; },
    colour: () => { if (typeof closeRibbon === 'function') closeRibbon();
                    const fm = ROOT.querySelector('#fam-modal'); if (fm) fm.hidden = true; },
    photo:  () => { ['#match-modal','#photo-modal','#wheel-modal'].forEach(sel => {
                      const el = ROOT.querySelector(sel); if (el) el.hidden = true; }); },
  };
  Object.keys(shut).forEach(k => { if (k !== keep) shut[k](); });
}
function openTextCard(id){
  photoFlow = false;
  if (!textCard) return;
  soloSection('text');
  textCardFor = id;
  if (typeof glowPanel === 'function') glowPanel(id);
  if (typeof lookAtPanel === 'function') lookAtPanel(id);   // also sets the refit's target
  // hold the bag still, so the lettering can be read as it is typed
  if (stage._controls){
    // remember the turn only while none is pending, so reopening cannot lose it
    if (!textCard.__spun) textCard.__spun = true;   // Apply always gives it back
    stage._controls.autoRotate = false;
  }
  if (typeof setStageLift === 'function') setStageLift(true);
  const c = (textTools[id] && textTools[id].cfg) || {};
  const body = QSEL('#textcard-body');
  if (body){
    /* The typeface, size and placement controls belong with the words. They
       carry the same data-t / data-k attributes as the ones on the text page,
       so the existing delegated handler drives them without duplication. */
    const dim = PANEL_PRINT[id];
    body.innerHTML = `
      ${dim ? `<p class="printnote">Printable area ${dim}</p>` : ''}
      <div class="panelbox"${dim ? ' data-ratio="' + PANEL_RATIO[id] + '"' : ''}${id === 'pocket' ? ' data-zip="1"' : ''}>
        ${id === 'pocket' ? '<span class="zipband" aria-hidden="true"></span>' : ''}
        <textarea id="textcard-field" data-t="${id}" data-k="msg" maxlength="90" style="text-align:${c.align || 'center'}"
          placeholder="A name, a date, a line worth keeping">${panelWords(id)}</textarea>
      </div>
      <em class="cap"><b data-out="${id}-msg">${panelWords(id).length}</b> of 90 characters</em>

      <!-- three marks under the words: each opens its own tool and closes again
           the moment the choice is made -->
      <div class="tooldock" id="tooldock">
        <button class="toolmark" data-tool="font" aria-expanded="false" title="Font">
          <i aria-hidden="true">Aa</i><em>Font</em></button>
        <button class="toolmark" data-tool="size" aria-expanded="false" title="Font size">
          <i aria-hidden="true" class="ico-size"></i><em>Font size</em></button>
        <button class="toolmark" data-tool="place" aria-expanded="false" title="Placement">
          <i aria-hidden="true" class="ico-place"></i><em>Placement</em></button>
        <button class="toolmark" data-tool="ink" aria-expanded="false" title="Font colour">
          <i aria-hidden="true" class="ico-ink" style="--ink:${c.ink || '#141312'}">A</i><em>Colour</em></button>
        <button class="toolmark" data-tool="align" aria-expanded="false" title="Align">
          <i aria-hidden="true" class="ico-align" data-al="${c.align || 'center'}"><b></b><b></b><b></b></i><em>Align</em></button>
      </div>

      <div class="tooldrawer" data-drawer="font" hidden>
        <label class="row"><span>Font</span>
          <select data-t="${id}" data-k="font">
            ${FONTS.map((fo,i) => `<option value="${i}" ${i === c.font ? 'selected' : ''}>${fo[0]}</option>`).join('')}
          </select></label>
      </div>
      <div class="tooldrawer" data-drawer="size" hidden>
        <label class="row"><span>Font size <b class="val" id="textcard-size">${Math.round(c.pt || 18)} pt</b></span>
          <input type="number" min="10" max="28" step="1" value="${Math.round(c.pt || 18)}"
            data-t="${id}" data-k="pt" inputmode="numeric"></label>
      </div>
      <div class="tooldrawer" data-drawer="ink" hidden>
        <div class="inkrow" role="radiogroup" aria-label="Font colour">
          <label class="inkpick" title="Auto">
            <input type="radio" name="ink-${id}" value="auto" data-t="${id}" data-k="ink"
              ${(c.ink || 'auto') === 'auto' ? 'checked' : ''}>
            <i class="inkauto"></i><em>Auto</em></label>
          <label class="inkpick" title="Black">
            <input type="radio" name="ink-${id}" value="#141312" data-t="${id}" data-k="ink"
              ${(c.ink || 'auto') === '#141312' ? 'checked' : ''}>
            <i style="background:#141312"></i><em>Black</em></label>
          <label class="inkpick" title="Cream">
            <input type="radio" name="ink-${id}" value="#F6F1E4" data-t="${id}" data-k="ink"
              ${(c.ink || 'auto') === '#F6F1E4' ? 'checked' : ''}>
            <i style="background:#F6F1E4"></i><em>Cream</em></label>
          <label class="inkpick" title="Gold">
            <input type="radio" name="ink-${id}" value="#B08A3E" data-t="${id}" data-k="ink"
              ${(c.ink || 'auto') === '#B08A3E' ? 'checked' : ''}>
            <i style="background:#B08A3E"></i><em>Gold</em></label>
          <label class="inkpick" title="Silver">
            <input type="radio" name="ink-${id}" value="#BFC3C7" data-t="${id}" data-k="ink"
              ${(c.ink || 'auto') === '#BFC3C7' ? 'checked' : ''}>
            <i style="background:#BFC3C7"></i><em>Silver</em></label>
        </div>
      </div>
      <div class="tooldrawer" data-drawer="align" hidden>
        <div class="alignrow" role="radiogroup" aria-label="Align">
          ${['left','center','right'].map(a => `<label class="alignpick" title="${a === 'center' ? 'Centred' : a[0].toUpperCase() + a.slice(1)}">
            <input type="radio" name="align-${id}" value="${a}" data-t="${id}" data-k="align"
              ${(c.align || 'center') === a ? 'checked' : ''}>
            <i class="ico-align" data-al="${a}"><b></b><b></b><b></b></i>
            <em>${a === 'center' ? 'Centre' : a[0].toUpperCase() + a.slice(1)}</em></label>`).join('')}
        </div>
      </div>
      <div class="tooldrawer" data-drawer="place" hidden>
        <div class="row two">
          <label class="row"><span>Across</span>
            <input type="range" min="${TEXT_TARGETS.find(t=>t.id===id).x[0]}"
              max="${TEXT_TARGETS.find(t=>t.id===id).x[1]}" step="0.002"
              value="${c.x}" data-t="${id}" data-k="x"></label>
          <label class="row"><span>Height</span>
            <input type="range" min="${TEXT_TARGETS.find(t=>t.id===id).y[0]}"
              max="${TEXT_TARGETS.find(t=>t.id===id).y[1]}" step="0.002"
              value="${c.y}" data-t="${id}" data-k="y"></label>
        </div>
      </div>`;
    wireToolDock(body);
  }
  let box = stage.getBoundingClientRect();
  if (box.width < 40 && pickWrap) box = pickWrap.getBoundingClientRect();
  const pin = dotHost && dotHost.querySelector(`[data-dot="${id}"]`);
  let x = box.width / 2, y = box.height / 2;
  if (pin && pin.style.display !== 'none'){
    x = parseFloat(pin.style.left) || x;
    y = (parseFloat(pin.style.top) || y) + 40;
  }
  const halfW = 170, halfH = 175;          // the card is about 320 by 326
  textCard.style.left = Math.min(Math.max(x, halfW), Math.max(halfW, box.width - halfW)) + 'px';
  textCard.style.top  = Math.min(Math.max(y, halfH), Math.max(halfH, box.height - halfH)) + 'px';
  textCard.hidden = false;
  const fld = QSEL('#textcard-field');
  if (fld) fld.focus();
  // keep the card's own readout, and the page's controls, in step with the card
  if (body && !body.__wired){
    body.__wired = true;
    body.addEventListener('input', (e) => {
      const el = e.target, k = el.dataset && el.dataset.k, who = el.dataset && el.dataset.t;
      if (!k || !who) return;
      if (k === 'pt'){
        const out = QSEL('#textcard-size');
        if (out) out.textContent = Math.round(parseFloat(el.value) || 18) + ' pt';
      }
      // the text page holds the same controls; mirror so nothing drifts
      const twin = textHost.querySelector(`[data-t="${who}"][data-k="${k}"]`);
      if (twin && twin.value !== el.value) twin.value = el.value;
    });
  }
}

/* One tool at a time: the mark opens its drawer, and the drawer closes itself
   once the choice is committed — 'change' rather than 'input', so a slider
   closes on release and not mid-drag. */
function wireToolDock(body){
  const dock = body.querySelector('#tooldock');
  if (!dock) return;
  const drawers = [...body.querySelectorAll('.tooldrawer')];
  const shut = () => {
    drawers.forEach(d => d.hidden = true);
    dock.querySelectorAll('.toolmark').forEach(b => {
      b.setAttribute('aria-expanded', 'false');
      b.removeAttribute('data-on');
    });
  };
  dock.addEventListener('click', (e) => {
    const b = e.target.closest('[data-tool]');
    if (!b) return;
    const want = b.dataset.tool;
    const open = b.getAttribute('aria-expanded') === 'true';
    shut();
    if (!open){
      const d = body.querySelector(`.tooldrawer[data-drawer="${want}"]`);
      if (d) d.hidden = false;
      b.setAttribute('aria-expanded', 'true');
      b.dataset.on = '1';
      const first = d && d.querySelector('select, input');
      if (first && first.tagName === 'SELECT') first.focus();
    }
    if (typeof measureCard === 'function') setTimeout(measureCard, 40);
  });
  body.addEventListener('change', (e) => {
    if (!e.target.closest('.tooldrawer')) return;
    shut();
    if (typeof measureCard === 'function') setTimeout(measureCard, 40);
  });
}

/* the bag rides higher while a card is open, so the card never covers its base */
let cardMeasureTimer = null;
function openCardEl(){
  const text = QSEL('#textcard');
  if (text && !text.hidden) return text;
  const photo = QSEL('#photo-modal');
  if (photo && !photo.hidden) return photo.querySelector('.sheet-box') || photo;
  const door = QSEL('#match-modal');
  if (door && !door.hidden) return door.querySelector('.sheet-box') || door;
  return null;
}
function measureCard(){
  const wrap = stage && stage.parentElement;
  if (!wrap || !wrap.dataset.lift) return;
  const card = openCardEl();
  const h = card ? Math.round(card.getBoundingClientRect().height) : 0;
  if (!h) return;
  const was = wrap.style.getPropertyValue('--niadira-cardh');
  const now = h + 'px';
  if (was !== now){
    wrap.style.setProperty('--niadira-cardh', now);
    if (typeof refit === 'function') setTimeout(refit, 60);
  }
}
function setStageLift(on){
  const wrap = stage && stage.parentElement;
  if (!wrap) return;
  if (on){
    wrap.dataset.lift = (on === true ? 'text' : String(on));
    // the card's height is the reserve; it changes as the photograph and its
    // ring arrive, so it is watched rather than measured once
    [0, 80, 260, 600].forEach(ms => setTimeout(measureCard, ms));
    if (!cardMeasureTimer) cardMeasureTimer = setInterval(measureCard, 300);
  } else {
    wrap.removeAttribute('data-lift');
    wrap.style.removeProperty('--niadira-cardh');
    if (cardMeasureTimer){ clearInterval(cardMeasureTimer); cardMeasureTimer = null; }
  }
  if (typeof refit === 'function') setTimeout(refit, 60);
}



/* While a card is open, zooming may enlarge the bag but never carry its base
   beneath the card: after every camera change the bag's lowest point is
   projected, and if it has dropped behind the card the camera eases back
   along its own line until it clears. The card itself never moves. */
let zoomGuardBusy = false;
function guardZoomAboveCard(){
  if (zoomGuardBusy) return;
  const wrap = stage && stage.parentElement;
  const barUp = (() => { const b = document.querySelector('#photoback'); return b && !b.hidden; })();
  if (!wrap || (!wrap.dataset.lift && !barUp) || !stage._camera || !stage._controls) return;
  const sr = stage.getBoundingClientRect();
  const cards = ['#textcard', '#photo-modal .sheet-box', '#match-modal .sheet-box', '#fam-modal .sheet-box', '#photoback']
    .map(sel => document.querySelector(sel))
    .filter(el => el && !el.hidden && el.getBoundingClientRect().height > 20);
  const cardTop = cards.length
    ? Math.min(...cards.map(el => el.getBoundingClientRect().top))
    : sr.bottom;
  const limit = Math.min(sr.bottom, cardTop) - 10;   // just above the card's real top edge
  const cam = stage._camera, ctl = stage._controls;
  const V = cam.position.constructor;
  const lowest = () => {
    cam.updateMatrixWorld(true);
    let low = -Infinity;
    const onPocket = (typeof textCardFor !== 'undefined' && textCardFor === 'pocket');
    const py = (typeof POCKET_Y === 'number') ? POCKET_Y : 0.195;
    const pts = onPocket
      ? [[-0.10, py - 0.07, -0.07], [0.10, py - 0.07, -0.07]]
      : [[-0.28, 0, -0.09], [-0.28, 0, 0.09], [0.28, 0, -0.09], [0.28, 0, 0.09]];
    for (const [x, y, z] of pts){
      const p = new V(x, y, z).project(cam);
      low = Math.max(low, sr.top + (1 - (p.y + 1) / 2) * sr.height);
    }
    return low;
  };
  if (lowest() <= limit) return;
  zoomGuardBusy = true;
  const dir = cam.position.clone().sub(ctl.target);
  const maxD = ctl.maxDistance || Infinity;
  for (let i = 0; i < 24 && lowest() > limit && dir.length() * 1.04 <= maxD; i++){
    dir.multiplyScalar(1.04);
    cam.position.copy(ctl.target).add(dir);
  }
  zoomGuardBusy = false;
}

function frameForReturnBar(){
  if (typeof setStageLift === 'function') setStageLift(false);
  setTimeout(() => {
    if (typeof lookAtPanel === 'function') lookAtPanel(typeof lastLooked !== 'undefined' ? lastLooked : 'front');
  }, 80);
}
function resumeSpin(){
  if (typeof setStageLift === 'function') setStageLift(false);
  if (stage._controls && textCard && textCard.__spun){
    stage._controls.autoRotate = true;
    textCard.__spun = false;
  }
}
function closeTextCard(){
  resumeSpin();                          // the bag turns again on the way out
  if (textCard) textCard.hidden = true;
  textCardFor = null;
}
/* leaving without writing anything: the card closes and the bag is cleared of
   its glow, so nobody is stranded in a field they did not want */
if (QSEL('#textcard-back')) QSEL('#textcard-back').addEventListener('click', () => {
  closeTextCard();                       // resumes the turn
  closePanelPick();
});
if (QSEL('#textcard-go')) QSEL('#textcard-go').addEventListener('click', () => {
  const id = textCardFor, fld = QSEL('#textcard-field'), tool = textTools[id];
  if (fld && !fld.value.trim()){
    // nothing to apply: ask for the words rather than closing on an empty panel
    fld.focus();          // the brass focus rule is the prompt
    return;
  }
  if (id && fld && tool){
    tool.cfg.text = fld.value;
    if (tool.draw) tool.draw();
    if (tool.build) tool.build();
    const real = QSEL(`[data-t="${id}"][data-k="msg"]`);
    if (real) real.value = fld.value;
    if (typeof syncTextConfirm === 'function') syncTextConfirm();
    validate(); drawNow();
  }
  closeTextCard(); closePanelPick();
  if (id) seeTheBag(id);
});

/* tapping the model opens the same chooser for whichever panel was touched */
if (canvas){
  let down = null;
  canvas.addEventListener('pointerdown', e => { down = { x:e.clientX, y:e.clientY }; });
  canvas.addEventListener('pointerup', e => {
    if (!down) return;
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    down = null;
    if (moved > 6) return;                       // that was a drag, not a tap
    const r = canvas.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1
    ), stage._camera);
    const hit = ray.intersectObjects(bag.children, true)[0];
    const sec = hit && hit.object.userData.section;
    if (sec && PARTS.some(s => s.id === sec && !s.fixed)) openPanelPick(sec);
  });
}


/* ---------- colour naming and the grouped palette ---------- */
const WEB_COLOURS = [
['Alice Blue','#F0F8FF'],['Antique White','#FAEBD7'],['Aqua','#00FFFF'],['Aquamarine','#7FFFD4'],
['Azure','#F0FFFF'],['Beige','#F5F5DC'],['Bisque','#FFE4C4'],['Black','#000000'],
['Blanched Almond','#FFEBCD'],['Blue','#0000FF'],['Blue Violet','#8A2BE2'],['Brown','#A52A2A'],
['Burlywood','#DEB887'],['Cadet Blue','#5F9EA0'],['Chartreuse','#7FFF00'],['Chocolate','#D2691E'],
['Coral','#FF7F50'],['Cornflower Blue','#6495ED'],['Cornsilk','#FFF8DC'],['Crimson','#DC143C'],
['Cyan','#00FFFF'],['Dark Blue','#00008B'],['Dark Cyan','#008B8B'],['Dark Goldenrod','#B8860B'],
['Dark Gray','#A9A9A9'],['Dark Green','#006400'],['Dark Khaki','#BDB76B'],['Dark Magenta','#8B008B'],
['Dark Olive Green','#556B2F'],['Dark Orange','#FF8C00'],['Dark Orchid','#9932CC'],['Dark Red','#8B0000'],
['Dark Salmon','#E9967A'],['Dark Sea Green','#8FBC8F'],['Dark Slate Blue','#483D8B'],['Dark Slate Gray','#2F4F4F'],
['Dark Turquoise','#00CED1'],['Dark Violet','#9400D3'],['Deep Pink','#FF1493'],['Deep Sky Blue','#00BFFF'],
['Dim Gray','#696969'],['Dodger Blue','#1E90FF'],['Firebrick','#B22222'],['Floral White','#FFFAF0'],
['Forest Green','#228B22'],['Fuchsia','#FF00FF'],['Gainsboro','#DCDCDC'],['Ghost White','#F8F8FF'],
['Gold','#FFD700'],['Goldenrod','#DAA520'],['Gray','#808080'],['Green','#008000'],
['Green Yellow','#ADFF2F'],['Honeydew','#F0FFF0'],['Hot Pink','#FF69B4'],['Indian Red','#CD5C5C'],
['Indigo','#4B0082'],['Ivory','#FFFFF0'],['Khaki','#F0E68C'],['Lavender','#E6E6FA'],
['Lavender Blush','#FFF0F5'],['Lawn Green','#7CFC00'],['Lemon Chiffon','#FFFACD'],['Light Blue','#ADD8E6'],
['Light Coral','#F08080'],['Light Cyan','#E0FFFF'],['Light Goldenrod Yellow','#FAFAD2'],['Light Gray','#D3D3D3'],
['Light Green','#90EE90'],['Light Pink','#FFB6C1'],['Light Salmon','#FFA07A'],['Light Sea Green','#20B2AA'],
['Light Sky Blue','#87CEFA'],['Light Slate Gray','#778899'],['Light Steel Blue','#B0C4DE'],['Light Yellow','#FFFFE0'],
['Lime','#00FF00'],['Lime Green','#32CD32'],['Linen','#FAF0E6'],['Magenta','#FF00FF'],
['Maroon','#800000'],['Medium Aquamarine','#66CDAA'],['Medium Blue','#0000CD'],['Medium Orchid','#BA55D3'],
['Medium Purple','#9370DB'],['Medium Sea Green','#3CB371'],['Medium Slate Blue','#7B68EE'],['Medium Spring Green','#00FA9A'],
['Medium Turquoise','#48D1CC'],['Medium Violet Red','#C71585'],['Midnight Blue','#191970'],['Mint Cream','#F5FFFA'],
['Misty Rose','#FFE4E1'],['Moccasin','#FFE4B5'],['Navajo White','#FFDEAD'],['Navy','#000080'],
['Old Lace','#FDF5E6'],['Olive','#808000'],['Olive Drab','#6B8E23'],['Orange','#FFA500'],
['Orange Red','#FF4500'],['Orchid','#DA70D6'],['Pale Goldenrod','#EEE8AA'],['Pale Green','#98FB98'],
['Pale Turquoise','#AFEEEE'],['Pale Violet Red','#DB7093'],['Papaya Whip','#FFEFD5'],['Peach Puff','#FFDAB9'],
['Peru','#CD853F'],['Pink','#FFC0CB'],['Plum','#DDA0DD'],['Powder Blue','#B0E0E6'],
['Purple','#800080'],['Rebecca Purple','#663399'],['Red','#FF0000'],['Rosy Brown','#BC8F8F'],
['Royal Blue','#4169E1'],['Saddle Brown','#8B4513'],['Salmon','#FA8072'],['Sandy Brown','#F4A460'],
['Sea Green','#2E8B57'],['Seashell','#FFF5EE'],['Sienna','#A0522D'],['Silver','#C0C0C0'],
['Sky Blue','#87CEEB'],['Slate Blue','#6A5ACD'],['Slate Gray','#708090'],['Snow','#FFFAFA'],
['Spring Green','#00FF7F'],['Steel Blue','#4682B4'],['Tan','#D2B48C'],['Teal','#008080'],
['Thistle','#D8BFD8'],['Tomato','#FF6347'],['Turquoise','#40E0D0'],['Violet','#EE82EE'],
['Wheat','#F5DEB3'],['White','#FFFFFF'],['White Smoke','#F5F5F5'],['Yellow','#FFFF00'],
['Yellow Green','#9ACD32'],
];
function fromHSLpre(hh, s, l){
return '#' + new THREE.Color().setHSL((hh%1+1)%1, s, l, THREE.SRGBColorSpace)
.getHexString(THREE.SRGBColorSpace).toUpperCase();
}
const FAMILIES = (() => {
const tones = [
[0.88, 0.30], [0.78, 0.46], [0.66, 0.60],
[0.54, 0.68], [0.42, 0.66], [0.30, 0.62], [0.20, 0.58],
];
const band = (h0, h1, n) => {
const out = [];
for (let i = 0; i < n; i++){
const hue = (h0 + (h1 - h0) * (n === 1 ? 0.5 : i / (n - 1))) % 360;
for (const [l, sat] of tones) out.push([null, fromHSLpre(hue / 360, sat, l)]);
}
return out;
};
const neutral = (hue, sat, ls) => ls.map(l => [null, fromHSLpre(hue / 360, sat, l)]);
return [
['Reds',    band(348, 374, 4)],
['Pinks',   band(320, 350, 4)],
['Purples', band(258, 315, 4)],
['Blues',   band(196, 250, 5)],
['Greens',  band(80, 172, 5)],
['Yellows', band(38, 62, 4)],
['Browns',  [
...neutral(28, 0.34, [0.72, 0.60, 0.48, 0.38, 0.28, 0.20]),
...neutral(20, 0.50, [0.66, 0.54, 0.42, 0.32, 0.24]),
...neutral(34, 0.22, [0.70, 0.56, 0.44, 0.32]),
]],
['Whites',  neutral(38, 0.10, [1.00, 0.98, 0.96, 0.94, 0.92, 0.90, 0.88, 0.86])],
['Greys',   neutral(0, 0.00, [0.82, 0.74, 0.66, 0.58, 0.50, 0.42, 0.34, 0.26])],
['Blacks',  neutral(0, 0.00, [0.20, 0.16, 0.13, 0.10, 0.07, 0.04])],
];
})();
const SPECTRUM = FAMILIES.flatMap(f => f[1]);
const FAM_INK = {
Reds:'#A82A28', Pinks:'#B4547E', Purples:'#6B4B8A', Blues:'#2F5A8C',
Greens:'#4E6B45', Yellows:'#A8802A', Browns:'#7A4526', Whites:'#B9B1A0',
Greys:'#6E6A63', Blacks:'#22201D',
};
function hexToLab(hex){
const n = parseInt(hex.replace('#',''), 16);
const f = v => { v /= 255; return v <= 0.04045 ? v/12.92 : Math.pow((v+0.055)/1.055, 2.4); };
const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
let X = (r*0.4124 + g*0.3576 + b*0.1805) / 0.95047;
let Y = (r*0.2126 + g*0.7152 + b*0.0722);
let Z = (r*0.0193 + g*0.1192 + b*0.9505) / 1.08883;
const k = t => t > 0.008856 ? Math.cbrt(t) : (7.787*t + 16/116);
X = k(X); Y = k(Y); Z = k(Z);
return [116*Y - 16, 500*(X - Y), 200*(Y - Z)];
}
const dE = (a, b) => Math.hypot(a[0]-b[0], a[1]-b[1], a[2]-b[2]);
const LAB_WEB = WEB_COLOURS.map(c => [c[0], c[1], hexToLab(c[1])]);
const HUE_NAMES = ['Red','Vermilion','Orange','Amber','Gold','Yellow','Lime','Chartreuse',
'Leaf Green','Green','Emerald','Jade','Teal','Aqua','Cyan','Sky Blue','Azure','Blue',
'Indigo','Violet','Purple','Magenta','Fuchsia','Rose'];
const TONE_NAMES = ['Palest','Pale','Light','Mid','Deep','Dark','Darkest'];
const GREY_NAMES = ['Off White','Pale Grey','Light Grey','Grey','Mid Grey','Slate Grey','Charcoal','Near Black'];
function descriptive(hex){
const c = {}; new THREE.Color(hex).getHSL(c, THREE.SRGBColorSpace);
if (c.s < 0.10) return GREY_NAMES[Math.min(7, Math.round((1 - c.l) * 7))];
const hue = HUE_NAMES[Math.round(c.h * 24) % 24];
const tone = TONE_NAMES[Math.min(6, Math.max(0, Math.round((1 - c.l) * 7) - 1))];
return (c.s < 0.30 ? 'Muted ' : '') + tone + ' ' + hue;
}
// the family name as it reads on the panel row: RED SHADES, not REDS
function famLabel(name){
  // the prefix is wrapped so a narrow screen can drop it and keep "(Purple)"
  return name
    ? '(<span class="labpre">Shades of </span>' + name.replace(/s$/, '') + ')'
    : '(Any colour)';
}
function toneOf(hex){
  const c = {}; new THREE.Color(hex).getHSL(c, THREE.SRGBColorSpace);
  return Math.round((1 - c.l) * 7);
}
function nameFor(hex){
  const near = nearestWeb(hex, 1)[0];
  // a borrowed name must agree about the tone, or it describes something else
  if (near.d < 6 && Math.abs(toneOf(hex) - toneOf(near.hex)) <= 1) return near.name;
  return descriptive(hex);
}
function nearestWeb(hex, n){
const lab = hexToLab(hex);
return LAB_WEB.map(c => ({ name:c[0], hex:c[1], d:dE(lab, c[2]) }))
.sort((a,b) => a.d - b.d).slice(0, n || 1);
}
const hsl = hex => { const o = {}; new THREE.Color(hex).getHSL(o, THREE.SRGBColorSpace); return o; };
const fromHSL = (hh, s, l) =>
'#' + new THREE.Color().setHSL((hh%1+1)%1, Math.min(1,Math.max(0,s)),
Math.min(1,Math.max(0,l)), THREE.SRGBColorSpace).getHexString(THREE.SRGBColorSpace).toUpperCase();

/* ---------- panel ---------- */
const chosen = {};
const pick = s => (s.swatches||[]).find(c => c[1].toLowerCase() === s.def.toLowerCase()) || [s.def, s.def];
PARTS.forEach(s => { chosen[s.id] = pick(s); });

const host = $('#sections');
const summary = $('#summary');
host.innerHTML = SECTIONS.map(s => `
  <div class="sec" data-sec="${s.id}" data-open="0">
    <button class="sec-btn" data-toggle="${s.id}">
      <i class="dot" data-dot="${s.id}" style="background:${chosen[s.id][1]}"></i>
      <span class="sec-name">${s.name}</span>
      <span class="sec-val" data-val="${s.id}">${chosen[s.id][0]} · ${chosen[s.id][1].toUpperCase()}</span>
    </button>
    <button class="seclabel" data-fold="${s.id}" aria-expanded="true">
      <b>${s.name}</b>
      <em data-lab="${s.id}" data-labcat="">${chosen[s.id][0]}</em><i class="chev">−</i></button>
    <div class="secbody">
    <div class="famtabs">
      ${FAMILIES.map(fm => `<button data-fam="${fm[0]}" data-famsec="${s.id}"
        style="--fc:${FAM_INK[fm[0]]}"><i></i>${fm[0]}</button>`).join('')}
      <button class="anycol" data-anysec="${s.id}">Any colour</button>
      <button class="toblack" data-black="${s.id}">Back to black</button>
    </div>
    </div>
  </div>`).join('');

PARTS.forEach(s => {
  const el = host.querySelector(`[data-lab="${s.id}"]`);
  if (!el) return;
  const hex = chosen[s.id][1].toUpperCase();
  setFamBalloon(el, hex);
});

const srcMode = {}, srcFind = {};
SECTIONS.forEach(s => { srcMode[s.id] = 'spectrum'; srcFind[s.id] = ''; });
/* a colour may not be a member of any family list (the house defaults, or a
   hex a customer typed), so fall back to the family whose shades sit nearest */
function familyOf(hex){
  const exact = FAMILIES.find(fm => fm[1].some(c => c[1].toUpperCase() === hex.toUpperCase()));
  if (exact) return exact[0];
  const lab = hexToLab(hex);
  let best = null, bestD = Infinity;
  for (const [name, cols] of FAMILIES){
    for (const c of cols){
      const d = dE(lab, hexToLab(c[1]));
      if (d < bestD){ bestD = d; best = name; }
    }
  }
  return bestD < 11 ? best : null;
}
const pickedFamily = {};
function famCatText(hex, known){
  const fam = known || familyOf(hex);
  return fam || 'Any colour';
}
/* the balloon is drawn in the family's own ink, so it reads as that swatch */
function setFamBalloon(el, hex, known){
  if (!el) return;
  // the pill is the chosen colour itself; its ink flips for legibility
  el.style.setProperty('--sc', hex);
  const c = String(hex).replace('#','');
  const lum = (parseInt(c.slice(0,2),16)*0.299 + parseInt(c.slice(2,4),16)*0.587
             + parseInt(c.slice(4,6),16)*0.114) / 255;
  el.style.setProperty('--sc-ink', lum > 0.62 ? '#1B1A18' : '#FFF8E8');
  el.dataset.labcat = famCatText(hex, known);
  const fam = known || familyOf(hex);
  el.style.setProperty('--fc', (fam && FAM_INK[fam]) || 'var(--ink)');
}

let famSec = null, famName = null, anyColourTimer = null;
const famModal = $('#fam-modal'), famTitle = $('#fam-title'), famBody = $('#fam-body');
function tileFor(id, c0, seen){
  let c = c0[0] ? c0 : [nameFor(c0[1]), c0[1]];
  if (seen.has(c[0]) && !c0[0]) c = [descriptive(c0[1]), c0[1]];
  let n = c[0];
  if (seen.has(n)) n = 'Near ' + nearestWeb(c[1],1)[0].name;
  if (seen.has(n)) n = c[0] + ' ' + c[1].toUpperCase();
  seen.add(n);
  return `<button class="sw" data-sec="${id}" data-color="${c[1]}" data-label="${n}"
    aria-pressed="${c[1].toUpperCase()===chosen[id][1].toUpperCase()}">
    <i style="background:${c[1]}"></i><span>${n}<em>${c[1].toUpperCase()}</em></span></button>`;
}
function openFamily(id, name){
  famSec = id; famName = name;
  famModal.dataset.any = '0';
  famModal.removeAttribute('data-seethrough');
  const sec = SECTIONS.find(x => x.id === id);
  const fam = FAMILIES.find(x => x[0] === name);
  const seen = new Set();
  famTitle.innerHTML = `${sec.name}
    <em class="famnow" id="fam-now"><i style="background:${chosen[id][1]}"></i>${chosen[id][0]}</em>`;
  const tiles = fam[1].map(c => tileFor(id, c, seen));
  famBody.innerHTML = splitColumns(tiles);
  famBody.scrollTop = 0;
  famModal.hidden = false;
  playEntrance(famModal);
}
/* Two columns either side of the bag. The column height decides how many fit,
   and anything left over goes to the far side rather than off the page. */
function splitColumns(tiles){
  const box = stage.getBoundingClientRect();
  const tall = Math.max(240, (box.height || 700) - 150);
  const per  = Math.max(4, Math.floor(tall / 46));       // a circle plus its gap
  const left = tiles.slice(0, Math.min(per, Math.ceil(tiles.length / 2)));
  const right = tiles.slice(left.length, left.length + per);
  const rest = tiles.slice(left.length + right.length);
  // anything still over is shared back into the two columns, tightening the gap
  const l = left.concat(rest.filter((_, i) => i % 2 === 0));
  const r = right.concat(rest.filter((_, i) => i % 2 === 1));
  return `<div class="swcols">
      <div class="swcol">${l.join('')}</div>
      <div class="swcol right">${r.join('')}</div>
    </div>`;
}

/* one line of shades, stepped by its arrows */
function wireShadeRow(){
  const row = famBody.querySelector('.swgrid');
  const L = famBody.querySelector('[data-shade="-1"]');
  const R = famBody.querySelector('[data-shade="1"]');
  if (!row || !L || !R) return;
  const fit = () => {
    const max = row.scrollWidth - row.clientWidth - 2;
    L.hidden = row.scrollLeft <= 2;
    R.hidden = row.scrollLeft >= max;
  };
  const step = (dir) => {
    row.scrollLeft += dir * Math.max(120, row.clientWidth * 0.7);
    fit();
  };
  L.addEventListener('click', () => step(-1));
  R.addEventListener('click', () => step(1));
  row.addEventListener('scroll', fit);
  requestAnimationFrame(fit);
  setTimeout(fit, 60);
  setTimeout(fit, 260);
}

function openAnyColour(id){
  famSec = id; famName = null;
  famModal.dataset.any = '1';
  playEntrance(famModal);
  const sec = SECTIONS.find(x => x.id === id);
  famTitle.innerHTML = `${sec.name}
    <em class="famnow" id="fam-now"><i style="background:${chosen[id][1]}"></i>${chosen[id][0]}</em>`;
  const seenAny = new Set();
  const spectrum = isTouch()
    ? `<div class="swgrid anygrid">${SPECTRUM.map(c => tileFor(id, c, seenAny)).join('')}</div>`
    : '';
  famBody.innerHTML = `
    <p class="anynote">${isTouch()
      ? 'Choose any shade below. If you already have a colour reference, you can enter it.'
      : 'Choose with the picker, or enter a colour reference if you have one.'}</p>
    <div class="anyrow">
      <input class="hexin" type="text" id="fam-hex" maxlength="7" spellcheck="false"
        placeholder="#RRGGBB" value="${chosen[id][1]}">
      ${isTouch() ? '' : `<label class="exact"><span>Pick</span>
        <input type="color" id="fam-pick" value="${chosen[id][1]}"></label>`}
    </div>
    ${spectrum}`;
  famModal.hidden = false;
}

host.querySelectorAll('.sec').forEach((el, i) => {
  el.dataset.folded = i === 0 ? '0' : '1';
  const b = el.querySelector('[data-fold]');
  if (b){ b.setAttribute('aria-expanded', String(i === 0)); b.querySelector('.chev').textContent = i === 0 ? '\u2212' : '+'; }
});

const VIEWS = {
  front:  { pos:[0.10, 0.44, 1.24], tgt:[0, 0.26, 0] },
  back:   { pos:[-0.10, 0.44, -1.24], tgt:[0, 0.26, 0] },
  pocket: { pos:[0.04, 0.86, 0.62], tgt:[0, 0.30, -0.03] },
};
let viewAnim = null, viewSettle = null;
let lastLooked = 'front';
let fitScale = 1;                // what the measured fit has learned
/* the vertical band the overlays occupy, as a fraction of the stage */
let photoFlow = false;
function showMatch(){
  photoFlow = true;
  if (typeof setStageLift === 'function') setStageLift('photo');
  const have = mshot && mshot.classList.contains('on');
  if (matchModal){
    matchModal.hidden = false;
    if (have) matchModal.dataset.hasphoto = '1';
    else matchModal.removeAttribute('data-hasphoto');
  }
  if (photoModal) photoModal.hidden = !have;   // the photograph, or the door
}
function syncPhotoBack(){
  const pb = ROOT.querySelector('#photoback') || document.querySelector('#photoback');
  if (!pb) return;
  const loaded = mshot && mshot.classList.contains('on');
  const busy = ['#photo-modal','#match-modal','#panelpick','#textcard','#pocket-modal',
                '#fam-modal','#revealbar','#swatchoffer','#sheet']
    .some(sel => { const el = ROOT.querySelector(sel) || document.querySelector(sel);
                   return el && !el.hidden; });
  const drawer = ROOT.querySelector('.panel');
  const drawerOpen = drawer && drawer.dataset.drawer === 'open';
  const want = !!loaded && photoFlow && !busy && !drawerOpen;

  /* and when no flow is running, the way on: one clear act to finish. */
  const go = ROOT.querySelector('#finishorder') || document.querySelector('#finishorder');
  const allChosen = ['front','back','pocket'].every(id => pickedNow[id]);
  if (go) go.hidden = !(allChosen && !busy && !drawerOpen && !want);
  if (want && pb.hidden){
    if (typeof setPhotoThumb === 'function') setPhotoThumb(pb);
    const lab = ROOT.querySelector('#photoback-label');
    if (lab && !lab.textContent.trim()) lab.textContent = 'Your photograph, on the bag';
    pb.hidden = false;
    frameForReturnBar();
  } else if (!want && !pb.hidden){
    pb.hidden = true;
  }
}
setInterval(syncPhotoBack, 350);

let cardWatch = null;
function watchCards(){
  if (cardWatch) return;
  cardWatch = setInterval(() => {
    const now = Math.round(bottomCardPx() / 20);
    if (now !== watchCards.last){ watchCards.last = now; fitToBand(); clearTheCard(); }
  }, 300);
}
function bottomCardPx(){
  // the tallest card currently sitting over the lower part of the stage
  const sr = stage.getBoundingClientRect();
  let deepest = 0;
  ['#photo-modal .sheet-box', '#textcard', '#panelpick', '.ribbon']
    .forEach(sel => {
      const el = (typeof QSEL === 'function' ? QSEL(sel) : null)
             || document.querySelector(sel);
      if (!el || el.hidden || !el.offsetParent) return;
      const r = el.getBoundingClientRect();
      if (r.height < 40) return;
      const intrudes = sr.bottom - r.top;          // how far up it reaches
      if (intrudes > deepest) deepest = intrudes;
    });
  return Math.max(0, Math.min(deepest, sr.height * 0.62));
}
watchCards();
function chromeBand(){
  const h = stage.clientHeight || 1;
  return { top: 14 / h, bottom: Math.max(14, bottomCardPx() + 12) / h };
}
/* pull back until the whole bag sits inside that band, with a little air */
function reserveChrome(){
  const cam = stage._camera;
  const w = stage.clientWidth, h = stage.clientHeight;
  if (!cam || w < 2 || h < 2) return;
  let topPx = 60;
  const icons = (typeof QSEL === 'function' ? QSEL('.pickmarks') : null);
  if (icons){
    const r = icons.getBoundingClientRect();
    if (r.height > 4) topPx = Math.max(topPx, r.bottom - stage.getBoundingClientRect().top);
  }
  let c = Math.round(Math.min(h * 0.34, topPx + 92));
  const card = bottomCardPx();
  if (card > h * 0.22) c = Math.round(Math.min(c, topPx + 8));   // room is above
  // render as though the stage were taller, and take the lower part of it
  cam.setViewOffset(w, h + c, 0, c, w, h);
  cam.updateProjectionMatrix();
}
function clearTheCard(){
  const card = bottomCardPx();
  if (card < 40) return;
  const cam = stage._camera, ctl = stage._controls;
  if (!cam || !ctl) return;
  const sr = stage.getBoundingClientRect();
  const V = cam.position.constructor;
  for (let i = 0; i < 10; i++){
    cam.updateMatrixWorld(true);
    let lo = 1;
    for (const x of [-0.28, 0.28]) for (const y of [0, 0.60]) for (const z of [-0.09, 0.09])
      lo = Math.min(lo, new V(x, y, z).project(cam).y);
    const bagBottom = sr.top + (1 - (lo + 1) / 2) * sr.height;
    const limit = sr.bottom - card - 10;
    if (bagBottom <= limit) break;
    const off = cam.position.clone().sub(ctl.target);
    cam.position.copy(ctl.target).add(off.multiplyScalar(1.07));
    ctl.update();
  }
  if (typeof drawNow === 'function') drawNow();
}
function fitToBand(){
  reserveChrome();
  const cam = stage._camera, ctl = stage._controls;
  if (!cam || !ctl) return;
  const band = chromeBand();
  const V = cam.position.constructor;
  const corners = [];
  for (const x of [-0.28, 0.28]) for (const y of [0, 0.60]) for (const z of [-0.09, 0.09])
    corners.push([x, y, z]);
  for (let i = 0; i < 14; i++){
    cam.updateMatrixWorld(true);
    let lo = 1, hi = -1, lft = 1, rgt = -1;
    for (const c of corners){
      const p = new V(c[0], c[1], c[2]).project(cam);
      if (p.y < lo) lo = p.y;
      if (p.y > hi) hi = p.y;
      if (p.x < lft) lft = p.x;
      if (p.x > rgt) rgt = p.x;
    }
    // NDC: +1 is the top and the right edge of the stage
    const overTop = hi - (1 - band.top * 2);
    const overBot = (-1 + band.bottom * 2) - lo;
    const overSide = Math.max(rgt - 0.94, -0.94 - lft);   // a hair of margin
    const over = Math.max(overTop, overBot, overSide);
    // bring the silhouette back to the middle of the band before pushing away,
    // because distance alone cannot correct an aim that is off centre
    const safeMid = ((1 - band.top * 2) + (-1 + band.bottom * 2)) / 2;
    const mid = (hi + lo) / 2;
    if (Math.abs(mid - safeMid) > 0.03){
      const span = Math.max(0.2, hi - lo);
      const world = (mid - safeMid) * (0.60 / span) * 0.55;
      ctl.target.y += world;
      cam.position.y += world;
      ctl.update();
      continue;                                    // measure again from the new aim
    }
    if (over <= 0.02) break;                       // it fits, with air
    const grow = 1 + Math.min(0.12, over * 0.5);
    const dir = cam.position.clone().sub(ctl.target);
    cam.position.copy(ctl.target).addScaledVector(dir.normalize(), dir.length() * grow);
    ctl.update();
    fitScale = Math.min(2.2, fitScale * grow);     // remembered for the next reframe
  }
  if (typeof drawNow === 'function') drawNow();
}

function lookAtPanel(id){
  lastLooked = id || lastLooked;
  const v = VIEWS[id];
  if (!v || !stage._camera || !stage._controls) return;
  const mob = isMobile();
  const portrait = (stage.clientWidth || 1) / (stage.clientHeight || 1) < 1;
  let lift = (mob && portrait) ? -0.05 : 0;   // keep the handles clear of the tabs
  // a card open below takes the lower 44%, so the bag sits into what is left,
  // clear of the heading above and the card beneath
  const carded = stage.parentElement && stage.parentElement.dataset.lift;
  if (mob && carded) lift -= (carded === 'photo' && id !== 'pocket' ? 0.26 : 0.12);  // a lower target lifts it
  /* The photograph's card only sits beneath the bag in the stacked layout —
     on a wide screen it is beside it, and lifting there threw the handles off
     the top of the stage. */
  const stacked = window.matchMedia('(max-width: 900px)').matches;
  const photoUp = stacked && [ROOT.querySelector('#photo-modal'), ROOT.querySelector('#match-modal')]
    .some(el => el && !el.hidden);
  if (photoUp) lift -= 0.30;
  const cam = stage._camera, ctl = stage._controls;
  const p0 = cam.position.clone(), t0 = ctl.target.clone();
  const p1 = new THREE.Vector3(...v.pos), t1 = new THREE.Vector3(...v.tgt);
  if (mob){
    // keep each panel's own direction, but derive the distance from the stage's
    // real aspect so the bag fills the frame at portrait AND landscape
    const top = id === 'pocket';
    const h = 1.30, w = 1.15;                       // bag bounds + margin, metres
    reserveChrome();
  const aspect = (stage.clientWidth || 1) / (stage.clientHeight || 1);
    const fov = (stage._camera.fov || 34) * Math.PI / 180;
    const dV = (h / 2) / Math.tan(fov / 2);         // height-limited distance
    const dH = (w / 2) / Math.tan(fov / 2) / aspect;// width-limited distance
    // the app bar and the icon band sit over the top of the stage, so the bag
    // is framed into what is left rather than the whole rectangle
    const chrome = 176 / Math.max(1, stage.clientHeight || 1);
    const tall = (stage.clientHeight || 1) / Math.max(1, stage.clientWidth || 1);
    const near = tall > 1.4 ? 0.74 : 1;           // portrait: fill more of it
    let dist = Math.max(dV, dH) * (top ? 0.58 : 0.52) * near * fitScale;
    // set the direction first and independently of distance, or raising the
    // distance tilts the camera further overhead and the bag grows instead
    p1.normalize();
    p1.y = top ? 1.15 : 0.22;
    if (mob && carded) dist *= 1.24;      // a little further off, so it fits
    p1.normalize().multiplyScalar(dist);
    // and the target drops by half that band, which carries the bag downward
    t1.set(0, top ? 0.34 : 0.30, top ? -0.03 : 0);
  }
  const pocketClose = mob && carded && id === 'pocket';
  if (pocketClose){
    const py = (typeof POCKET_Y === 'number') ? POCKET_Y : 0.195;
    t1.set(0, py, -0.06);
    p1.set(0, 0.86, 0.52).normalize().multiplyScalar(0.64).add(t1);
    lift = 0;
  }
  // the bag spans y 0 → 0.60, so the aim must stay within it
  const aimed = t1.y + lift;
  const floor = 0.12;
  if (aimed < floor) lift += (floor - aimed);
  p1.y += lift; t1.y += lift;
  if (p0.distanceTo(p1) < 0.004) return;
  const t = performance.now();
  if (viewAnim) cancelAnimationFrame(viewAnim);
  if (viewSettle) clearTimeout(viewSettle);
  let done = false;
  const land = () => {
    if (done) return;
    done = true;
    cam.position.copy(p1); ctl.target.copy(t1); ctl.update();
    if (!pocketClose){ fitToBand(); clearTheCard(); }   // the close-up keeps its framing
    if (typeof drawNow === 'function') drawNow();
  };
  const step = () => {
    if (done) return;
    const k = Math.min(1, (performance.now() - t) / 620);
    const e = k < 0.5 ? 2*k*k : 1 - Math.pow(-2*k+2, 2)/2;
    cam.position.lerpVectors(p0, p1, e);
    ctl.target.lerpVectors(t0, t1, e);
    ctl.update();
    if (typeof drawNow === 'function') drawNow();
    if (k < 1) viewAnim = requestAnimationFrame(step);
    else { viewAnim = null; done = true; fitToBand(); clearTheCard(); }
  };
  step();
  // background tabs and embedded frames throttle rAF; make sure the view still lands
  viewSettle = setTimeout(land, 700);
}
function isMobile(){ return true; }   // one stacked layout at every width
function openSection(id){
  // one route for every view: the full-screen Colour window, scrolled to that
  // panel — no separate bottom tray, which had no way to dismiss
  host.querySelectorAll('.sec').forEach(el => el.dataset.open = '0');
  lookAtPanel(id);
  openDrawer('colour', PANEL_TITLES.colour, 'colour');
  const bring = () => {
    // fold the other panels so the wanted one can actually reach the top
    host.querySelectorAll('.sec').forEach(el => {
      el.dataset.folded = el.dataset.sec === id ? '0' : '1';
      const b = el.querySelector('[data-fold]');
      if (b){
        b.setAttribute('aria-expanded', String(el.dataset.sec === id));
        const c = b.querySelector('.chev');
        if (c) c.textContent = el.dataset.sec === id ? '\u2212' : '+';
      }
    });
    const lab = host.querySelector(`.sec[data-sec="${id}"] .seclabel`);
    if (lab && panelEl){
      const top = lab.getBoundingClientRect().top - panelEl.getBoundingClientRect().top;
      panelEl.scrollTop = Math.max(0, panelEl.scrollTop + top - 8);
    }
  };
  bring();
  setTimeout(bring, 60);
  setTimeout(bring, 240);
}
const touched = {};
function tonal(hex){
  const c = new THREE.Color(hex);
  const hsl = {}; c.getHSL(hsl);
  return new THREE.Color().setHSL(hsl.h, hsl.s, Math.max(0.06, hsl.l * 0.78));
}
function apply(id, color, label, userPick){
  if (mats[id]) mats[id].__base = undefined;       // a new colour is the new base
  if (userPick) swatchOrdered = false;
  chosen[id] = [label, color];
  if (id === 'front') mats.stitchSide.color.copy(tonal(color));
  if (userPick) touched[id] = true;
  if (userPick && !restoring) pickedNow[id] = true;
  mats[id].color.set(color);
  if (mats[id].__sweep) mats[id].__sweep.edge.value = BAG_FOOT - 1;
  host.querySelector(`[data-dot="${id}"]`).style.background = color;
  host.querySelector(`[data-val="${id}"]`).textContent = label;
  const lab = host.querySelector(`[data-lab="${id}"]`);
  if (lab) lab.textContent = label;
  setFamBalloon(lab, color, pickedFamily[id]);
  if (famSec === id){
    const now = ROOT.querySelector('#fam-now');
    if (now) now.innerHTML = `<i style="background:${color}"></i>${label}`;
  }
  host.querySelectorAll(`.sw[data-sec="${id}"]`).forEach(b =>
    b.setAttribute('aria-pressed', String(b.dataset.color.toUpperCase() === color.toUpperCase())));
  renderSummary();
  if (id === 'pocket' && typeof drawPocketFlat === 'function') drawPocketFlat();
  validate();
  if (typeof drawNow === 'function') drawNow();
  if (!restoring && typeof saveLocal === 'function') saveLocal();
}
const partLabel = s => s.fixed ? (s.label || nameFor(s.def)) : chosen[s.id][0];
function renderSummary(){
  summary.innerHTML = PARTS.map(s => {
    return `${s.name} — <b>${partLabel(s)}</b>`;
  }).join('<br>');
}
renderSummary();

const closeSwatchPopup = () => host.querySelectorAll('.sec').forEach(el => el.dataset.open = '0');
/* the hover name is one shared element, placed from the tile's own rectangle and
   clamped to the window, so a scrolling grid can never clip it */
const swTip = ROOT.querySelector('#swtip');
if (swTip && swTip.parentElement !== ROOT) ROOT.appendChild(swTip);
function armedTile(){ return famBody && famBody.querySelector('.sw[data-armed="1"]'); }
function hideSwTip(){
  if (!isTouch()){
    const a = armedTile();
    if (a) a.dataset.armed = '0';
  }
  if (!swTip) return;
  swTip.dataset.on = '0';
  swTip.style.opacity = '0';
  swTip.hidden = true;
}
function showSwTip(sw){
  if (!swTip) return;
  const name = sw.dataset.label || '';
  const hex = (sw.dataset.color || '').toUpperCase();
  swTip.innerHTML = name;
  swTip.hidden = false;
  swTip.dataset.on = '0';
  swTip.dataset.below = '0';
  const r = sw.getBoundingClientRect();
  const box = { left:0, right:window.innerWidth, top:0, bottom:window.innerHeight };
  // measure once placed, then clamp inside the window's own box
  swTip.style.left = '0px'; swTip.style.top = '0px';
  const tw = swTip.offsetWidth, th = swTip.offsetHeight;
  let cx = r.left + r.width / 2;
  const min = box.left + tw / 2 + 8, max = box.right - tw / 2 - 8;
  const clamped = Math.min(Math.max(cx, min), max);
  let top = r.top - th - 9;
  if (top < box.top + 4){                       // no room above: sit beneath
    top = r.bottom + 9;
    swTip.dataset.below = '1';
  }
  swTip.style.left = Math.round(clamped) + 'px';
  swTip.style.top = Math.round(top) + 'px';
  // the pointer follows the tile even when the label has been nudged sideways
  swTip.style.setProperty('--arrow', Math.round(tw / 2 + (cx - clamped)) + 'px');
  swTip.dataset.on = '1';
  swTip.style.opacity = '1';
}
famModal.addEventListener('pointerover', e => {
  const sw = e.target.closest('.sw');
  if (isTouch()) return;                    // no hover on a touch screen
  if (sw) showSwTip(sw); else hideSwTip();
});
famModal.addEventListener('pointerleave', () => { if (!isTouch()) hideSwTip(); });
let swSliding = false;
famModal.addEventListener('touchstart', e => {
  if (!famBody || !famBody.contains(e.target)) return;
  swSliding = !!e.target.closest('.sw');
}, { passive:true });
famModal.addEventListener('touchmove', e => {
  if (!swSliding) return;
  const t = e.touches && e.touches[0];
  if (!t) return;
  const el = document.elementFromPoint(t.clientX, t.clientY);
  const sw = el && el.closest ? el.closest('.sw') : null;
  if (sw) showSwTip(sw);
}, { passive:true });
famModal.addEventListener('touchend', () => { swSliding = false; }, { passive:true });
famModal.addEventListener('pointerdown', e => {
  const sw = e.target.closest('.sw');
  if (sw){
    showSwTip(sw);                       // name it the moment it is touched
    return;                              // arming belongs to the click handler
  }
  hideSwTip();
});
if (famBody) famBody.addEventListener('scroll', () => {
  if (!isTouch()) hideSwTip();
});

famModal.addEventListener('change', e => {
  if (!famSec) return;
  if (!e.target.closest('#fam-hex, #fam-pick')) return;
  clearTimeout(anyColourTimer);
  const el = document.activeElement;
  if (el && el.blur) el.blur();
  famModal.hidden = true;
  seeTheBag(famSec);
});
famModal.addEventListener('keydown', e => {
  if (e.key !== 'Enter' || !famSec) return;
  if (!e.target.closest('#fam-hex')) return;
  e.preventDefault();
  clearTimeout(anyColourTimer);
  e.target.blur();
  famModal.hidden = true;
  seeTheBag(famSec);
});

/* the way back to the shades just left */
let shadeBack = null;
function showShadeBack(b){
  if (typeof closePanelPick === 'function') closePanelPick(false);
  shadeBack = b;
  const bar = ROOT.querySelector('#shadeback');
  const lab = ROOT.querySelector('#shadeback-label');
  if (!bar || !lab || !b) return;
  const sec = PARTS.find(x => x.id === b.id);
  lab.textContent = (sec ? sec.name + ' \u00B7 ' : '') + b.label;
  bar.querySelector('button').textContent = b.fam
    ? '\u2190 Back to ' + b.fam.toLowerCase()
    : '\u2190 Back to the shades';
  bar.hidden = false;
}
function hideShadeBack(){
  const bar = ROOT.querySelector('#shadeback');
  if (bar) bar.hidden = true;
  shadeBack = null;
}
{
  const bar = ROOT.querySelector('#shadeback');
  if (bar) bar.querySelector('button').addEventListener('click', () => {
    if (!shadeBack) return;
    const b = shadeBack;
    hideShadeBack();
    if (b.fam) openFamily(b.id, b.fam);
    else openAnyColour(b.id);
    const fm = ROOT.querySelector('#fam-modal');
    if (fm) fm.dataset.seethrough = '1';
  });
}

famModal.addEventListener('click', e => {
  if (!(isTouch() && e.target.closest('.sw'))) hideSwTip();
  if (e.target.id === 'fam-close' || e.target === famModal){ famModal.hidden = false; famModal.hidden = true; return; }
  const sw = e.target.closest('.sw');
  if (sw && isTouch() && sw.dataset.armed !== '1'){
    // first tap names the shade; a second tap on the same tile applies it
    famBody.querySelectorAll('.sw[data-armed="1"]').forEach(x => x.dataset.armed = '0');
    sw.dataset.armed = '1';
    showSwTip(sw);
    return;
  }
  if (sw){
    const label = sw.dataset.label.replace(/^Nearest: /, '');
    if (famName) pickedFamily[sw.dataset.sec] = famName;
    else delete pickedFamily[sw.dataset.sec];
    apply(sw.dataset.sec, sw.dataset.color, label, true);
    const back = { id: sw.dataset.sec, fam: famName, label };
    seeTheBag(sw.dataset.sec);            // records the open category, then closes it
    showShadeBack(back);
  }
});
famModal.addEventListener('input', e => {
  if (!famSec) return;
  const hx = e.target.closest('#fam-hex'), pk = e.target.closest('#fam-pick');
  const raw = hx ? hx.value.trim().replace('#','') : pk ? pk.value.replace('#','') : null;
  if (!raw || !/^[0-9a-fA-F]{6}$/.test(raw)) return;
  const hex = '#' + raw.toUpperCase();
  delete pickedFamily[famSec];
  apply(famSec, hex, nameFor(hex), true);
  clearTimeout(anyColourTimer);
  // let them finish typing, then show the panel they have just coloured
  anyColourTimer = setTimeout(() => {
    const el = document.activeElement;
    if (el && el.blur) el.blur();
    famModal.hidden = true;
    seeTheBag(famSec);
  }, 1100);
  const prev = document.getElementById('fam-prev');
  if (prev) prev.style.background = hex;
  if (hx){ const p2 = document.getElementById('fam-pick'); if (p2) p2.value = hex; }
  if (pk){ const h2 = document.getElementById('fam-hex'); if (h2) h2.value = hex; }
});

host.addEventListener('click', e => {
  const fb = e.target.closest('button[data-fam]');
  if (fb){ openFamily(fb.dataset.famsec, fb.dataset.fam); return; }
  const ab = e.target.closest('button[data-anysec]');
  if (ab){ openAnyColour(ab.dataset.anysec); return; }
  const bb = e.target.closest('button[data-black]');
  if (bb){
    apply(bb.dataset.black, '#1B1A18', 'Ink Black', true);
    return;
  }
  const pk = e.target.closest('button[data-peek]');
  if (pk){
    const body = host.querySelector(`.sec[data-sec="${pk.dataset.peek}"] .secbody`);
    if (!body) return;
    const on = body.dataset.collapsed === '1';
    body.dataset.collapsed = on ? '0' : '1';
    pk.textContent = on ? 'Hide colours' : 'Show colours';
    return;
  }
  if (e.target.closest('[data-back]')){
    closeSwatchPopup();
    if (isMobile())
      openDrawer(lastTab === 'order' ? 'colour' : lastTab, PANEL_TITLES[lastTab], lastTab);
    return;
  }
  const labTap = e.target.closest('em[data-lab]');
  if (labTap && isTouch()){
    const already = labTap.dataset.bloomed === '1';
    host.querySelectorAll('em[data-lab]').forEach(x => x.dataset.bloomed = '0');
    bloomFamily(labTap.dataset.lab, !already);
    labTap.dataset.bloomed = already ? '0' : '1';
    return;
  }
  const fold = e.target.closest('[data-fold]');
  if (fold){
    const id = fold.dataset.fold;
    const sec = host.querySelector(`.sec[data-sec="${id}"]`);
    const wasOpen = sec.dataset.folded !== '1';
    host.querySelectorAll('.sec').forEach(el => {
      const on = !wasOpen && el.dataset.sec === id;
      el.dataset.folded = on ? '0' : '1';
      const btn = el.querySelector('[data-fold]');
      if (btn){
        btn.setAttribute('aria-expanded', String(on));
        btn.querySelector('.chev').textContent = on ? '\u2212' : '+';
      }
    });
    return;
  }
  if (e.target.closest('[data-done]')){ closeSwatchPopup(); return; }
  const t = e.target.closest('[data-toggle]');
  if (t){
    const el = host.querySelector(`.sec[data-sec="${t.dataset.toggle}"]`);
    const wasOpen = el.dataset.open === '1';
    if (wasOpen){
      el.dataset.open = '0';                       // collapsing: leave the view alone
    } else {
      openSection(t.dataset.toggle);
    }
    return;
  }
  const src = e.target.closest('[data-src]');
  if (src){
    const id = src.dataset.src;
    srcMode[id] = src.dataset.mode;
    host.querySelectorAll(`[data-src="${id}"]`).forEach(b =>
      b.setAttribute('aria-pressed', String(b.dataset.mode === srcMode[id])));
  
    return;
  }
  const sw = e.target.closest('.sw');
  if (sw && isTouch() && sw.dataset.armed !== '1'){
    // first tap names the shade; a second tap on the same tile applies it
    famBody.querySelectorAll('.sw[data-armed="1"]').forEach(x => x.dataset.armed = '0');
    sw.dataset.armed = '1';
    showSwTip(sw);
    return;
  }
  if (sw){
    const label = sw.dataset.label.replace(/^Nearest: /, '');
    apply(sw.dataset.sec, sw.dataset.color, label, true);
    if (isMobile()){
      const sec = SECTIONS.find(x => x.id === sw.dataset.sec);
      showBagPeek(sw.dataset.sec, (sec ? sec.name + ' — ' : '') + label, '← Back to colours');
    }
  }
});
let pickPeekTimer = null;
host.addEventListener('input', e => {
  const hx = e.target.closest('[data-hex]');
  if (hx){
    const v = hx.value.trim().replace('#','');
    if (/^[0-9a-fA-F]{6}$/.test(v)){
      const hex = '#' + v.toUpperCase();
      apply(hx.dataset.hex, hex, nameFor(hex), true);
    }
    return;
  }
  const x = e.target.closest('[data-exact]');
  if (x){
    const hex = x.value.toUpperCase();
    const label = nameFor(hex);
    apply(x.dataset.exact, hex, label, true);
    if (isMobile()){
      clearTimeout(pickPeekTimer);
      pickPeekTimer = setTimeout(() => {
        const sec = SECTIONS.find(y => y.id === x.dataset.exact);
        showBagPeek(x.dataset.exact, (sec ? sec.name + ' — ' : '') + label, '← Back to colours');
      }, 700);
    }
    return;
  }
  const f = e.target.closest('[data-find]');
  if (!f) return;
  srcFind[f.dataset.find] = f.value;
  
});
/* Copy that survives an embedded page. navigator.clipboard is refused inside
   an iframe without clipboard permission and on non-HTTPS pages, so fall back
   to a selected hidden field, and finally show the text to copy by hand. */
async function copyText(text){
  try {
    if (navigator.clipboard && window.isSecureContext){
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.appendChild(ta);
    ta.select(); ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    ta.remove();
    if (ok) return true;
  } catch (err) {}
  try { window.prompt('Copy this:', text); } catch (err) {}
  return false;
}
$('#copy').addEventListener('click', async (e) => {
  const text = PARTS.map(s => {
    const hex = (s.fixed ? s.def : chosen[s.id][1]).toUpperCase();
    const label = partLabel(s);
    return s.name + ': ' + label + ' ' + hex;
  }).join('\n');
  e.target.textContent = (await copyText(text)) ? 'Copied to clipboard' : 'Shown for copying';
  setTimeout(() => { e.target.textContent = 'Copy spec with hex codes'; }, 2000);
});
$('#reset').addEventListener('click', () => {
  SECTIONS.forEach(s => { const c = pick(s); apply(s.id, c[1], c[0]); delete touched[s.id]; });
  validate();
});
/* ---------- every section must be completed ---------- */
const REQUIRED = [
  { key:'colour-front',  label:'Front panel colour',  tab:'colour', test:()=>!!touched.front },
  { key:'colour-back',   label:'Back panel colour',   tab:'colour', test:()=>!!touched.back },
  { key:'colour-pocket', label:'Inner pocket colour', tab:'colour', test:()=>!!touched.pocket },
  { key:'confirmed',     label:'Configuration approved', tab:'order', test:()=>$('#confirmed').checked },

];
const checklist = $('#checklist');
const cartBtn = $('#addcart');
/* a quiet line on the colour page naming the panels still waiting for a colour */
function renderPending(){
  const tab = ROOT.querySelector('#tab-colour');
  if (!tab) return;
  let note = ROOT.querySelector('#colourpending');
  if (!note){
    note = document.createElement('p');
    note.id = 'colourpending';
    note.className = 'colourpending';
    tab.insertBefore(note, tab.firstChild);
  }
  const left = SECTIONS.filter(x => !touched[x.id]).map(x => x.name);
  let bagNote = ROOT.querySelector('#bagpending');
  const wrap = ROOT.querySelector('.stage-wrap');
  if (!bagNote && wrap){
    bagNote = document.createElement('p');
    bagNote.id = 'bagpending';
    bagNote.className = 'colourpending bagpending';
    wrap.appendChild(bagNote);
  }
  if (bagNote){
    bagNote.innerHTML = left.length
      ? '<b>Still to choose</b> ' + left.join(' \u00B7 ')
      : '<b>All panels chosen</b>';
    if (left.length) delete bagNote.dataset.done; else bagNote.dataset.done = '1';
  }
  if (!left.length){
    note.innerHTML = '<b>All panels chosen</b>';
    note.dataset.done = '1';
  } else {
    note.innerHTML = '<b>Still to choose</b> ' + left.join(' \u00B7 ');
    delete note.dataset.done;
  }
}
function validate(){
  try { renderPending(); } catch (err) {}
  const state = REQUIRED.map(r => ({ ...r, ok: r.test() }));
  const done = state.filter(r => r.ok).length;
  checklist.innerHTML = done === state.length
    ? `<div class="done"><b class="mark">✓</b><span>All ${state.length} colours chosen</span></div>`
    : state.map(r =>
        `<div class="${r.ok ? 'done' : ''}"><b class="mark">${r.ok ? '✓' : '○'}</b><span>${r.label}</span></div>`
      ).join('');
  renderPrice();
  const left = state.filter(r => !r.ok);
  cartBtn.disabled = left.length > 0;
  if (swatchOrdered){
    cartBtn.textContent = 'Add swatch set to cart';
    cartBtn.disabled = false;      // a swatch order needs no colour confirmation
    return left;                   // but the bag's own gate is unchanged
  }
  cartBtn.textContent = left.length
    ? `${left.length} section${left.length>1?'s':''} still to complete`
    : 'Lock design & send to the workshop';
  return left;
}
textHost.addEventListener('input', validate);
$('#confirmed').addEventListener('change', validate);
$('#textok').addEventListener('change', validate);

cartBtn.addEventListener('click', async () => {
  if (swatchOrdered){ orderSwatches(cartBtn); return; }
  const left = validate();
  if (left.length){ showTab(left[0].tab === 'match' ? 'colour' : left[0].tab); return; }
  /* Shopify prints a line item property on the cart, at checkout and in the
     confirmation email — unless its name begins with an underscore, in which
     case it is kept on the order and in the Admin API but never shown. So the
     customer reads colour names, and the workshop and Contrado get the codes
     and the setting-out alongside them. */
  const props = {};
  SECTIONS.forEach(s => {
    props[s.name + ' colour'] = chosen[s.id][0];
    props['_' + s.name + ' hex'] = chosen[s.id][1].toUpperCase();
  });
  // every part, chosen or fixed, so nothing has to be looked up by hand
  props['_All hex codes'] = PARTS.map(p => {
    const hex = p.fixed ? p.def : chosen[p.id][1];
    return p.name + '=' + String(hex).toUpperCase();
  }).join('; ');
  TEXT_TARGETS.forEach(T => {
    const c = textTools[T.id].cfg;
    const msg = (c.msg || '').trim();
    if (!msg) return;                                 // text is optional
    props[T.label + ' text'] = msg;
    props['_' + T.label + ' setting'] =
      'printed, ' + FONTS[c.font][0] + ', ' +
      (c.ink ? c.ink.toUpperCase() + ' ink, ' : 'auto ink, ') + (c.align || 'center') + ' aligned, ' +
      Math.round(c.size*18) + 'pt, ' + (c.x*100).toFixed(1) + 'cm across, ' +
      (c.y*100).toFixed(1) + 'cm up';
  });
  const extras = priceLines().slice(1);
  if (extras.length){
    // the customer sees what they are paying extra for; the breakdown is ours
    props['Customisation'] = extras.map(l => l[0]).join('; ');
    props['_Customisation charges'] = extras.map(l => l[0] + ' ' + money(l[1])).join('; ')
      + ' — total ' + money(priceLines().reduce((a,l)=>a+l[1],0));
  }
  props['_Print artwork'] = ['front','back','pocket']
    .filter(id => (textTools[id] && (textTools[id].cfg.text || '').trim()) || touched[id])
    .map(id => PRINT_SHEET[id].label + ' ' + PRINT_SHEET[id].w + '×' + PRINT_SHEET[id].h
      + ' @200dpi on ' + (chosen[id] ? chosen[id][1].toUpperCase() : ''))
    .join('; ');
  const rows = Object.entries(props).filter(([k]) => !k.startsWith('_'));
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* Which product is the bag? The section setting if it exists; otherwise the
     product page this sits on; otherwise a product found by name. */
  async function bagVariant(){
    const host = ROOT;
    if (host.dataset.variantId) return host.dataset.variantId;
    try {
      const m = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
      if (m && m.variants && m.variants.length) return String(m.variants[0].id);
    } catch (err) {}
    const fromHandle = async (h) => {
      try {
        const r = await fetch(root + 'products/' + h + '.js', { headers:{ 'Accept':'application/json' } });
        if (!r.ok) return null;
        const pr = await r.json();
        const v = (pr.variants || []).find(x => x.available) || (pr.variants || [])[0];
        return v ? String(v.id) : null;
      } catch (err) { return null; }
    };
    for (const h of [host.dataset.bagHandle || '', 'personalised-tote-bag-100-nappa-leather', 'tote', 'the-tote', 'niadira-tote', 'leather-tote', 'grain-leather-tote']){
      if (h){ const v = await fromHandle(h); if (v) return v; }
    }
    try {
      const r = await fetch(root + 'search/suggest.json?q=tote&resources[type]=product&resources[limit]=5',
        { headers:{ 'Accept':'application/json' } });
      if (r.ok){
        const j = await r.json();
        const list = (((j.resources || {}).results || {}).products) || [];
        const hit = list.find(p => /tote|bag/i.test(p.title || '') && !/swatch/i.test(p.title || '')) || null;
        if (hit && hit.handle) return await fromHandle(hit.handle);
      }
    } catch (err) {}
    return null;
  }

  async function sendToCheckout(btn){
    if (!(btn instanceof HTMLElement)) btn = document.getElementById('sheet-go');
    btn.disabled = true; btn.textContent = 'Adding\u2026';
    const id = await bagVariant();
    if (!id){
      btn.disabled = false; btn.textContent = 'Bag product not found';
      console.warn('[Niadira] Could not find the bag product. Give it a web address ending /products/tote, or add the section to its product page.');
      return;
    }
    try {
      const res = await fetch(root + 'cart/add.js', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: props }] }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.description || body.message || res.statusText);
      window.location.href = root + 'checkout';
    } catch (err){
      btn.disabled = false;
      btn.textContent = 'Could not add \u2014 ' + String(err.message || 'try again').slice(0, 60);
    }
  }

  // the review comes first, then one clear way to pay
  openSheet({
    title: 'Your commission',
    intro: 'This is the specification our workshop will cut from. Check it over, then continue to checkout.',
    rows,
    totalLabel: 'Total',
    total: priceLines().reduce((a,l) => a + l[1], 0),
    action: { label: 'Continue to checkout', run: sendToCheckout },
  });
  clearPhoto();
  try { await navigator.clipboard.writeText(rows.map(([k,v]) => k + ': ' + v).join('\n')); } catch {}
});

/* ---------- colour match ---------- */
const matchHost = $('#tab-match');
matchHost.innerHTML = `
  <div class="mgroup">
    <div class="photohead"><b>Match My Photo<sup>™</sup></b>
      <button class="reset" id="match-back">← Back to the bag</button></div>
    <div class="drop" id="mdrop">
      <span class="dropmark" aria-hidden="true"></span>
      <b>Choose a photograph</b>
      <em class="privline">Stays on your device</em>
      <input type="file" id="mfile" hidden
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp,.jpg,.jpeg,.png,.heic,.heif,.webp">
    </div>
    <div class="sheet" id="photo-modal" hidden>
      <div class="sheet-box">
        <div class="photohead"><b>Your photo</b>
          <span class="photoacts">
            <button class="reset" id="photo-new">New photo</button>
            <button class="reset" id="photo-close">Done</button>
          </span></div>
    <div class="haloshell" id="mhalo">
      <div class="halospokes" id="mhalospokes" aria-hidden="true"></div>
    <div class="shot" id="mshot">
      <canvas id="mcanvas"></canvas>
      <div class="spotmark" id="mspot" aria-hidden="true"></div>
      <div class="halocross" id="mhalocross" aria-hidden="true"></div>
      <div class="scanline" aria-hidden="true"></div>
      <div class="scanread"><span id="mread-out"></span><span class="dots" id="mread-dots"></span></div>

      <p class="warn castnote" id="mcast" hidden></p>

    </div>
      <div class="halobar">
        <button class="haloarrow" id="mhaloprev" aria-label="Previous shade">‹</button>
        <span class="haloname" id="mhaloname"></span>
        <button class="reset halowhole" id="mwhole" title="Read the whole photograph">Whole</button>
        <button class="haloarrow" id="mhalonext" aria-label="Next shade">›</button>
      </div>
      <div class="haloto" id="mhaloto"></div>
    </div>
    <div class="mrow" hidden>
      <input type="color" id="mpick" value="#E8A0B4">
      <input type="text" id="mhex" value="#E8A0B4" maxlength="7" spellcheck="false">
    </div>
    <p class="autonote" id="mautonote" hidden>
      <button class="reset" id="mautoundo" title="Undo">↺</button>
      <button class="reset" id="mautoredo" title="Back to the colours from your photograph" hidden>↻</button>
    </p>

    <div class="saverow" id="msaverow">
      <input type="text" id="msavename" maxlength="40" spellcheck="false"
        placeholder="Name this match — “my navy coat”">
      <button class="reset" id="msave">Save this match</button>
      <button class="reset" id="mshare">Share this match</button>
    </div>
    <div class="saved" id="msaved" hidden></div>
      </div>
    </div>
  </div>`;

const RECIPES = [
  ['Closest web colour', 'the named colour nearest to yours', c => c.hex],
  ['Deeper tone',        'the same hue, taken darker for the body',
    c => fromHSL(c.h, Math.min(1, c.s*1.05), Math.max(0.10, c.l*0.55))],
  ['Softer tint',        'a paler version, good for a large panel',
    c => fromHSL(c.h, c.s*0.55, Math.min(0.93, c.l + (1-c.l)*0.55))],
  ['Complement',         'opposite hue, for a pocket that reads as a lining',
    c => fromHSL(c.h + 0.5, c.s*0.72, Math.min(0.6, Math.max(0.22, c.l*0.8)))],
  ['Neighbouring hue',   'one step round the wheel',
    c => fromHSL(c.h + 0.075, c.s*0.85, c.l*0.9)],
  ['Neighbouring hue',   'one step the other way round the wheel',
    c => fromHSL(c.h - 0.075, c.s*0.85, c.l*0.9)],
  ['Grounding neutral',  'a quiet partner that lets your colour lead',
    c => fromHSL(c.h, 0.10, 0.26)],
];

/* one tile of the bag's own grain, reused as an overlay on every colour chip */
const GRAIN_TILE = (() => {
  try {
    const src = grain.bump.image;
    const cv = document.createElement('canvas');
    cv.width = cv.height = 128;
    const c = cv.getContext('2d');
    c.drawImage(src, 0, 0, 256, 256, 0, 0, 128, 128);
    return cv.toDataURL('image/png');
  } catch (err) { return null; }
})();
const grainStyle = hex => GRAIN_TILE
  ? `background-color:${hex};background-image:url(${GRAIN_TILE});` +
    `background-size:34px 34px;background-blend-mode:soft-light;`
  : `background:${hex};`;

/* The dial: the suggestions sit round a wheel, turned by drag, arrows, or the
   keyboard. Whichever shade reaches the top is the one on offer — named in the
   middle, with the three panels beneath it. */
/* The wheel needs its own window: inside the photo page it pushed the naming
   and sharing row far below the fold. It is appended to the configurator root
   rather than the photo window, because that window animates and so becomes a
   containing block for anything fixed inside it. */
let wheelModal = null;   // superseded by the Halo

function photoPageOpen(){
  // the bag reveal takes precedence: the wheel never sits over it
  const bar = ROOT.querySelector('#revealbar');
  if (bar && !bar.hidden) return false;
  return [ROOT.querySelector('#photo-modal'), ROOT.querySelector('#match-modal')].some(shown);
}
function openWheel(){ return radiate(); }
function closeWheel(){ /* the Halo belongs to the photograph; nothing to close */ }

/* For the crosshair to be honest it has to know where in the photograph each
   shade was read. The source colours carry no coordinates, so the picture is
   scanned once at low resolution and the closest pixel to each shade recorded. */
function locateShades(rows){
  try {
    if (!mcanvas || !mcanvas.width) return;
    const step = Math.max(1, Math.floor(Math.min(mcanvas.width, mcanvas.height) / 60));
    const d = mcanvas.getContext('2d', { willReadFrequently: true })
      .getImageData(0, 0, mcanvas.width, mcanvas.height).data;
    rows.forEach(r => {
      const h = String(r.hex || '').replace('#', '');
      if (h.length !== 6) return;
      const tr = parseInt(h.slice(0,2),16)/255,
            tg = parseInt(h.slice(2,4),16)/255,
            tb = parseInt(h.slice(4,6),16)/255;
      let bx = 0.5, by = 0.5, best = 1e9;
      for (let y = 0; y < mcanvas.height; y += step){
        for (let x = 0; x < mcanvas.width; x += step){
          const i = (y * mcanvas.width + x) * 4;
          if (d[i+3] < 8) continue;
          const dr = d[i]/255 - tr, dg = d[i+1]/255 - tg, db = d[i+2]/255 - tb;
          const q = dr*dr + dg*dg + db*db;
          if (q < best){ best = q; bx = x / mcanvas.width; by = y / mcanvas.height; }
        }
      }
      r.at = [bx, by];
    });
  } catch (err) {}
}

/* the photograph sits at the centre of the dial, scanned by the crosshair */
function paintLoupe(row){
  const cv = QSEL('#mloupeshot'), cross = QSEL('#mloupecross'), nm = QSEL('#mloupename');
  if (!cv || !mcanvas || !mcanvas.width) return;
  /* The whole photograph is letterboxed in, rather than cropped square: a
     crosshair is only honest if the point it marks is actually on screen. */
  const c = cv.getContext('2d');
  c.clearRect(0, 0, cv.width, cv.height);
  const sc = Math.min(cv.width / mcanvas.width, cv.height / mcanvas.height);
  const dw = mcanvas.width * sc, dh = mcanvas.height * sc;
  const dx = (cv.width - dw) / 2, dy = (cv.height - dh) / 2;
  c.fillStyle = '#1B1A18';
  c.fillRect(0, 0, cv.width, cv.height);
  c.drawImage(mcanvas, 0, 0, mcanvas.width, mcanvas.height, dx, dy, dw, dh);
  if (cross && row && row.at){
    const clamp = (v) => Math.min(Math.max(v, 0.055), 0.945) * 100 + '%';
    cross.style.left = clamp((dx + row.at[0] * dw) / cv.width);
    cross.style.top  = clamp((dy + row.at[1] * dh) / cv.height);
    cross.style.setProperty('--fc', row.hex);
    cross.dataset.on = '1';
  } else if (cross) cross.dataset.on = '0';
  if (nm) nm.textContent = row ? row.name : '';
}

function mountDial(){
  const dial = ROOT.querySelector('#mdial');
  const face = ROOT.querySelector('#mdialface');
  const to   = ROOT.querySelector('#mdialto');
  const rows = matchHost._rows || [];
  if (!dial || !rows.length) return;
  const step = 360 / rows.length;

  function show(i){
    const idx = ((i % rows.length) + rows.length) % rows.length;
    matchHost._dialAt = idx;
    dial.style.transform = `rotate(${-idx * step}deg)`;
    dial.querySelectorAll('.spoke').forEach((sp, k) =>
      sp.setAttribute('aria-current', String(k === idx)));
    const r = rows[idx];
    paintLoupe(r);
    // the bag wears whatever reaches the top, live
    const live = (typeof pickedPanel !== 'undefined' && pickedPanel) ? pickedPanel : 'front';
    if (mats[live]){
      mats[live].__base = undefined;
      apply(live, r.hex, r.name, true);
    }
    to.innerHTML = SECTIONS.map(s => `<button data-apply="${idx}" data-to="${s.id}"
      title="Apply to ${s.name}">${s.name.split(' ')[0]}</button>`).join('');
  }

  // the note is shown on request, and remembered once dismissed
  const help = ROOT.querySelector('#mdialhelp');
  const tip  = ROOT.querySelector('#mdialtip');
  const SEEN = 'niadira.dialtip.seen';
  let seen = false;
  try { seen = localStorage.getItem(SEEN) === '1'; } catch (err) {}
  if (!seen && tip) tip.hidden = false;
  if (help) help.addEventListener('click', () => { if (tip) tip.hidden = false; });
  const closeTip = () => {
    if (tip) tip.hidden = true;
    try { localStorage.setItem(SEEN, '1'); } catch (err) {}
  };
  const tipClose = ROOT.querySelector('#mdialtipclose');
  if (tipClose) tipClose.addEventListener('click', closeTip);

  ROOT.querySelector('#mdialprev')
    .addEventListener('click', () => show(matchHost._dialAt - 1));
  ROOT.querySelector('#mdialnext')
    .addEventListener('click', () => show(matchHost._dialAt + 1));
  dial.addEventListener('click', e => {
    const sp = e.target.closest('[data-spoke]');
    if (sp && !dial.dataset.dragging) show(+sp.dataset.spoke);
  });

  // turning it by hand: the angle from the centre, measured as the pointer moves
  let startAngle = null, startIdx = 0, moved = 0;
  const angleOf = (ev) => {
    const b = dial.getBoundingClientRect();
    return Math.atan2(ev.clientY - (b.top + b.height / 2),
                      ev.clientX - (b.left + b.width / 2)) * 180 / Math.PI;
  };
  dial.addEventListener('pointerdown', e => {
    startAngle = angleOf(e); startIdx = matchHost._dialAt; moved = 0;
    dial.setPointerCapture(e.pointerId);
  });
  dial.addEventListener('pointermove', e => {
    if (startAngle === null) return;
    let d = angleOf(e) - startAngle;
    if (d > 180) d -= 360; if (d < -180) d += 360;
    moved = Math.abs(d);
    if (moved > 6) dial.dataset.dragging = '1';
    show(startIdx + Math.round(d / step));
  });
  const release = () => {
    startAngle = null;
    setTimeout(() => { delete dial.dataset.dragging; }, 0);
  };
  dial.addEventListener('pointerup', release);
  dial.addEventListener('pointercancel', release);
  dial.addEventListener('keydown', e => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp'){ show(matchHost._dialAt - 1); e.preventDefault(); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown'){ show(matchHost._dialAt + 1); e.preventDefault(); }
  });

  show(0);
}

function renderMatch(hex){
  const base = hsl(hex);
  const near = nearestWeb(hex, 1)[0];
  const rows = RECIPES.map(([title, why, fn]) => {
    const raw = fn({ ...base, hex: near.hex });
    return { title, why, hex: raw.toUpperCase(), name: nameFor(raw) };
  });
  const warmth = hex => Math.cos(2*Math.PI*(hsl(hex).h - 0.08));   // 1 at the warm pole
  const nb = rows.filter(r => r.title === 'Neighbouring hue');
  if (nb.length === 2){
    const [warmer, cooler] = warmth(nb[0].hex) >= warmth(nb[1].hex) ? nb : [nb[1], nb[0]];
    warmer.title = 'Warmer neighbour'; warmer.why = 'one step round the wheel, towards red';
    cooler.title = 'Cooler neighbour'; cooler.why = 'one step round the wheel, towards blue';
  }
  /* The Niadira Halo: the photograph is the dial. Its shades orbit the image
     they came from, so there is nothing to look at twice. */
  const step = 360 / rows.length;
  const spokes = ROOT.querySelector('#mhalospokes');
  if (spokes){
    spokes.style.setProperty('--n', rows.length);
    spokes.innerHTML = rows.map((r, i) => `
      <button class="halospoke" data-spoke="${i}" style="--a:${i * step}deg;--sin:${Math.sin(i * step * Math.PI / 180).toFixed(4)};--cos:${Math.cos(i * step * Math.PI / 180).toFixed(4)};--d:${i * 55}ms;${grainStyle(r.hex)}"
        aria-label="${r.name}" title="${r.name}"></button>`).join('');
    spokes.removeAttribute('aria-hidden');
  }
  locateShades(rows);
  matchHost._rows = rows;
  matchHost._dialAt = 0;
  mountHalo();
  radiate();
}

/* the shades sweep outward from the centre, one after another */
function sizeHalo(){
  const shell = ROOT.querySelector('#mhalo');
  const cv = ROOT.querySelector('#mcanvas');
  const card = ROOT.querySelector('#photo-modal .sheet-box') || shell;
  if (!shell || !cv) return 0;
  const cr = cv.getBoundingClientRect();
  const sr = shell.getBoundingClientRect();
  if (cr.width < 8 || cr.height < 8) return 0;      // not laid out yet

  /* The ring is placed against the photograph, but changing the shell's
     padding moves the photograph — so the box was measured against a layout
     that no longer existed and the ring drifted off towards a corner. Place,
     pad, then place again against the settled layout. */
  const SPOKE = 17, EDGE = 6, GAP = SPOKE + 7;
  const spokes = ROOT.querySelector('#mhalospokes');

  function layOut(){
    // re-read the shades each time: an early call would otherwise place none
    const list = spokes ? [...spokes.querySelectorAll('.halospoke')] : [];
    if (!list.length) return 0;
    const c = cv.getBoundingClientRect();
    const s2 = shell.getBoundingClientRect();
    if (c.width < 8 || !spokes) return 0;
    // the ring's box is the photograph's box, measured now
    spokes.style.inset = 'auto';
    spokes.style.left = Math.round(c.left - s2.left) + 'px';
    spokes.style.top = Math.round(c.top - s2.top) + 'px';
    spokes.style.width = Math.round(c.width) + 'px';
    spokes.style.height = Math.round(c.height) + 'px';

    /* One radius for every shade. Scaling individual shades inward to fit the
       card is what dropped some of them onto the picture; if there is not room
       the whole ring closes in together, so they stay equidistant. */
    const R = Math.min(c.width, c.height) / 2;
    const room = (card.getBoundingClientRect().width || c.width) / 2 - SPOKE - EDGE;
    const radius = Math.max(R + 8, Math.min(R + GAP, room));
    let far = R;
    for (const b of list){
      const sn = parseFloat(b.style.getPropertyValue('--sin')) || 0;
      const cs = parseFloat(b.style.getPropertyValue('--cos')) || 0;
      b.style.setProperty('--rx', Math.round(radius * sn) + 'px');
      b.style.setProperty('--ry', Math.round(radius * cs) + 'px');
      far = Math.max(far, Math.abs(radius * cs));
    }
    return { far, R };
  }

  let m = layOut();
  if (!m) return 0;
  const pad = Math.max(0, Math.round(m.far - m.R) + SPOKE + 10);
  const mob = window.matchMedia('(max-width: 900px)').matches;
  const below = Math.max(0, Math.round(m.far - m.R) + Math.round(SPOKE / 2) + 8);
  const above = mob ? Math.max(6, below - 26) : pad;
  shell.style.paddingTop = above + 'px';
  shell.style.paddingBottom = '0px';
  const bar = shell.querySelector('.halobar');
  if (bar) bar.style.marginTop = (mob ? below : Math.max(0, pad - 6)) + 'px';
  // the padding has moved the photograph, so place the ring again against it
  requestAnimationFrame(() => { layOut(); });
  const maxY = m.far;
  return Math.round(maxY) || 1;
}
let radiateWatch = null;
function radiate(){
  const sh = ROOT.querySelector('#mhalo') || document.querySelector('#mhalo');
  if (sh){ sh.classList.remove('radiating'); void sh.offsetWidth; }
  const shell = ROOT.querySelector('#mhalo');
  if (!shell) return;
  if (!sizeHalo()){
    /* the card may not be measurable yet. Watch until it is, rather than
       trusting a chain of timers that dies if one link misses. */
    if (!radiateWatch){
      let tries = 0;
      radiateWatch = setInterval(() => {
        const cv2 = ROOT.querySelector('#mcanvas');
        const ready = cv2 && cv2.getBoundingClientRect().width > 8 &&
          ROOT.querySelectorAll('#mhalospokes .halospoke').length > 0;
        if (ready || ++tries > 60){
          clearInterval(radiateWatch); radiateWatch = null;
          if (ready) radiate();
        }
      }, 120);
    }
    return;
  }
  if (radiateWatch){ clearInterval(radiateWatch); radiateWatch = null; }
  shell.dataset.open = '1';
  shell.classList.remove('radiating');
  void shell.offsetWidth;                       // restart the motion
  shell.classList.add('radiating');
}
function bindHaloPick(){
  const cv = ROOT.querySelector('#mcanvas');
  if (!cv || cv.__halo) return;
  cv.__halo = true;
  cv.addEventListener('click', (e) => {
    const r = cv.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
    if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return;
    const c = cv.getContext('2d', { willReadFrequently: true });
    const px = c.getImageData(
      Math.round(fx * cv.width), Math.round(fy * cv.height), 1, 1).data;
    const hex = '#' + [px[0], px[1], px[2]]
      .map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();
    // the cross lands where they touched, and the halo re-forms around it
    const cross = ROOT.querySelector('#mhalocross'), shot = ROOT.querySelector('#mshot');
    if (cross && shot){
      const sr = shot.getBoundingClientRect();
      cross.hidden = false;
      cross.style.left = Math.round(r.left - sr.left + fx * r.width) + 'px';
      cross.style.top  = Math.round(r.top - sr.top + fy * r.height) + 'px';
      cross.style.setProperty('--fc', hex);
      cross.classList.remove('landing'); void cross.offsetWidth;
      cross.classList.add('landing');
    }
    setBase(hex);
    renderMatch(hex);
  });
}

function reframeForPhoto(){
  if (typeof lookAtPanel === 'function' && typeof lastLooked !== 'undefined')
    lookAtPanel(lastLooked);
}
function refreshHalo(){
  reframeForPhoto();
  const shell = ROOT.querySelector('#mhalo');
  if (!shell || !matchHost._rows || !matchHost._rows.length) return;
  if (!sizeHalo()) return;
  if (typeof matchHost._haloShow === 'function') matchHost._haloShow(matchHost._dialAt || 0);
  shell.dataset.open = '1';
  shell.classList.add('radiating');
}
window.addEventListener('resize', () => setTimeout(refreshHalo, 80));
/* the card is laid out after the photograph lands and again when its rows
   settle, so the ring is re-placed against the real box each time */
(function watchHaloBox(){
  const cv = ROOT.querySelector('#mcanvas');
  if (!cv || !window.ResizeObserver) return;
  let t = null;
  new ResizeObserver(() => {
    clearTimeout(t);
    t = setTimeout(() => {
      const shell = ROOT.querySelector('#mhalo');
      if (!shell) return;
      if (shell.classList.contains('radiating')) sizeHalo();
      else if (ROOT.querySelectorAll('#mhalospokes .halospoke').length) radiate();
    }, 90);
  }).observe(cv);
})();

function mountHalo(){
  bindHaloPick();
  const shell = ROOT.querySelector('#mhalo');
  const spokes = ROOT.querySelector('#mhalospokes');
  const nameEl = ROOT.querySelector('#mhaloname');
  const to = ROOT.querySelector('#mhaloto');
  const rows = matchHost._rows || [];
  if (!shell || !spokes || !rows.length) return;

  if (typeof matchHost._turns !== 'number') matchHost._turns = 0;
  const show = (i, spin) => {
    const k = ((i % rows.length) + rows.length) % rows.length;
    // follow the direction asked for, rather than snapping back at the wrap
    if (spin === 1 || spin === -1) matchHost._turns += spin;
    else matchHost._turns = k;
    matchHost._dialAt = k;
    const r = rows[k];
    spokes.style.setProperty('--turn', (-matchHost._turns * (360 / rows.length)) + 'deg');
    spokes.querySelectorAll('.halospoke').forEach((b, j) =>
      b.setAttribute('aria-current', String(j === k)));
    if (nameEl) nameEl.textContent = r.name;
    // the dropper moves to the point on the photograph this colour came from
    moveHaloCross(r);
    if (to) to.innerHTML = PARTS.filter(p => !p.fixed).map(p =>
      `<button data-apply="${k}" data-to="${p.id}">${p.name.split(' ')[0]}</button>`).join('');
  };
  matchHost._haloShow = show;

  const prev = ROOT.querySelector('#mhaloprev'), next = ROOT.querySelector('#mhalonext');
  if (prev) prev.onclick = () => show(matchHost._dialAt - 1, -1);
  if (next) next.onclick = () => show(matchHost._dialAt + 1,  1);
  spokes.onclick = (e) => {
    const b = e.target.closest('[data-spoke]');
    if (b) show(+b.dataset.spoke);
  };
  // resting on the photograph brings the halo out
  shell.onpointerenter = () => radiate();

  show(0);
}
/* the dropper: where on the photograph this shade was read */
function moveHaloCross(row){
  const cross = ROOT.querySelector('#mhalocross');
  const shot = ROOT.querySelector('#mshot');
  if (!cross || !shot || !row) return;
  const at = row.at;                            // [x, y], set by locateShades
  if (!at){ cross.hidden = true; return; }
  const cv = ROOT.querySelector('#mcanvas');
  if (!cv) return;
  // the canvas sits inside the shot, so the point is placed against the canvas
  const cr = cv.getBoundingClientRect(), sr = shot.getBoundingClientRect();
  if (cr.width < 8 || cr.height < 8) return;    // hidden: measure later, not now
  cross.hidden = false;
  cross.style.left = Math.round(cr.left - sr.left + at[0] * cr.width) + 'px';
  cross.style.top  = Math.round(cr.top - sr.top + at[1] * cr.height) + 'px';
  cross.style.setProperty('--fc', row.hex);
  cross.classList.remove('landing');
  void cross.offsetWidth;
  cross.classList.add('landing');
}


/* A photograph carries the light it was taken in. A warm indoor bulb pushes
   everything yellow, open shade pushes it blue, and either will send a match
   astray — so we say so rather than let the customer find out in the post. */
/* A poor light reading is worth stopping for: the colour will be wrong, and the
   customer would rather know before the bag is dyed than after. Guidance, and a
   way to try again, rather than a line of small print they will scroll past. */
const CAST_GUIDE = {
  "warm": "Move to a window in daylight and turn the room lights off.",
  "cool": "Step into brighter, more direct daylight — out of the shade.",
  "green": "Move away from strip lighting or greenery and shoot in daylight.",
  "dark": "Take it again somewhere brighter, near a window.",
  "bright": "Move out of direct sun into even, indirect daylight."
};
let castWin = null;
function showCastWarning(cast){
  if (!cast) return;
  if (!castWin){
    castWin = document.createElement('div');
    castWin.className = 'sheet';
    castWin.id = 'cast-modal';
    castWin.hidden = true;
    castWin.innerHTML =
      '<div class="sheet-box">' +
        '<h2>The light in this photograph</h2>' +
        '<p id="cast-note"></p>' +
        '<p id="cast-guide" class="castguide"></p>' +
        '<div class="sheet-acts">' +
          '<button class="cart" id="cast-retake">Use a different photograph</button>' +
          '<button class="reset" id="cast-anyway">Read it anyway</button>' +
        '</div>' +
      '</div>';
    ROOT.appendChild(castWin);
    castWin.addEventListener('click', (e) => {
      if (e.target.id === 'cast-anyway' || e.target === castWin){
        castWin.hidden = true;
        castHeld = false;                      // read it, as asked
        setTimeout(readWhole, 120);
        return;
      }
      if (e.target.id === 'cast-retake'){
        castWin.hidden = true;
        castHeld = false;
        if (typeof clearPhoto === 'function') clearPhoto();
        showMatch();
        const fi = ROOT.querySelector('#mfile'); if (fi) fi.click();
      }
    });
  }
  const note = castWin.querySelector('#cast-note');
  const guide = castWin.querySelector('#cast-guide');
  if (note) note.textContent = cast.note;
  if (guide) guide.textContent = CAST_GUIDE[cast.kind] || '';
  castWin.hidden = false;
}

let castHeld = false;
function readWhole(){
  if (castHeld) return;                        // waiting on the light warning
  if (!awaitingSpot || !lastPalette.length) return;   // or a spot already chosen
  awaitingSpot = false;
  if (mshot) mshot.dataset.stage = 'read';
  runScan(lastPalette, () => {
    setBase(lastPalette[0]);
    autoSkin(lastPalette, lastPalette[0]);
  }, lastPalette[0]);
}

function lightCast(img){
  const w = 60, hh = Math.max(1, Math.round(w * img.height / img.width));
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = hh;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, hh);
  const d = ctx.getImageData(0, 0, w, hh).data;
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < d.length; i += 4){
    if (d[i+3] < 128) continue;
    r += d[i]; g += d[i+1]; b += d[i+2]; n++;
  }
  if (!n) return null;
  r /= n; g /= n; b /= n;
  const mean = (r + g + b) / 3 || 1;
  const warm = (r - b) / mean;              // >0 yellow-red, <0 blue
  const green = (g - (r + b) / 2) / mean;
  if (warm > 0.16) return {
    kind: 'warm',
    note: 'This looks like indoor light — a bulb pushes colour towards yellow. ' +
          'Your item is likely a little cooler in daylight than it appears here.' };
  if (warm < -0.13) return {
    kind: 'cool',
    note: 'This looks like shade or overcast light, which pushes colour towards blue. ' +
          'Your item is likely a little warmer in direct daylight.' };
  if (green > 0.10) return {
    kind: 'green',
    note: 'There is a green cast here, often from strip lighting or foliage nearby. ' +
          'A daylight photograph would read truer.' };
  if (mean < 62) return {
    kind: 'dark',
    note: 'This photograph is quite dark, so the colour we read may be deeper than the ' +
          'real thing. A brighter shot near a window would read truer.' };
  if (mean > 218) return {
    kind: 'bright',
    note: 'This photograph is very bright, which washes colour out. A shot out of ' +
          'direct sun would read truer.' };
  return null;
}

/* what we can say about a single colour, in the customer's words */
function describeColour(hex){
  const c = hsl(hex);
  const tone = c.l < 0.22 ? 'very deep' : c.l < 0.42 ? 'deep'
    : c.l < 0.62 ? 'mid-toned' : c.l < 0.82 ? 'light' : 'very pale';
  const sat = c.s < 0.10 ? 'almost neutral' : c.s < 0.28 ? 'muted'
    : c.s < 0.55 ? 'soft' : 'strong';
  const warm = Math.cos(2 * Math.PI * (c.h - 0.08)) > 0 ? 'warm' : 'cool';
  return { tone, sat, warm };
}

/* pull the dominant colours out of a photo */
function paletteFrom(img){
  const w = 140, hh = Math.max(1, Math.round(w * img.height / img.width));
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = hh;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, hh);
  const d = ctx.getImageData(0, 0, w, hh).data;
  const bins = new Map();
  for (let i = 0; i < d.length; i += 4){
    if (d[i+3] < 128) continue;
    const key = ((d[i] >> 4) << 8) | ((d[i+1] >> 4) << 4) | (d[i+2] >> 4);
    const b = bins.get(key) || [0,0,0,0];
    b[0] += d[i]; b[1] += d[i+1]; b[2] += d[i+2]; b[3]++;
    bins.set(key, b);
  }
  const cand = [...bins.values()]
    .filter(b => b[3] > 3)
    .sort((a,b) => b[3] - a[3])
    .map(b => {
      const r = Math.round(b[0]/b[3]), g = Math.round(b[1]/b[3]), bl = Math.round(b[2]/b[3]);
      const hex = '#' + [r,g,bl].map(v => v.toString(16).padStart(2,'0')).join('').toUpperCase();
      return { hex, lab: hexToLab(hex), n: b[3] };
    });
  const out = [];
  for (const c of cand){
    if (out.length >= 7) break;
    if (out.every(o => dE(o.lab, c.lab) > 15)) out.push(c);
  }
  return out.map(c => c.hex);
}

const mdrop   = matchHost.querySelector('#mdrop');
const mfile   = matchHost.querySelector('#mfile');
const mshot   = matchHost.querySelector('#mshot');
const mcanvas = matchHost.querySelector('#mcanvas');
const mchips  = { innerHTML:'', addEventListener(){}, querySelectorAll(){ return []; } };

const MAX_PHOTO = 15 * 1024 * 1024;
function photoNote(msg){
  mdrop.querySelector('b').textContent = msg || 'Upload a photo';
}
const PHOTO_TYPES = /^image\/(jpeg|png|heic|heif|webp)$/i;
const PHOTO_EXT = /\.(jpe?g|png|heic|heif|webp)$/i;
function loadPhoto(file){
  if (!file) return;
  if (!PHOTO_TYPES.test(file.type || '') && !PHOTO_EXT.test(file.name || '')){
    photoNote('That file type will not work — use a JPEG, PNG, HEIC or WebP');
    return;
  }
  if (file.size > MAX_PHOTO){
    photoNote('That photo is over 15MB — try a smaller one');
    return;
  }
  if (!file.type.startsWith('image/')){
    photoNote('That file is not an image');
    return;
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onerror = () => {
    photoNote(/hei[cf]/i.test(file.type + file.name)
      ? 'This browser cannot read HEIC — save it as JPEG and try again'
      : 'That photo could not be opened');
    URL.revokeObjectURL(url);
  };
  img.onload = () => {
    const W = 460, H = Math.round(W * img.height / img.width);
    mcanvas.width = W; mcanvas.height = H;
    mcanvas.getContext('2d', { willReadFrequently: true }).drawImage(img, 0, 0, W, H);
    mshot.classList.add('on');
    photoFlow = true;
    photoModal.hidden = false;
    if (matchModal) matchModal.dataset.hasphoto = '1';   // the door steps aside
    mdrop.querySelector('b').textContent = 'Use a different photo';
    const pal = paletteFrom(img);
    mchips.innerHTML = pal.map(hex =>
      `<button data-chip="${hex}"><i style="background:${hex}"></i>${nameFor(hex)} ${hex}</button>`
    ).join('');

    lastPalette = pal;
    lastCast = lightCast(img);
    castHeld = !!lastCast;                    // nothing is read until they answer
    if (lastCast) setTimeout(() => showCastWarning(lastCast), 320);
    awaitingSpot = true;
  swatchOffered = false;
    if (mshot) mshot.dataset.stage = 'choose';
    const sp = ROOT.querySelector('#mspot'); if (sp) sp.dataset.on = '0';
    // the whole photograph is read straight away: the bag should transform the
    // moment the picture lands, not wait to be asked
    setTimeout(readWhole, 420);

    URL.revokeObjectURL(url);
  };
  img.src = url;
}
/* Reading the photograph. The colours are already known by this point — the
   sequence exists so the customer sees them being lifted, one at a time, rather
   than a result arriving from nowhere. */
let scanTimers = [], lastPalette = [], lastCast = null, awaitingSpot = false;
function stopScan(){
  scanTimers.forEach(clearTimeout);
  scanTimers = [];
  if (mshot) mshot.dataset.scanning = '0';
}
function runScan(pal, done, subject){
  stopScan();
  const read = mshot ? mshot.querySelector('#mread-out') : null;
  const dots = mshot ? mshot.querySelector('#mread-dots') : null;
  mshot.dataset.scanning = '1';
  mshot.dataset.read = '0';
  if (dots) dots.innerHTML = '';

  // say what is actually being measured, so the pause carries information
  const subj = subject || pal[0];
  const d = describeColour(subj);
  const near = nearestWeb(subj, 1)[0];
  const lines = [
    'Reading the colour',
    d.warm + ' · ' + d.tone,
    d.sat + ' · ' + near.name
  ];
  lines.forEach((t, i) => {
    scanTimers.push(setTimeout(() => { if (read) read.textContent = t; }, i * 620));
  });

  // each colour found lands on the strip as the light passes over it
  pal.slice(0, 6).forEach((hex, i) => {
    scanTimers.push(setTimeout(() => {
      if (dots) dots.insertAdjacentHTML('beforeend', `<i style="background:${hex}"></i>`);
    }, 300 + i * 230));
  });

  scanTimers.push(setTimeout(() => {
    mshot.dataset.scanning = '0';
    mshot.dataset.read = '1';
    if (read) read.textContent = 'Colours read from your photo';
    if (typeof done === 'function') done();
  }, 2000));
}

/* The instant the reading finishes, the bag wears the photograph. The strongest
   tone goes on the front and the next clearly different one on the back, so the
   customer sees their own colours on the bag before choosing anything. Every
   panel can still be set by hand, or returned to black, without losing the photo. */
/* three tones that read as distinct from one another, for the three panels */
function distinctTones(pal, lead){
  const out = [(lead || pal[0]).toUpperCase()];
  for (const h of pal){
    const hex = h.toUpperCase();
    if (out.length >= 3) break;
    if (out.every(o => dE(hexToLab(o), hexToLab(hex)) > 16)) out.push(hex);
  }
  while (out.length < 3) out.push(out[out.length - 1]);
  return out;
}

function autoSkin(pal, lead){
  if (!pal || !pal.length) return;
  preSkin = {
    front: chosen.front.slice(),
    back: chosen.back.slice(),
    pocket: chosen.pocket.slice(),
  };
  const [a, b, c] = distinctTones(pal, lead);
  revealSkin({ front:a, back:b, pocket:c });
}

/* The colours arrive on the bag one panel at a time while it turns, so the
   customer watches their photograph become the object rather than being handed
   a finished result. Timers rather than animation frames, because an embedded
   frame is given none. */
let revealTimers = [], revealBusy = false;
function settleSweeps(){
  // nothing may be left mid-sweep: a held boundary shows a colour that is no
  // longer the panel's own
  Object.keys(mats).forEach(k => {
    const m = mats[k];
    if (m && m.__sweep){ m.__sweep.edge.value = BAG_FOOT - 1; }
  });
  if (typeof drawNow === 'function') drawNow();
}
function stopReveal(){
  revealTimers.forEach(clearInterval);
  revealTimers.forEach(clearTimeout);
  revealTimers = [];
  settleSweeps();
}
/* The sweep lives in the material's own shader, so the grain, the sheen and the
   stitching all keep their behaviour while the colour crosses the panel. The
   boundary is measured in world height, which every vertex has — no dependence
   on the panel's UVs. */
const BAG_TOP = 0.62, BAG_FOOT = -0.03;
function armSweep(mat){
  if (mat.__sweep) return mat.__sweep;
  const u = {
    edge: { value: BAG_FOOT - 1 },              // at rest: nothing held back
    from: { value: new THREE.Color('#ffffff') },
    soft: { value: 0.075 },                     // the width of the wet edge
  };
  mat.__sweep = u;
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uSweepEdge = u.edge;
    sh.uniforms.uSweepFrom = u.from;
    sh.uniforms.uSweepSoft = u.soft;
    sh.vertexShader = 'varying float vSweepY;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\n  vSweepY = (modelMatrix * vec4(transformed, 1.0)).y;'
    );
    sh.fragmentShader =
      'uniform float uSweepEdge;\nuniform vec3 uSweepFrom;\nuniform float uSweepSoft;\n' +
      'varying float vSweepY;\n' + sh.fragmentShader.replace(
      '#include <color_fragment>',
      '#include <color_fragment>\n' +
      '  float sw = smoothstep(uSweepEdge + uSweepSoft, uSweepEdge - uSweepSoft, vSweepY);\n' +
      '  diffuseColor.rgb = mix(diffuseColor.rgb, uSweepFrom, sw);'
    );
  };
  mat.needsUpdate = true;
  return u;
}
function easeColour(id, toHex, ms, onDone){
  const mat = mats[id];
  if (!mat){ if (onDone) onDone(); return; }
  const u = armSweep(mat);
  // the new colour is already on the material; the old one is held above the
  // boundary and gives way as it descends
  u.from.value.copy(mat.color);
  mat.color.set(toHex);
  u.edge.value = BAG_TOP + u.soft.value;        // the panel starts wholly unpainted
  const span = Math.max(900, ms || 0);          // unhurried: the point is to watch it
  const t0 = performance.now();
  const tick = setInterval(() => {
    const k = Math.min(1, (performance.now() - t0) / span);
    // slow in, steady through the middle, settling at the foot
    const e = k < 0.5 ? 2*k*k : 1 - Math.pow(-2*k + 2, 2) / 2;
    u.edge.value = (BAG_TOP + u.soft.value) + ((BAG_FOOT - u.soft.value) - (BAG_TOP + u.soft.value)) * e;
    drawNow();
    if (k >= 1){
      clearInterval(tick);
      u.edge.value = BAG_FOOT - 1;              // and the panel is simply its colour
      drawNow();
      if (onDone) onDone();
    }
  }, 16);
  revealTimers.push(tick);
}

function revealSkin(picks){
  if (revealBusy) return;
  revealBusy = true;
  stopReveal();

  // clear the way: the bag needs the whole screen for this
  const wasPhoto = photoModal && !photoModal.hidden;
  if (photoModal) photoModal.hidden = true;
  if (matchModal) matchModal.hidden = true;
  closeDrawer();

  const bar = document.getElementById('revealbar');
  const list = document.getElementById('reveallist');
  if (list) list.innerHTML = '';
  if (bar){ bar.dataset.stage = 'running'; bar.hidden = false; }
  const say = ROOT.querySelector('.revealsay');
  if (say) say.textContent = 'Let\u2019s paint your bag with your memory\u2026';
  // the bag is the whole show while it is being revealed
  ['#wheel-modal', '#match-modal', '#photo-modal', '#fam-modal'].forEach(sel => {
    const el = ROOT.querySelector(sel); if (el) el.hidden = true;
  });
  if (typeof setStageLift === 'function') setStageLift(false);

  const order = [['front','Front panel'], ['back','Back panel'], ['pocket','Inner pocket']];
  const TURN = 900, NAME = 1800, PAINT = 3200, REST = 900;
  if (stage._controls) stage._controls.autoRotate = false;
  startDrawing();

  let at = 240;
  order.forEach(([id, label]) => {
    // the bag turns to the panel about to be coloured
    revealTimers.push(setTimeout(() => { lookAtPanel(id); }, at));
    at += TURN;
    // the colour is named first, so it can be read before it is seen
    revealTimers.push(setTimeout(() => {
      const hex = picks[id];
      if (list) list.insertAdjacentHTML('beforeend',
        `<span><i style="background:${hex}"></i>${label} · ${nameFor(hex)}</span>`);
    }, at));
    at += NAME;
    // and then drawn down the panel it belongs to
    revealTimers.push(setTimeout(() => {
      const hex = picks[id];
      const from = mats[id] ? mats[id].color.clone() : null;
      apply(id, hex, nameFor(hex), true);       // rows, price and checklist
      if (from) { mats[id].color.copy(from); easeColour(id, hex, PAINT); }
    }, at));
    at += PAINT + REST;
  });

  revealTimers.push(setTimeout(() => {
    // and then it turns again, so the bag is the thing to look at
    if (bar) bar.dataset.stage = 'done';
    if (say) say.textContent = 'Your photograph, on the bag.';
    revealBusy = false;
    autoSkinned = true;
    if (stage._controls) stage._controls.autoRotate = true;
    suggested = { front: chosen.front.slice(), back: chosen.back.slice(),
                  pocket: chosen.pocket.slice() };
    if (typeof setPhotoThumb === 'function') setPhotoThumb();
    const redo = ROOT.querySelector('#mautoredo'); if (redo) redo.hidden = false;
    renderSummary(); validate(); drawNow();
  }, at + 300));
}
let autoSkinned = false, preSkin = null, suggested = null;

/* ---------- saved matches ----------
   A match is worth keeping: it turns a single purchase into a wardrobe, and
   gives the customer a reason to come back. Only the colours are stored —
   the photograph itself never leaves the device. */
const MATCH_KEY = 'niadira.matches.v1';
function readMatches(){
  try { return JSON.parse(localStorage.getItem(MATCH_KEY) || '[]'); }
  catch (err) { return []; }
}
function writeMatches(list){
  try { localStorage.setItem(MATCH_KEY, JSON.stringify(list.slice(0, 12))); }
  catch (err) {}
}
function renderMatches(){
  const box = ROOT.querySelector('#msaved');
  if (!box) return;
  const list = readMatches();
  box.hidden = !list.length;
  if (!list.length) return;
  box.innerHTML = '<h4>Your saved matches</h4>' + list.map((m, i) => `
    <div class="savedrow">
      <i style="${grainStyle(m.hex)}"></i>
      <span><b>${m.label}</b><em>${m.date}</em></span>
      <button class="reset" data-usematch="${i}">Use</button>
      <button class="reset" data-delmatch="${i}">Remove</button>
    </div>`).join('');
}
function saveMatch(hex){
  const name = (ROOT.querySelector('#msavename')?.value || '').trim()
    || nameFor(hex) + ' match';
  const list = readMatches();
  list.unshift({
    hex: hex.toUpperCase(), label: name,
    date: new Date().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
  });
  writeMatches(list);
  renderMatches();
  const inp = ROOT.querySelector('#msavename'); if (inp) inp.value = '';
}

function clearPhoto(){
  if (typeof photoModal !== 'undefined' && photoModal) photoModal.hidden = true;
  if (typeof matchModal !== 'undefined' && matchModal) matchModal.removeAttribute('data-hasphoto');
  if (typeof closeWheel === 'function') closeWheel();
  suggested = null;
  const rd = ROOT.querySelector('#mautoredo'); if (rd) rd.hidden = true;
  const wb = ROOT.querySelector('#mwheelopen'); if (wb) wb.hidden = true;
  stopScan(); stopReveal();
  const rb = ROOT.querySelector('#revealbar'); if (rb) rb.hidden = true;
  revealBusy = false;
  if (mshot){
    mshot.dataset.read = '0';
    const d = mshot.querySelector('#mread-dots');
    if (d) d.innerHTML = '';
  }
  const sp0 = ROOT.querySelector('#mspot'); if (sp0) sp0.dataset.on = '0';
  const pb = ROOT.querySelector('#photoback');
  if (pb){
    pb.hidden = true;
    const th = pb.querySelector('.photothumb');
    if (th) th.remove();                       // it goes with the photograph
  }
  if (photoModal) photoModal.hidden = true;
  mshot.classList.remove('on');
  mchips.innerHTML = '';
  mfile.value = '';
  const ctx = mcanvas.getContext('2d');
  ctx.clearRect(0, 0, mcanvas.width, mcanvas.height);
  photoNote();
}
/* a look at the bag without losing your place in the photo */
const peek = document.getElementById('bagpeek');
const peekLabel = document.getElementById('peek-label');
let peekHidden = [], peekPanel = null, peekBack = 'Back to your photo';
let peekTrack = null, peekTimer = null, peekBound = false;
function placePeek(){
  if (peek.hidden) return;
  const cam = stage._camera, wrap = peek.parentElement;
  // no render may have happened (throttled iframe), so the camera's matrices
  // can be stale — refresh them before projecting the silhouette
  if (cam) cam.updateMatrixWorld(true);
  if (cam && wrap){
    const V = cam.position.constructor;
    const w = wrap.clientWidth || 1, h = wrap.clientHeight || 1;
    // sit beside the bag, level with the top of the handles, on whichever side
    // has room — measured from the bag's own projected silhouette
    const pts = [];
    for (const x of [-0.28, 0.28]) for (const y of [0, 0.60]) for (const z of [-0.09, 0.09]){
      pts.push(new V(x, y, z).project(cam));
    }
    const xs = pts.map(p => (p.x + 1) / 2 * w);
    const ys = pts.map(p => (1 - (p.y + 1) / 2) * h);
    const bagL = Math.min(...xs), bagR = Math.max(...xs);
    const bagT = Math.min(...ys), bagB = Math.max(...ys);
    const bw = peek.offsetWidth || 210, bh = peek.offsetHeight || 56;
    const room = 12;
    // never ride over the tab rail
    const safe = parseFloat(getComputedStyle(peek).getPropertyValue('--safe-b')) || 0;
    const rail = wrap.querySelector('.rail');
    const railBottom = rail && !rail.hidden
      ? rail.getBoundingClientRect().bottom - wrap.getBoundingClientRect().top : 0;
    const ceiling = Math.max(railBottom + 12, 92);
    let left = bagL - bw - room, top = Math.max(bagT, ceiling);
    if (left < room){
      left = bagR + room;                              // try the other side
      if (left + bw > w - room){
        left = room;                                   // keep it to the left
        if (bagT - bh - 12 >= ceiling){
          top = bagT - bh - 12;                        // above the handles
        } else {
          top = bagB + 14;                             // or clear below the base
        }
      }
    }
    top = Math.min(Math.max(top, ceiling), h - bh - 16 - safe);
    peek.style.right = 'auto';
    peek.style.marginInline = '0';
    peek.style.bottom = 'auto';
    peek.style.left = Math.round(left) + 'px';
    peek.style.top = Math.round(top) + 'px';
  }
}
function trackPeek(){
  if (peek.hidden){ peekTrack = null; return; }
  placePeek();
  peekTrack = requestAnimationFrame(trackPeek);
}
function bindPeekDrivers(){
  if (peekBound) return;
  peekBound = true;
  // OrbitControls fires on every camera change regardless of frame throttling
  if (stage._controls && stage._controls.addEventListener)
    stage._controls.addEventListener('change', placePeek);
  window.addEventListener('resize', placePeek);
  // and a timer, because rAF is zero inside an iframe (Shopify's theme editor)
  peekTimer = setInterval(placePeek, 300);
}
let resume = null;
function seeTheBag(sectionId){
  hideSwTip();
  // the full-screen window covers the bag, so step out of it to show the change
  // and record enough to come straight back
  if (sectionId) lookAtPanel(sectionId);
  resume = {
    tab: panelEl.dataset.tab || 'colour',
    scroll: panelEl.scrollTop,
    folded: [...host.querySelectorAll('.sec')].map(el => [el.dataset.sec, el.dataset.folded || '0']),
    fam: famModal.hidden ? null : { sec: famSec, name: famName },
  };
  famModal.hidden = true;
  closeDrawer();
}
function showBagPeek(sectionId){ seeTheBag(sectionId); }
function restoreResume(){
  if (!resume) return false;
  const r = resume; resume = null;
  panelEl.dataset.tab = r.tab;
  panelEl.scrollTop = r.scroll;             // set while still hidden
  openDrawer(r.tab, PANEL_TITLES[r.tab], r.tab, true);
  r.folded.forEach(([sec, v]) => {
    const el = host.querySelector(`.sec[data-sec="${sec}"]`);
    if (!el) return;
    el.dataset.folded = v;
    const b = el.querySelector('[data-fold]');
    if (b){
      b.setAttribute('aria-expanded', String(v !== '1'));
      const c = b.querySelector('.chev');
      if (c) c.textContent = v === '1' ? '+' : '\u2212';
    }
  });
  if (r.fam) openFamily(r.fam.sec, r.fam.name);
  panelEl.scrollTop = r.scroll;
  // one late correction only, in case fonts or images changed the height
  setTimeout(() => { if (panelEl.scrollTop !== r.scroll) panelEl.scrollTop = r.scroll; }, 90);
  return true;
}
function showBagPeekOld(sectionId, label, backLabel){
  lookAtPanel(sectionId);
  peekLabel.textContent = label;
  peekBack = backLabel || 'Back to your photo';
  document.getElementById('peek-close').textContent = peekBack;
  peekHidden = ['#photo-modal', '#match-modal']
    .map(sel => document.querySelector(sel))
    .filter(el => el && !el.hidden);
  peekHidden.forEach(el => el.hidden = true);
  // a full-screen tab window covers the bag, so step out of it too
  const p = document.querySelector('.panel');
  peekPanel = (p && p.dataset.drawer === 'open') ? p.dataset.tab || 'colour' : null;
  if (peekPanel) p.dataset.drawer = 'closed';
  peek.hidden = false;
  bindPeekDrivers();
  placePeek();
  if (!peekTrack) trackPeek();
}
document.getElementById('peek-close').addEventListener('click', () => {
  peek.hidden = true;
  if (peekTrack){ cancelAnimationFrame(peekTrack); peekTrack = null; }
  peek.style.top = ''; peek.style.bottom = '';
  peek.style.left = ''; peek.style.right = ''; peek.style.marginInline = '';
  peekHidden.forEach(el => el.hidden = false);
  peekHidden = [];
  if (peekPanel){
    openDrawer(peekPanel === 'order' ? 'colour' : peekPanel, PANEL_TITLES[peekPanel], peekPanel);
    peekPanel = null;
  }
});

/* leaving the photo page returns the bag to where it sits with nothing open:
   the lift that raised it above the card is released and the framing reset */
function backToBagHome(){
  photoFlow = false;
  if (typeof setStageLift === 'function') setStageLift(false);
  if (typeof closeWheel === 'function') closeWheel();
  setTimeout(() => { if (typeof lookAtPanel === 'function') lookAtPanel('front'); }, 90);
  if (stage._controls) stage._controls.autoRotate = true;
}
matchHost.querySelector('#match-back').addEventListener('click', () => {
  document.getElementById('match-modal').hidden = true;
  backToBagHome();
});
const photoModal = matchHost.querySelector('#photo-modal');
matchHost.querySelector('#photo-close').addEventListener('click', () => {
  photoModal.hidden = true;
  const mm = document.getElementById('match-modal');
  if (mm) mm.hidden = true;
  backToBagHome();
});
// keep what the photograph gave, or go and choose by hand
const revealBar  = ROOT.querySelector('#revealbar');
const revealKeep = ROOT.querySelector('#revealkeep');
const revealPick = ROOT.querySelector('#revealpick');
if (revealKeep) revealKeep.addEventListener('click', () => {
  stopReveal();
  revealBusy = false;
  if (revealBar) revealBar.hidden = true;
  // the photograph stays within reach, with a print of it as the anchor
  const pb = ROOT.querySelector('#photoback'), pbl = ROOT.querySelector('#photoback-label');
  if (pb){
    if (typeof setPhotoThumb === 'function') setPhotoThumb(pb);
    if (pbl) pbl.textContent = 'Your photograph, on the bag';
    pb.hidden = false;
    frameForReturnBar();
  }
  offerSwatch();
  // the offer's tidy-up runs after this, so the way back is restored behind it
  [80, 400, 1000].forEach(ms => setTimeout(() => {
    const el = ROOT.querySelector('#photoback');
    if (el && mshot && mshot.classList.contains('on')) el.hidden = false;
  }, ms));
});

/* shown once per visit: a colour read from a photograph is the strongest case
   for seeing the leather in the hand first */
let swatchOffered = false;
const offerEl = ROOT.querySelector('#swatchoffer');
if (offerEl && offerEl.parentElement !== ROOT) ROOT.appendChild(offerEl);
if (offerEl){
  const close = () => { offerEl.hidden = true; };
  // the card has just been moved to the page body, so its own buttons are no
  // longer inside ROOT — look for them within the card itself
  offerEl.querySelector('#offerskip').addEventListener('click', close);
  offerEl.querySelector('#offerswatch').addEventListener('click', () => {
    close();
    const b = ROOT.querySelector('#swatchorder');
    if (b) b.click();
  });
  offerEl.addEventListener('click', e => { if (e.target === offerEl) close(); });
}
function offerSwatch(){
  if (swatchOffered) return;                 // already answered for this photograph
  const el = offerEl;                        // held directly: it has been re-homed
  if (!el) return;
  setTimeout(() => {
    el.hidden = false;
    swatchOffered = true;                    // only once it is genuinely up
  }, 900);
}
if (revealPick) revealPick.addEventListener('click', () => {
  stopReveal();
  revealBusy = false;
  if (revealBar) revealBar.hidden = true;
  // straight back to the photograph, still loaded, with its suggestions intact
  showMatch();
  [60, 260].forEach(ms => setTimeout(refreshHalo, ms));
});

const autoUndo = ROOT.querySelector('#mautoundo');
if (autoUndo) autoUndo.addEventListener('click', () => {
  if (!preSkin) return;
  apply('front', preSkin.front[1], preSkin.front[0], true);
  apply('back', preSkin.back[1], preSkin.back[0], true);
  preSkin = null; autoSkinned = false;
  const note = ROOT.querySelector('#mautonote'); if (note) note.hidden = true;
});

const autoRedo = ROOT.querySelector('#mautoredo');
if (autoRedo) autoRedo.addEventListener('click', () => {
  if (!suggested) return;
  ['front','back','pocket'].forEach(id => {
    if (suggested[id]) apply(id, suggested[id][1], suggested[id][0], true);
  });
  autoSkinned = true;
  const note = ROOT.querySelector('#mautonote'); if (note) note.hidden = false;
  renderSummary(); validate(); drawNow();
});

const saveBtn = ROOT.querySelector('#msave');
if (saveBtn) saveBtn.addEventListener('click', () => {
  const hex = (ROOT.querySelector('#mhex')?.value || '').trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(hex)) saveMatch('#' + hex.replace('#',''));
});
const savedBox = ROOT.querySelector('#msaved');
if (savedBox) savedBox.addEventListener('click', e => {
  const u = e.target.closest('[data-usematch]');
  if (u){
    const m = readMatches()[+u.dataset.usematch];
    if (m){ setBase(m.hex); renderMatch(m.hex); }
    return;
  }
  const d = e.target.closest('[data-delmatch]');
  if (d){
    const list = readMatches();
    list.splice(+d.dataset.delmatch, 1);
    writeMatches(list);
    renderMatches();
  }
});
renderMatches();

/* A card the customer can keep or post: their own photograph beside the bag in
   the colour it produced. The coincidence is the shareable thing, not the bag. */
const shareBtn = ROOT.querySelector('#mshare');
if (shareBtn) shareBtn.addEventListener('click', async () => {
  const W = 1080, Hgt = 1350;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = Hgt;
  const c = cv.getContext('2d');
  c.fillStyle = '#F9F5EB'; c.fillRect(0, 0, W, Hgt);

  /* One measured column, laid out top to bottom with no two blocks sharing a
     band. The previous version placed the caption and the chip labels from
     opposite ends of the card, so on a tall photograph they landed on top of
     each other. */
  const M = 84;                                  // the margin the whole card keeps
  const colW = W - M * 2;

  // their photograph, in a landscape window across the top
  const photoH = 700;
  if (mcanvas.width){
    const sc = Math.max(colW / mcanvas.width, photoH / mcanvas.height);
    const sw = colW / sc, sh = photoH / sc;
    c.save();
    c.beginPath(); c.rect(M, M, colW, photoH); c.clip();
    c.drawImage(mcanvas,
      (mcanvas.width - sw) / 2, (mcanvas.height - sh) / 2, sw, sh,
      M, M, colW, photoH);
    c.restore();
  }

  // the caption sits on its own line, clear of everything
  let y = M + photoH + 92;
  c.fillStyle = '#8A6616';
  c.font = '400 60px "Instrument Serif", Georgia, serif';
  c.textBaseline = 'alphabetic';
  c.fillText('Matched to something I own', M, y);

  // then the three leathers, each chip with its own label beneath it
  const picks = SECTIONS.map(sec => [sec.name, chosen[sec.id][0], chosen[sec.id][1]]);
  const gap = 22;
  const chipW = (colW - gap * (picks.length - 1)) / picks.length;
  const chipTop = y + 54, chipH = 132;
  picks.forEach(([, , hex], i) => {
    c.fillStyle = hex;
    c.fillRect(M + i * (chipW + gap), chipTop, chipW, chipH);
  });

  // a label that will not fit is shortened rather than allowed to run on
  const fit = (text, max) => {
    if (c.measureText(text).width <= max) return text;
    let t = text;
    while (t.length > 1 && c.measureText(t + '…').width > max) t = t.slice(0, -1);
    return t + '…';
  };
  picks.forEach(([label, name], i) => {
    const x = M + i * (chipW + gap);
    c.fillStyle = '#7C776C';
    c.font = '600 20px "Hanken Grotesk", system-ui, sans-serif';
    c.fillText(fit(label.toUpperCase(), chipW), x, chipTop + chipH + 38);
    c.fillStyle = '#1B1A18';
    c.font = '600 26px "Hanken Grotesk", system-ui, sans-serif';
    c.fillText(fit(name, chipW), x, chipTop + chipH + 74);
  });

  // and the mark, on the last line
  c.fillStyle = '#7C776C';
  c.font = '600 22px "Hanken Grotesk", system-ui, sans-serif';
  c.fillText('NIADIRA · MAKE IT YOURS™', M, Hgt - 58);

  const blob = await new Promise(r => cv.toBlob(r, 'image/png'));
  const file = new File([blob], 'niadira-match.png', { type: 'image/png' });
  // share sheet where the device offers one, a download everywhere else
  if (navigator.canShare && navigator.canShare({ files: [file] })){
    try { await navigator.share({ files: [file], title: 'My Niadira match' }); return; }
    catch (err) { if (err && err.name === 'AbortError') return; }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'niadira-match.png';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
});



matchHost.addEventListener('click', (e) => {
  const f = e.target.closest('[data-tipfold]');
  if (!f) return;
  const row = f.closest('.tipfold');
  const wasOpen = row.dataset.folded !== '1';
  matchHost.querySelectorAll('.tipfold').forEach(r => {
    r.dataset.folded = '1';
    const b = r.querySelector('[data-tipfold]');
    if (b){ b.setAttribute('aria-expanded', 'false'); b.querySelector('.chev').textContent = '+'; }
  });
  if (!wasOpen){
    row.dataset.folded = '0';
    f.setAttribute('aria-expanded', 'true');
    f.querySelector('.chev').textContent = '\u2212';
  }
});

const photoBack = ROOT.querySelector('#photoback');
if (photoBack){
  ROOT.querySelector('#photoback-btn').addEventListener('click', () => {
    photoBack.hidden = true;
    showMatch();
    [60, 260].forEach(ms => setTimeout(refreshHalo, ms));
  });
}
const mread = matchHost.querySelector('#mread');   // may be absent now
function uploadReady(){ return true; }   // no gate: the photo comes first
mdrop.addEventListener('click', () => { if (uploadReady()) mfile.click(); });
mfile.addEventListener('change', () => loadPhoto(mfile.files[0]));
/* start again with a different photograph: the current one is cleared, the
   door comes back, and the picker opens straight away — within this same tap,
   which is what lets the browser allow it */
{
  const np = matchHost.querySelector('#photo-new');
  if (np) np.addEventListener('click', () => {
    if (typeof clearPhoto === 'function') clearPhoto();
    mfile.value = '';                       // so the same file can be chosen again
    const mm = document.getElementById('match-modal');
    if (mm){ mm.hidden = false; mm.removeAttribute('data-hasphoto'); }
    mfile.click();
  });
}
['dragenter','dragover'].forEach(ev => mdrop.addEventListener(ev, e => {
  e.preventDefault(); mdrop.classList.add('over');
}));
['dragleave','drop'].forEach(ev => mdrop.addEventListener(ev, e => {
  e.preventDefault(); mdrop.classList.remove('over');
}));
mdrop.addEventListener('drop', e => { if (uploadReady()) loadPhoto(e.dataTransfer.files[0]); });

/* sample the pixel the customer tapped, averaged over a small patch */
function sampleAt(ev){
  const r = mcanvas.getBoundingClientRect();
  const x = Math.round((ev.clientX - r.left) / r.width * mcanvas.width);
  const y = Math.round((ev.clientY - r.top) / r.height * mcanvas.height);
  const ctx = mcanvas.getContext('2d', { willReadFrequently: true });
  const s = 5, x0 = Math.max(0, x - 2), y0 = Math.max(0, y - 2);
  const d = ctx.getImageData(x0, y0, Math.min(s, mcanvas.width - x0), Math.min(s, mcanvas.height - y0)).data;
  let R = 0, G = 0, B = 0, n = 0;
  for (let i = 0; i < d.length; i += 4){ R += d[i]; G += d[i+1]; B += d[i+2]; n++; }
  if (!n) return;
  const hex = '#' + [R/n, G/n, B/n].map(v => Math.round(v).toString(16).padStart(2,'0')).join('').toUpperCase();
  if (awaitingSpot){
    awaitingSpot = false;
    if (mshot) mshot.dataset.stage = 'read';
    // the tapped colour leads, with the rest of the photo behind it
    const pal = [hex, ...lastPalette.filter(h => h.toUpperCase() !== hex.toUpperCase())];
    runScan(pal, () => { setBase(hex); autoSkin(pal, hex); }, hex);
    return;
  }
  setBase(hex);
}
// some photographs are of one thing throughout; this reads them whole
const mwhole = ROOT.querySelector('#mwhole');
if (mwhole) mwhole.addEventListener('click', () => {
  if (!lastPalette.length) return;
  awaitingSpot = false;
  if (mshot) mshot.dataset.stage = 'read';
  runScan(lastPalette, () => {
    setBase(lastPalette[0]);
    autoSkin(lastPalette, lastPalette[0]);
  }, lastPalette[0]);
});

// a small mark shows where the reading was taken from
function markSpot(ev){
  const spot = ROOT.querySelector('#mspot');
  if (!spot) return;
  const r = mcanvas.getBoundingClientRect();
  const host = mcanvas.parentElement.getBoundingClientRect();
  spot.style.left = Math.round(ev.clientX - host.left) + 'px';
  spot.style.top  = Math.round(ev.clientY - host.top) + 'px';
  spot.dataset.on = '1';
}
mcanvas.addEventListener('click', markSpot);
mcanvas.addEventListener('click', sampleAt);
mcanvas.addEventListener('pointermove', e => { if (e.buttons === 1) sampleAt(e); });
mchips.addEventListener('click', e => {
  const b = e.target.closest('[data-chip]');
  if (b) setBase(b.dataset.chip);
});

const mpick = matchHost.querySelector('#mpick');
const mhex  = matchHost.querySelector('#mhex');
function setBase(hex){
  hex = hex.toUpperCase();
  mpick.value = hex; mhex.value = hex;
  mchips.querySelectorAll('[data-chip]').forEach(b =>
    b.setAttribute('aria-pressed', String(b.dataset.chip === hex)));
  renderMatch(hex);
}
mpick.addEventListener('input', () => setBase(mpick.value));
mhex.addEventListener('input', () => {
  const v = mhex.value.trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(v)){
    setBase('#' + v.replace('#',''));
  }
});
function onApplyClick(e){
  const b = e.target.closest('[data-apply]');
  if (!b) return;
  const r = matchHost._rows[+b.dataset.apply];
  apply(b.dataset.to, r.hex, r.name, true);
  const sec = SECTIONS.find(x => x.id === b.dataset.to);
  if (typeof closeWheel === 'function') closeWheel();
  photoModal.hidden = true;
  matchModal.hidden = true;
  seeTheBag(b.dataset.to);
  // offer the way back, so the photo and its suggestions are not lost
  const pb = ROOT.querySelector('#photoback'), pbl = ROOT.querySelector('#photoback-label');
  if (pb && typeof setPhotoThumb === 'function') setPhotoThumb(pb);
  if (pb && pbl){
    pbl.textContent = (sec ? sec.name + ' — ' : '') + r.name;
    pb.hidden = false;
    frameForReturnBar();
  }
}

/* ---------- what it costs ----------
   Base comes from the product; the surcharges are the workshop's, set here so the
   panel can show them before checkout rather than surprising anyone at the till. */
const PRICE = { base: 245, customColour: 0, emboss: 0, swatch: 6, currency: '£' };
// Shopify is the source of truth: read the bag's live price and use that alone.
(async function loadShopPrice(){
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const host = ROOT;
  const handles = [host.dataset.bagHandle || '', 'personalised-tote-bag-100-nappa-leather'];
  let price = null;
  try {
    const m = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
    if (m && m.variants && m.variants.length && m.variants[0].price != null) price = m.variants[0].price / 100;
  } catch (err) {}
  for (const h of handles){
    if (price != null || !h) continue;
    try {
      const r = await fetch(root + 'products/' + h + '.js', { headers:{ 'Accept':'application/json' } });
      if (r.ok){
        const pr = await r.json();
        const v = (pr.variants || []).find(x => x.available) || (pr.variants || [])[0];
        if (v && v.price != null) price = v.price / 100;
      }
    } catch (err) {}
  }
  if (price != null){
    PRICE.base = price;
    try { renderPrice(); } catch (err) {}
    try { validate(); } catch (err) {}
  }
})();
const money = n => PRICE.currency + n.toFixed(2);
const isHouse = (s) => (s.swatches || []).some(c => c[1].toUpperCase() === chosen[s.id][1].toUpperCase());
let swatchOrdered = false;
function priceLines(){
  if (swatchOrdered) return [['Leather swatch set', PRICE.swatch]];
  const lines = [['Grain leather tote', PRICE.base]];
  SECTIONS.forEach(s => {
    if (touched[s.id] && !isHouse(s))
      lines.push([s.name + ' in a custom colour', PRICE.customColour]);
  });
  TEXT_TARGETS.forEach(T => {
    if ((textTools[T.id].cfg.msg || '').trim())
      lines.push(['Printing — ' + T.label.toLowerCase(), PRICE.emboss]);
  });
  return lines.filter((l, i) => i === 0 || l[1] > 0);
}
/* the panels' real sizes, in metres — the sheet's lettering is scaled from the
   same proportion the 3D texture uses, so the two always agree */
/* the words for a panel, however they were entered */
function panelWords(id){
  const c = (textTools[id] && textTools[id].cfg) || {};
  return String(c.msg || c.text || '').trim();
}
const PANEL_W = { front: 0.52, back: 0.52, pocket: 0.19 };
const PANEL_H = { front: 0.30, back: 0.30, pocket: 0.15 };
const PRINT_SHEET = {
  front:  { w: 4585, h: 2720, label: 'Front panel' },
  back:   { w: 4585, h: 2720, label: 'Back panel' },
  pocket: { w: 1642, h: 1344, label: 'Inner zip pocket' },
};
/* printing ink: near-black on a pale hide, cream on a dark one */
function inkOn(hex){
  const n = parseInt(String(hex).replace('#',''), 16);
  if (isNaN(n)) return '#111111';
  const r = (n >> 16 & 255) / 255, g = (n >> 8 & 255) / 255, b = (n & 255) / 255;
  const lum = 0.2126*r + 0.7152*g + 0.0722*b;
  return lum < 0.42 ? '#F6F1E4' : '#111111';
}
function buildPrintSheet(id){
  const spec = PRINT_SHEET[id];
  if (!spec) return null;
  const cfg = (textTools[id] && textTools[id].cfg) || {};
  const hex = (chosen[id] && chosen[id][1]) || '#FFFFFF';
  const name = (chosen[id] && chosen[id][0]) || '';
  const cv = document.createElement('canvas');
  cv.width = spec.w; cv.height = spec.h;      // exactly the printable area
  const c = cv.getContext('2d');

  // the panel itself, in its leather colour
  c.fillStyle = hex;
  c.fillRect(0, 0, spec.w, spec.h);

  // the lettering, positioned as the customer placed it
  const words = panelWords(id);
  if (words){
    const T = TEXT_TARGETS.find(t => t.id === id) || {};
    const fam = (FONTS[cfg.font] && FONTS[cfg.font][1]) || '400 {S}px Georgia, serif';

    /* The bag draws the lettering onto a strip whose width is a proportion of
       the panel — maxW × cfg.size. Reproduce that proportion here rather than
       converting points, or the two disagree wildly. */
    const panelW = PANEL_W[id] || 0.52;             // the panel, in metres
    const stripW = (T.maxW || 0.26) * (cfg.size || 1);
    const target = Math.min(0.94, stripW / panelW) * spec.w;

    // find the size at which the words fill that width
    let px = Math.round(spec.h * 0.2);
    c.font = fam.replace('{S}', px);
    const w1 = c.measureText(words).width || 1;
    px = Math.max(8, Math.round(px * (target / w1)));
    // and never taller than the strip the customer positioned
    const maxH = Math.min(0.9, ((T.maxH || 0.07) / (PANEL_H[id] || 0.30))) * spec.h;
    px = Math.min(px, Math.round(maxH));
    c.font = fam.replace('{S}', px);

    c.fillStyle = cfg.ink || inkOn(hex);
    const al = cfg.align || 'center';
    c.textAlign = al; c.textBaseline = 'middle';
    // the placement sliders run in metres across the panel; carry them across
    const across = (cfg.x || 0), up = (cfg.y || 0);
    const spanY = (T.y ? (T.y[1] - T.y[0]) : 0.14) || 0.14;
    const midY  = T.y ? (T.y[0] + T.y[1]) / 2 : up;
    const fx0 = spec.w / 2 + (across / panelW) * spec.w;
    const fx = al === 'left' ? fx0 - target / 2 : al === 'right' ? fx0 + target / 2 : fx0;
    const fy = spec.h / 2 - ((up - midY) / spanY) * spec.h * 0.5;
    c.fillText(words, fx, fy, spec.w * 0.96);
  }

  return cv;
}
function downloadPrintSheets(){
  ['front','back','pocket'].forEach((id, i) => {
    const cv = buildPrintSheet(id);
    if (!cv) return;
    setTimeout(() => {
      const a = document.createElement('a');
      // the filename carries what the caption used to: panel, colour, hex, size
      const nm = (chosen[id] ? chosen[id][0] : '').replace(/[^a-z0-9]+/gi, '-');
      const hx = chosen[id] ? chosen[id][1].replace('#','').toUpperCase() : '';
      a.download = ['niadira', id, nm, hx,
        PRINT_SHEET[id].w + 'x' + PRINT_SHEET[id].h, '200dpi'].filter(Boolean).join('_') + '.png';
      a.href = cv.toDataURL('image/png');
      a.click();
    }, i * 320);                        // browsers refuse simultaneous saves
  });
}

function renderPrice(){
  const bar = document.getElementById('appprice');
  if (bar){
    const total = priceLines().reduce((a, l) => a + l[1], 0);
    bar.textContent = money(total);
  }
  const lines = priceLines();
  const total = lines.reduce((a, l) => a + l[1], 0);
  priceBox.innerHTML = lines.map(l =>
    `<div class="line"><span>${l[0]}</span><span>${money(l[1])}</span></div>`).join('')
    + `<div class="line total"><span>Total</span><span>${money(total)}</span></div>`;
  return total;
}
const priceBox = $('#price');

/* ---------- saving a design ---------- */
const STORE_KEY = 'niadira.bag.design.v1';
function serialise(){
  const out = { c: {}, t: {} };
  SECTIONS.forEach(s => { if (touched[s.id]) out.c[s.id] = chosen[s.id]; });
  TEXT_TARGETS.forEach(T => {
    const c = textTools[T.id].cfg;
    const msg = String(c.msg || c.text || '').trim();
    if (msg)
      out.t[T.id] = ['', c.font, +(+c.size || 1).toFixed(2), +(+c.x || 0).toFixed(3), +(+c.y || 0).toFixed(3),
        c.finish, msg, c.ink || '', c.align || ''];
  });
  return out;
}
var restoring = false;
var pickedNow = {};
function restore(d){
  if (!d) return;
  restoring = true;
  try { restoreInner(d); } finally { restoring = false; }
}
function restoreInner(d){
  Object.entries(d.c || {}).forEach(([id, v]) => {
    if (SECTIONS.some(s => s.id === id) && Array.isArray(v)) apply(id, v[1], v[0], true);
  });
  Object.entries(d.t || {}).forEach(([id, v]) => {
    const tool = textTools[id];
    if (!tool || !Array.isArray(v)) return;
    const [text, font, size, x, y, finish, msg, ink, align] = v;
    Object.assign(tool.cfg, { text: msg || text || '', font, size, x, y,
      finish: finish || 'print', msg: msg || '' });
    if (ink) tool.cfg.ink = ink;
    if (align) tool.cfg.align = align;
    tool.build(); tool.draw();
    const set = (k, val) => {
      const el = tEl(`[data-t="${id}"][data-k="${k}"]`);
      if (el) el.value = val;
    };
    set('font', font); set('x', x); set('y', y);
    set('pt', Math.round(size * 18));                // the size control is in points now
    set('msg', tool.cfg.msg);
    const out = (k, val) => {
      const el = tEl(`[data-out="${id}-${k}"]`);
      if (el) el.textContent = val;
    };
    out('size', Math.round(size * 18) + ' pt');
    out('msg', tool.cfg.msg.length);
    out('x', (x*100).toFixed(1) + 'cm');
    out('y', (y*100).toFixed(1) + 'cm');
  });
  
host.querySelectorAll('.sec').forEach((el, i) => {
  el.dataset.folded = i === 0 ? '0' : '1';
  const b = el.querySelector('[data-fold]');
  if (b){ b.setAttribute('aria-expanded', String(i === 0)); b.querySelector('.chev').textContent = i === 0 ? '\u2212' : '+'; }
});
  validate();
}
const enc = o => btoa(unescape(encodeURIComponent(JSON.stringify(o))))
  .replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
const dec = s => JSON.parse(decodeURIComponent(escape(
  atob(s.replace(/-/g,'+').replace(/_/g,'/')))));

let saveTimer;
function saveLocal(){
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(serialise())); } catch (e) {}
  }, 250);
}
host.addEventListener('click', saveLocal);
host.addEventListener('input', saveLocal);
// the strip, the shade windows and the text card sit outside the panels
ROOT.addEventListener('input', () => { if (!restoring) saveLocal(); }, true);
ROOT.addEventListener('change', () => { if (!restoring) saveLocal(); }, true);
ROOT.addEventListener('click', () => { if (!restoring) saveLocal(); }, true);
// and a last save as the page goes away, so nothing chosen in the final moment is lost
window.addEventListener('pagehide', () => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(serialise())); } catch (e) {}
});
textHost.addEventListener('input', saveLocal);
matchHost.addEventListener('click', saveLocal);
{
  const pm = ROOT.querySelector('#photo-modal');
  if (pm){ pm.addEventListener('click', onApplyClick); pm.addEventListener('click', saveLocal); }
}
matchHost.addEventListener('click', onApplyClick);

$('#share').addEventListener('click', async (e) => {
  const url = location.origin + location.pathname + location.search + '#d=' + enc(serialise());
  history.replaceState(null, '', '#d=' + enc(serialise()));
  e.target.textContent = (await copyText(url)) ? 'Link copied' : 'Shown for copying';
  setTimeout(() => { e.target.textContent = 'Copy link to this design'; }, 2200);
});

/* a link wins over the browser's own saved copy */
(() => {
  const m = /[#&]d=([^&]+)/.exec(location.hash);
  if (m){ try { restore(dec(m[1])); return; } catch (e) {} }
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) restore(JSON.parse(raw));
  } catch (e) {}
})();

function openSheet({ title, intro, rows, totalLabel, total, action }){
  const $$ = id => $('#' + id);
  // properties beginning with an underscore are for the workshop, not the
  // customer — Shopify hides them at checkout and this sheet does the same
  rows = (rows || []).filter(([k]) => !String(k).startsWith('_'));
  $$('sheet-title').textContent = title;
  $$('sheet-intro').textContent = intro;
  $$('sheet-rows').innerHTML = rows.map(([k,v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('');
  $$('sheet-total').innerHTML = total == null ? ''
    : `<span>${totalLabel}</span><span>${money(total)}</span>`;
  const go = $$('sheet-go');
  go.hidden = !action;
  if (action){ go.textContent = action.label; go.onclick = action.run; go.disabled = false; }
  // the print-ready sheets, offered on the commission itself
  let dl = $$('sheet-sheets');
  if (!dl){
    dl = document.createElement('button');
    dl.id = 'sheet-sheets';
    dl.className = 'reset';
    dl.textContent = 'Download the print sheets';
    const acts = $$('sheet-go').parentElement;
    if (acts) acts.insertBefore(dl, $$('sheet-close'));
    dl.addEventListener('click', () => {
      dl.textContent = 'Saving\u2026';
      downloadPrintSheets();
      setTimeout(() => { dl.textContent = 'Download the print sheets'; }, 1600);
    });
  }
  dl.hidden = !/commission/i.test(title);
  $$('sheet').hidden = false;
}

const swatchBtn = $('#swatchorder');
function swatchRows(){
  const picked = SECTIONS.filter(x => touched[x.id])
    .map(x => [x.name, chosen[x.id][0]]);
  return picked.length ? picked
    : [['Nothing chosen yet', 'Pick your colours first and we will cut a swatch of each']];
}
async function orderSwatches(btn){
  if (!(btn instanceof HTMLElement)) btn = null;
  const props = {};
  SECTIONS.filter(x => touched[x.id]).forEach(x => {
    props['Swatch — ' + x.name] = chosen[x.id][0];
    props['_Swatch hex — ' + x.name] = chosen[x.id][1].toUpperCase();
  });
  props['Credit'] = money(PRICE.swatch) + ' off your bag when you order it';
  const go = btn || $('#sheet-go');
  // a store on a language or market path needs its own root, e.g. /en-gb/
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  let variantId = ROOT.dataset.swatchVariantId;
  // No theme-editor setting needed: find the swatch product by its address,
  // /products/leather-swatch-set, and take its first available variant.
  if (!variantId){
    const fromHandle = async (h) => {
      try {
        const r = await fetch(root + 'products/' + h + '.js', { headers:{ 'Accept':'application/json' } });
        if (!r.ok) return null;
        const pr = await r.json();
        const v = (pr.variants || []).find(x => x.available) || (pr.variants || [])[0];
        return v ? String(v.id) : null;
      } catch (err) { return null; }
    };
    // the likely addresses first
    for (const h of [(typeof ROOT !== 'undefined' && ROOT.dataset && ROOT.dataset.swatchHandle) || '',
                     'leather-swatch-set','swatch-set','leather-swatches','swatches','leather-swatch']){
      if (h && !variantId) variantId = await fromHandle(h);
    }
    // otherwise ask the store's own search for a product named like a swatch
    if (!variantId){
      try {
        const r = await fetch(root + 'search/suggest.json?q=swatch&resources[type]=product&resources[limit]=5',
          { headers:{ 'Accept':'application/json' } });
        if (r.ok){
          const j = await r.json();
          const list = (((j.resources || {}).results || {}).products) || [];
          const hit = list.find(p => /swatch/i.test(p.title || '')) || list[0];
          if (hit && hit.handle) variantId = await fromHandle(hit.handle);
        }
      } catch (err) {}
    }
  }
  if (!variantId){
    // nothing to add: say so plainly rather than pretending it worked
    go.disabled = false;
    go.textContent = 'Swatches are not set up yet';
    console.warn('[Niadira] No swatch product found. Create a product whose web address ends /products/leather-swatch-set, and make sure it is published to the Online Store.');
    return;
  }
  go.disabled = true; go.textContent = 'Adding\u2026';
  try {
    const res = await fetch(root + 'cart/add.js', {
      method:'POST',
      headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.description || body.message || res.statusText);
    swatchOrdered = true;
    // straight to checkout, as asked; the cart page is the fallback
    window.location.href = root + 'checkout';
  } catch (err){
    go.disabled = false;
    go.textContent = 'Could not add \u2014 ' + String(err.message || 'try again').slice(0, 60);
    console.warn('[Niadira] Swatch add failed:', err);
  }
}
if (swatchBtn) swatchBtn.addEventListener('click', () => openSheet({
  title: 'The sensory prelude',
  intro: 'We cut a swatch of each leather you have chosen and post it to you. Feel the weight, the '
       + 'grain and the finish of our workshop leathers at home, in daylight, before you commission '
       + 'the bag. The ' + money(PRICE.swatch) + ' comes off its price when you do.',
  rows: swatchRows(),
  totalLabel: 'Swatch set',
  total: PRICE.swatch,
  action: { label: 'Send me these swatches', run: orderSwatches },
}));

$('#sheet-close').addEventListener('click', () => { $('#sheet').hidden = true; });
$('#sheet').addEventListener('click', (e) => { if (e.target.id === 'sheet') e.target.hidden = true; });

const dyeModal = $('#dye-modal');
const dyeOpen = $('#dyenote-open');
const dyeClose = $('#dye-close');
const dyeSwatch = $('#dye-swatch');
if (dyeOpen) dyeOpen.addEventListener('click', () => { dyeModal.hidden = false; });
if (dyeClose) dyeClose.addEventListener('click', () => { dyeModal.hidden = true; });
if (dyeSwatch) dyeSwatch.addEventListener('click', () => {
  dyeModal.hidden = true;
  const b = $('#swatchorder');
  if (b) b.click();
});

const matchModal = $('#match-modal');
const matchOpen = $('#match-open'), matchClose = $('#match-close');
if (matchOpen) matchOpen.addEventListener('click', () => {
  if (typeof showMatch === 'function') showMatch(); else matchModal.hidden = false;
});
if (matchClose) matchClose.addEventListener('click', () => { matchModal.hidden = true; });


/* mobile: the bag owns the screen and the controls arrive as a drawer */
const panelEl = $('.panel');
const PANEL_TITLES = { colour:'Colour', text:'Add text', match:'Match My Photo\u2122', order:'Order' };
let lastTab = 'colour';
function openDrawer(tab, title, key, keepScroll){
  // the drawer is the last word: anything left over from the making steps aside
  ['#photoback','#swatchoffer','#revealbar','#colourribbon','#textcard','#wheel-modal']
    .forEach(sel => { const el = ROOT.querySelector(sel); if (el) el.hidden = true; });
  if (tab) showTab(tab);
  const h = document.getElementById('paneltitle');
  if (h) h.textContent = title || PANEL_TITLES[tab] || 'Customise';
  lastTab = key || tab || 'colour';
  panelEl.dataset.tab = lastTab;
  const scrim = ROOT.querySelector('#panelscrim');
  if (scrim) scrim.hidden = false;
  panelEl.dataset.drawer = 'open';
  if (!keepScroll) panelEl.scrollTop = 0;
  playEntrance(panelEl);
}
/* A stalled document timeline (a hidden tab, an embedded frame, Shopify's theme
   editor) freezes CSS animations at frame 0, so the open state must never take
   its appearance from one. The entrance is added and then removed on a timer. */
let entranceTimer = null;
function clockRuns(){
  return document.visibilityState !== 'hidden' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function playEntrance(el){
  if (!el) return;
  el.classList.remove('pg-in');
  if (!clockRuns()) return;
  el.classList.add('pg-in');
  clearTimeout(entranceTimer);
  entranceTimer = setTimeout(() => el.classList.remove('pg-in'), 1000);
}
function closeDrawer(){
  const scrim = ROOT.querySelector('#panelscrim');
  if (scrim) scrim.hidden = true;
  panelEl.dataset.drawer = 'closed';
  panelEl.classList.remove('pg-in');
}
/* hovering a panel's chosen shade blooms that colour family's own box below,
   so the customer can see at a glance which range they are in */
const isTouch = () => window.matchMedia('(hover: none)').matches;
function bloomFamily(id, on){
  const sec = host.querySelector(`.sec[data-sec="${id}"]`);
  if (!sec) return;
  sec.querySelectorAll('.famtabs button.bloom').forEach(b => b.classList.remove('bloom'));
  if (!on) return;
  const fam = pickedFamily[id] || familyOf(chosen[id][1]);
  const btn = fam
    ? sec.querySelector(`.famtabs button[data-fam="${fam}"]`)
    : sec.querySelector('.famtabs .anycol');
  if (btn) btn.classList.add('bloom');
}
host.addEventListener('pointerover', e => {
  const em = e.target.closest('em[data-lab]');
  if (em) bloomFamily(em.dataset.lab, true);
});
host.addEventListener('pointerout', e => {
  const em = e.target.closest('em[data-lab]');
  if (em && !em.contains(e.relatedTarget)) bloomFamily(em.dataset.lab, false);
});

const panelScrim = ROOT.querySelector('#panelscrim');
if (panelScrim) panelScrim.addEventListener('click', closeDrawer);
$('#rail').addEventListener('click', e => {
  const b = e.target.closest('[data-rail]');
  if (!b) return;
  const k = b.dataset.rail;
  if (k === 'match'){
    closeDrawer();
    const h = document.getElementById('paneltitle');
    if (h) h.textContent = PANEL_TITLES.match;
    showMatch();
    return;
  }
  if (resume && resume.tab === (k === 'order' ? 'colour' : k) && k !== 'order'){
    restoreResume();
    return;
  }
  resume = null;
  openDrawer(k === 'order' ? 'colour' : k, PANEL_TITLES[k], k);
});
$('#panelclose').addEventListener('click', closeDrawer);

/* the desktop and mobile framings differ, and the canvas is sized once —
   re-fit both whenever the viewport changes (rotation, theme editor, resize) */
let wasMobile = isMobile(), fitTimer = null;
function refit(){
  const st = stage;
  if (!st || !st._renderer || !st._camera) return;
  const w = st.clientWidth, h = st.clientHeight;
  // mid-transition the host can measure at 0; retry rather than lock in a 1px canvas
  if (w < 2 || h < 2){ clearTimeout(fitTimer); fitTimer = setTimeout(refit, 160); return; }
  st._renderer.setSize(w, h);
  st._camera.aspect = w / h;
  st._camera.updateProjectionMatrix();
  if (st._controls){
    // a floor and a ceiling on zoom, so the bag can never shrink to a speck
    st._controls.minDistance = 0.62;
    st._controls.maxDistance = 2.30;
  }
  // the reframe is a nicety; never let it stop the resize itself
  try {
    wasMobile = isMobile();
    lookAtPanel(lastLooked);
  } catch (err) {}
}
window.addEventListener('resize', () => {
  clearTimeout(fitTimer);
  fitTimer = setTimeout(refit, 160);
});
if (window.ResizeObserver) new ResizeObserver(() => {
  clearTimeout(fitTimer);
  fitTimer = setTimeout(refit, 160);
}).observe(stage);
// The stage sizes its canvas once and misses later changes. Poll the HOST's own
// clientWidth/Height — a CSS transform on an ancestor scales getBoundingClientRect
// but not clientWidth, so comparing the two never agreed. A timer, not rAF, since
// rAF is throttled to zero in hidden frames (Shopify's theme editor, background tabs).
window.__niadiraPollTicks = 0;
setInterval(() => {
  window.__niadiraPollTicks++;
  const cv = stage._renderer && stage._renderer.domElement;
  if (!cv) return;
  const w = stage.clientWidth, h = stage.clientHeight;
  if (w < 2 || h < 2) return;
  // setSize writes these in the same unscaled CSS px as clientWidth, so they
  // compare directly — unlike getBoundingClientRect, which an ancestor
  // transform scales and which therefore never agreed with the host
  const cw = parseFloat(cv.style.width) || 0, ch = parseFloat(cv.style.height) || 0;
  if (Math.abs(cw - w) > 1 || Math.abs(ch - h) > 1) refit();
}, 350);
window.addEventListener('orientationchange', () => setTimeout(refit, 220));
if (window.matchMedia){
  const mq = window.matchMedia('(min-width: 0px)');
  const onMq = () => setTimeout(refit, 220);
  mq.addEventListener ? mq.addEventListener('change', onMq) : mq.addListener(onMq);
}
window.addEventListener('load', () => setTimeout(refit, 60));

/* with the stage ready, the window can also drive the render loop and framing */
const launcher = earlyCover, cfgClose = earlyClose, cfgRoot = cfgHost;
/* One frame, right now. Called after every change the customer makes, because
   neither requestAnimationFrame nor setInterval can be relied on to tick in an
   embedded frame — the same throttling that forced the canvas watchdog and the
   peek-bar tracker off rAF earlier. */
var sceneDirty = true;               // var: hoists with drawNow, which runs during init
var spinAt = 0;                      // when the turn was last advanced
function drawNow(){ sceneDirty = true; }
function paintFrame(){
  try {
    if (!stage._renderer || !stage._scene || !stage._camera) return;
    // correct the canvas here too: the timers that used to do it are throttled
    // to nothing in an embedded frame, and style.width is in the same unscaled
    // pixels as clientWidth, so the two compare directly
    const cv = stage._renderer.domElement;
    const w = stage.clientWidth, h = stage.clientHeight;
    if (w > 1 && h > 1){
      const cw = parseFloat(cv.style.width) || 0, ch = parseFloat(cv.style.height) || 0;
      if (Math.abs(cw - w) > 1 || Math.abs(ch - h) > 1){
        stage._renderer.setSize(w, h);
        stage._camera.aspect = w / h;
        stage._camera.updateProjectionMatrix();
      }
    }
    /* OrbitControls advances autoRotate by a fixed angle per update() call, so
       a driver that runs slowly turns the bag slowly rather than choppily.
       Advance the azimuth by elapsed time instead, and the speed holds
       whatever the frame rate. */
    const c = stage._controls;
    if (c){
      if (c.autoRotate){
        const now = performance.now();
        const dt = Math.min(120, now - (spinAt || now));
        spinAt = now;
        const was = c.autoRotate;
        c.autoRotate = false;                       // update() must not add its own
        const rad = (2 * Math.PI / 60) * (c.autoRotateSpeed || 2) * (dt / 1000);
        const off = stage._camera.position.clone().sub(c.target);
        const r = Math.hypot(off.x, off.z);
        const a = Math.atan2(off.x, off.z) - rad;
        stage._camera.position.x = c.target.x + r * Math.sin(a);
        stage._camera.position.z = c.target.z + r * Math.cos(a);
        stage._camera.lookAt(c.target);
        if (c.update) c.update();
        c.autoRotate = was;
      } else if (c.update) c.update();
    }
    stage._renderer.render(stage._scene, stage._camera);
    sceneDirty = false;
  } catch (err) {}
}
let drawingStarted = false;
function startDrawing(){
  if (drawingStarted) return;
  drawingStarted = true;
  /* Damping applies only ~8% of a drag's delta per update() call and needs an
     animation loop to carry the rest. requestAnimationFrame never fires in an
     embedded frame, so the remainder never arrived and the bag stopped dead
     mid-gesture. Without damping each pointermove applies its full delta and
     the change → drawNow binding paints it. */
  if (stage._controls) stage._controls.enableDamping = false;
  setInterval(() => {
    try {
      if (!stage._controls || !stage._controls.autoRotate) return;
      if (typeof fitToBand === 'function') fitToBand(); clearTheCard();
    } catch (err) {}
  }, 700);
  /* ONE frame driver. A dirty flag decides whether to draw, so the scene is
     rendered at most once per animation frame — and only when something has
     actually changed, unless the bag is turning of its own accord. */
  if (stage._renderer) stage._renderer.setAnimationLoop(null);   // not the stage's
  let lastPaint = 0;
  const MIN_GAP = 15;                  // never more than ~66 frames a second
  const tick = () => {
    const turning = stage._controls && stage._controls.autoRotate;
    if (!turning && !sceneDirty) return;
    const now = performance.now();
    if (now - lastPaint < MIN_GAP) return;   // one driver or the other, not both
    lastPaint = now;
    paintFrame();
  };
  let lastRaf = 0;
  const frame = () => { lastRaf = performance.now(); tick(); requestAnimationFrame(frame); };
  requestAnimationFrame(frame);
  // a camera change only marks the scene dirty; the drivers above do the work
  if (stage._controls && stage._controls.addEventListener){
    stage._controls.addEventListener('change', guardZoomAboveCard);
    stage._controls.addEventListener('change', drawNow);
  }
  /* A timer alongside it, because animation frames are throttled to a trickle
     in an embedded frame and in Shopify's theme editor — about 1.5 a second,
     which is nowhere near enough to carry a turning bag. The shared MIN_GAP
     above means the two drivers can never double up. */
  setInterval(() => {
    if (performance.now() - lastRaf < 120) return;   // frames are flowing: leave it to them
    tick();
  }, 16);
}

function setControlsReachable(on){
  ['.stage-wrap', '.rail', '.panel'].forEach(sel => {
    const el = ROOT.querySelector(sel);
    if (!el) return;
    if (on) el.removeAttribute('inert'); else el.setAttribute('inert', '');
  });
}
window.__niadiraAfterOpen = () => {
  measureOffset();
  setControlsReachable(true);
  refit();                      // synchronous: the host measures correctly now
  startDrawing();
  drawNow();
  [60, 260].forEach(ms => setTimeout(refit, ms));   // safety net only
  // frame the opening view the same way a panel does, so the chrome is reserved
  [40, 300].forEach(ms => setTimeout(() => lookAtPanel('front'), ms));
  [420, 900].forEach(ms => setTimeout(fitToBand, ms));
};
window.__niadiraBeforeClose = () => {
  const pm = ROOT.querySelector('#pocket-modal'); if (pm) pm.hidden = true;
  const pb = ROOT.querySelector('#photoback'); if (pb) pb.hidden = true;
  closeSwatchPopup();
  if (typeof closeDrawer === 'function') closeDrawer();
  ['#fam-modal','#photo-modal','#match-modal','#sheet','#dye-modal'].forEach(sel => {
    const el = ROOT.querySelector(sel);
    if (el) el.hidden = true;
  });
  setControlsReachable(false);
};
const openCustomiser = windowOpen, closeCustomiser = windowClose;
setControlsReachable(false);
startDrawing();
drawNow();

document.addEventListener('keydown', e => {
  if (e.key !== 'Escape') return;
  const pm = ROOT.querySelector('#pocket-modal');
  if (pm && !pm.hidden) return;      // only "Apply and see the bag" leaves it
  const anyOpen = ['#fam-modal','#photo-modal','#match-modal','#sheet','#dye-modal']
    .some(sel => { const el = document.querySelector(sel); return el && !el.hidden; });
  if (anyOpen || panelEl.dataset.drawer === 'open' ||
      host.querySelector('.sec[data-open="1"]')){
    closeSwatchPopup(); closeDrawer();
  } else if (launcher && launcher.hidden){
    closeCustomiser();
  }
});

/* the announcement bar, header and breadcrumb sit above the section, so 100dvh
   alone would push its bottom — the price and cart bar — below the fold */
function measureOffset(){
  // the offset only allows for the theme's header while the cover is showing —
  // once the window is open the customiser owns the whole screen, so it is zero
  if (ROOT.dataset && ROOT.dataset.window === 'open'){
    ROOT.style.setProperty('--niadira-offset', '0px');
    return;
  }
  const top = ROOT.getBoundingClientRect().top + (window.scrollY || 0);
  ROOT.style.setProperty('--niadira-offset', Math.max(0, Math.round(top)) + 'px');
}
measureOffset();
window.addEventListener('resize', measureOffset);
window.addEventListener('load', measureOffset);
new ResizeObserver(measureOffset).observe(document.documentElement);

lookAtPanel('front');
syncTextConfirm();
validate();
